import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Tooltip,
  Stack,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CancelIcon from "@mui/icons-material/Cancel";
import EditIcon from "@mui/icons-material/Edit";
import ErrorIcon from "@mui/icons-material/Error";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import GavelIcon from "@mui/icons-material/Gavel";
import VerifiedUserIcon from "@mui/icons-material/VerifiedUser";
import DescriptionIcon from "@mui/icons-material/Description";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import styles from "./nda-orchestrator.module.scss";
import MarkdownRenderer from "../../MarkdownRenderer";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../../store";
import { ChatMessage, sendMessageToAgent } from "../../../store/userSlice";
import { normalizeTextEncoding } from "../../../utils/textFormatter";
import {
  setNdaValidationFeedback,
  resetNdaValidationFeedback,
} from "../../../store/filtersSlice";
import { v4 as uuid } from "uuid";
import { recordAgentAction } from "../../../store/agentSlice";
import {
  buildValidationIssueLookup,
  hasDraftBlockingIssues,
  isDraftBlockingIssue,
  MultipartyValidationIssue,
  normalizeValidationIssueField,
  resolveHasPartySlotData,
  resolveHasPartySummaryData,
  shouldSuppressStaleStep4Fallback,
  shouldSuppressStep4GreetingMarkdown,
  Step4ViewMode,
} from "./step4PartyContract";
import { selectQaAnswerForTurn } from "./ndaQaContract";
import axiosClient from "../../../api/AxiosClient";

type NdaOrchestratorAgentProps = {
  parsedContent: any;
  messageId?: string;
};

type ConversationAction = {
  label: string;
  message?: string;
  variant?: "contained" | "outlined";
  downloadUrl?: string;
};

const NDA_BUILDER_TEMPLATE_OPTIONS = [
  {
    variant: "Mutual",
    templateName: "G42 - NDA - General Group (Mutual)",
  },
  {
    variant: "Unilateral",
    templateName: "G42 - NDA - Unilateral",
  },
  {
    variant: "Multiparty",
    templateName: "G42 - NDA - Multiparty",
  },
] as const;

function extractUrlFromMarkdown(text?: string | null): string | null {
  if (!text) return null;
  const match = text.match(/\((https?:\/\/[^)\s]+)\)/i);
  return match?.[1] || null;
}

function getDraftDownloadUrlFromResult(result: unknown): string | null {
  if (!result || typeof result !== "object") return null;
  const draftResult = result as {
    draft_download_url?: string;
    full_draft?: string;
  };
  if (draftResult.draft_download_url) {
    return draftResult.draft_download_url;
  }
  return extractUrlFromMarkdown(
    typeof draftResult.full_draft === "string" ? draftResult.full_draft : null,
  );
}

function getLatestDraftDownloadUrl(
  response: unknown,
  chat: ChatMessage[],
): string | null {
  const responseItems = Array.isArray(response) ? response : [response];
  for (let index = responseItems.length - 1; index >= 0; index -= 1) {
    const item = responseItems[index];
    if (!item || typeof item !== "object") continue;
    const agentItem = item as { agent_id?: string; result?: unknown };
    if (
      agentItem.agent_id === "nda_drafting_agent" ||
      getDraftDownloadUrlFromResult(agentItem.result)
    ) {
      const url = getDraftDownloadUrlFromResult(agentItem.result);
      if (url) return url;
    }
  }

  for (let index = chat.length - 1; index >= 0; index -= 1) {
    const message = chat[index];
    if (message.sender !== "agent") continue;
    const content = message.content;
    if (!content || typeof content !== "object") continue;
    const payload = (content as { payload?: { result?: { data?: unknown } } })
      .payload?.result?.data;
    if (!payload || typeof payload !== "object") continue;
    const data = payload as { response?: unknown; finalAnswer?: { response?: unknown } };
    const url = getLatestDraftDownloadUrl(
      data.response ?? data.finalAnswer?.response,
      [],
    );
    if (url) return url;
  }

  for (let index = chat.length - 1; index >= 0; index -= 1) {
    const message = chat[index];
    if (message.sender !== "agent") continue;
    const url = extractUrlFromMarkdown(
      typeof message.text === "string" ? message.text : null,
    );
    if (url) return url;
  }

  return null;
}

function responseIncludesTemplateSelection(response: unknown): boolean {
  const responseItems = Array.isArray(response) ? response : [response];
  return responseItems.some(
    (item) =>
      item &&
      typeof item === "object" &&
      (item as { result?: { recommended_template?: string } }).result
        ?.recommended_template !== undefined,
  );
}

function isTemplateProceedQuestion(question?: string): boolean {
  const questionText = (question || "").toLowerCase();
  return (
    questionText.includes("review the selected template") ||
    questionText.includes(
      "shall i proceed with this template to generate the nda draft",
    ) ||
    questionText.includes("shall i proceed with the recommended template")
  );
}

function findTemplateAgentResult(response: unknown): {
  agent_id: string;
  result: Record<string, unknown>;
} | null {
  const responseItems = Array.isArray(response) ? response : [response];
  for (let index = responseItems.length - 1; index >= 0; index -= 1) {
    const item = responseItems[index];
    if (!item || typeof item !== "object") continue;
    const agentItem = item as { agent_id?: string; result?: Record<string, unknown> };
    if (agentItem.result?.recommended_template !== undefined) {
      return {
        agent_id: agentItem.agent_id || "nda_template_selector_agent",
        result: agentItem.result,
      };
    }
  }
  return null;
}

type TemplateSelectionPayload = {
  recommended_template: string;
  alternate_templates?: string[];
  reason?: string;
  selection_rationale?: string;
  preselected_from_hint?: boolean;
  governing_law?: string;
  dispute_resolution?: string;
  actions?: ConversationAction[];
};

// C1: per-step template selector payload
type TemplateSelectorOption = {
  variant: string;
  template_name: string;
  display_label: string;
};
type TemplateSelectorPayload = {
  current_variant: string;
  switchable: boolean;
  options: TemplateSelectorOption[];
};

// C3: natural-language party-edit feedback
type EditHelpPayload = {
  ambiguous: boolean;
  variant?: string;
  hint: string;
};

type DownloadDraftPayload = {
  action?: string;
  available?: boolean;
  draft_download_url?: string;
  filename?: string;
  label?: string;
};

/** Session/API paths need axios (Bearer); SAS blob URLs can use window.open. */
function isNdaSessionDownloadUrl(url: string): boolean {
  return url.startsWith("/") && !url.startsWith("//");
}

async function performNdaFileDownload(
  url: string,
  dispatch: (action: ReturnType<typeof recordAgentAction>) => void,
  agentId: number,
  downloadType: "nda_draft" | "template_redline" | "markdown_link",
  filename = "nda_draft.docx",
): Promise<void> {
  dispatch(
    recordAgentAction({
      agentId,
      actionType: "download",
      content: JSON.stringify({
        type: downloadType,
        url,
      }),
    }),
  );

  if (isNdaSessionDownloadUrl(url)) {
    try {
      const response = await axiosClient.get(url, { responseType: "blob" });
      const blob = new Blob([response.data], {
        type:
          response.headers["content-type"] ||
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error(`[NDA] Session download failed for ${url}:`, error);
      alert(
        "Failed to download the NDA draft. Please try again or contact support if the issue persists.",
      );
    }
    return;
  }

  window.open(url, "_blank", "noopener,noreferrer");
}

function extractTemplateSelection(data: {
  template_selection?: TemplateSelectionPayload;
  finalAnswer?: { template_selection?: TemplateSelectionPayload };
}): TemplateSelectionPayload | null {
  return data?.template_selection ?? data?.finalAnswer?.template_selection ?? null;
}

function extractDownloadDraftAction(data: {
  download_draft?: DownloadDraftPayload;
  finalAnswer?: { download_draft?: DownloadDraftPayload };
}): DownloadDraftPayload | null {
  return data?.download_draft ?? data?.finalAnswer?.download_draft ?? null;
}

function extractHitlDraftDownloadUrl(data: {
  draft_download_url?: string;
  download_available?: boolean;
  download_draft?: DownloadDraftPayload;
  finalAnswer?: {
    draft_download_url?: string;
    download_available?: boolean;
    download_draft?: DownloadDraftPayload;
  };
}): string | null {
  return (
    data?.download_draft?.draft_download_url ||
    data?.finalAnswer?.download_draft?.draft_download_url ||
    data?.draft_download_url ||
    data?.finalAnswer?.draft_download_url ||
    null
  );
}

// C1: extract per-step template selector from HITL payload
function extractTemplateSelector(data: Record<string, unknown>): TemplateSelectorPayload | null {
  const raw = data?.template_selector ?? (data?.finalAnswer as Record<string, unknown>)?.template_selector;
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (
    typeof r.current_variant === "string" &&
    Array.isArray(r.options) &&
    r.options.length > 0
  ) {
    return raw as TemplateSelectorPayload;
  }
  return null;
}

// C3: extract NL party-edit hint from HITL payload
function extractEditHelp(data: Record<string, unknown>): EditHelpPayload | null {
  const raw = data?.edit_help ?? (data?.finalAnswer as Record<string, unknown>)?.edit_help;
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.hint === "string") {
    return raw as EditHelpPayload;
  }
  return null;
}

function getNdaResponseItems(data: any): any[] {
  if (Array.isArray(data?.response)) return data.response;
  if (Array.isArray(data?.finalAnswer?.response)) return data.finalAnswer.response;
  return [];
}

function findAgentResponseItem(
  responseItems: unknown[],
  predicate: (item: { agent_id?: string; result?: unknown }) => boolean,
): { agent_id: string; result: any } | null {
  if (!Array.isArray(responseItems)) return null;
  for (let index = responseItems.length - 1; index >= 0; index -= 1) {
    const item = responseItems[index];
    if (!item || typeof item !== "object") continue;
    const agentItem = item as { agent_id?: string; result?: unknown };
    if (agentItem.result && predicate(agentItem)) {
      return {
        agent_id: agentItem.agent_id || "nda_orchestrator",
        result: agentItem.result,
      };
    }
  }
  return null;
}

function findInputReviewAgentResult(
  responseItems: unknown[],
): { agent_id: string; result: any } | null {
  return findAgentResponseItem(
    responseItems,
    (item) =>
      item.agent_id === "nda_input_review_agent" ||
      isInputReviewResult(item.result),
  );
}

function findIntakeAgentResult(
  responseItems: unknown[],
): { agent_id: string; result: any } | null {
  return findAgentResponseItem(
    responseItems,
    (item) =>
      item.agent_id === "nda_intake_agent" &&
      typeof item.result === "object" &&
      item.result !== null &&
      (item.result as { is_complete?: unknown }).is_complete !== undefined,
  );
}

function DownloadDraftActionCard({
  download,
  onDownload,
}: {
  download: DownloadDraftPayload;
  onDownload: () => void;
}) {
  if (!download.available || !download.draft_download_url) {
    return null;
  }

  return (
    <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
      <Button
        size="large"
        variant="contained"
        startIcon={<DescriptionIcon />}
        onClick={onDownload}
        data-nda-action="download_draft"
        sx={{
          textTransform: "none",
          backgroundColor: "#00a888",
          "&:hover": { backgroundColor: "#00947a" },
          px: 4,
          py: 1.25,
          fontSize: "1rem",
        }}
      >
        {download.label || "Download Draft"}
      </Button>
    </Box>
  );
}

function triggerNdaFileDownload(
  url: string,
  dispatch: (action: ReturnType<typeof recordAgentAction>) => void,
  agentId: number,
  downloadType: "nda_draft" | "template_redline" | "markdown_link",
  filename = "nda_draft.docx",
) {
  void performNdaFileDownload(url, dispatch, agentId, downloadType, filename);
}

type BooleanPromptFieldId =
  | "counterparty_confidentiality_required"
  | "kyc_completed"
  | "space42_presight_disclosure";

type MultipartyTemplateIntakePrefill = {
  counterparty_name?: string;
  counterparty_country?: string;
  counterparty_registration_number?: string;
  counterparty_entity_type?: string;
  counterparty_address?: string;
  purpose?: string;
  counterparty_confidentiality_required?: boolean | null;
  kyc_completed?: boolean | null;
  space42_presight_disclosure?: boolean | null;
};

type MultipartyFormValues = {
  companyAName: string;
  companyAType: string;
  companyAJurisdiction: string;
  companyARegistrationNumber: string;
  companyAAddress: string;
  companyAKyc: "Yes" | "No" | "";
  companyBName: string;
  companyBType: string;
  companyBJurisdiction: string;
  companyBRegistrationNumber: string;
  companyBAddress: string;
  companyBKyc: "Yes" | "No" | "";
  companyCName: string;
  companyCType: string;
  companyCJurisdiction: string;
  companyCRegistrationNumber: string;
  companyCAddress: string;
  companyCKyc: "Yes" | "No" | "";
};

type MultipartyPartyFieldKeys = {
  name: keyof MultipartyFormValues;
  type: keyof MultipartyFormValues;
  jurisdiction: keyof MultipartyFormValues;
  registrationNumber: keyof MultipartyFormValues;
  address: keyof MultipartyFormValues;
};

const NDA_AGENT_ID = 48;
const NDA_ENDPOINT = "/stream/nda";
const NDA_BUILDER_STEP_INTAKE_FORM = 1;
const NDA_BUILDER_STEP_INTAKE_SUMMARY = 2;
const NDA_BUILDER_STEP_TEMPLATE = 3;
const NDA_BUILDER_STEP_PARTY_COLLECTION = 4; // C6/C7: post-template, pre-draft
const NDA_BUILDER_STEP_DRAFT_READY = 5;
const NDA_BUILDER_STEP_LEGAL_HANDOFF = 6;

function isNdaDownloadStep(step: number | undefined): boolean {
  return step === NDA_BUILDER_STEP_DRAFT_READY || step === NDA_BUILDER_STEP_LEGAL_HANDOFF;
}

/** Strip exact duplicated halves (REG-04: initial user input concatenated twice). */
function dedupeRepeatedUserText(text: string): string {
  const trimmed = text.trim();
  if (trimmed.length < 24 || trimmed.length % 2 !== 0) {
    return text;
  }
  const half = trimmed.length / 2;
  if (trimmed.slice(0, half) === trimmed.slice(half)) {
    return trimmed.slice(0, half);
  }
  return text;
}

function isInputReviewResult(result: unknown): boolean {
  if (!result || typeof result !== "object") {
    return false;
  }
  const record = result as Record<string, unknown>;
  return (
    record.type === "nda_input_review" ||
    (Array.isArray(record.extracted_fields) &&
      typeof record.review_copy === "string")
  );
}

function isStructuredNdaAgentResult(result: unknown): boolean {
  if (!result || typeof result !== "object") {
    return false;
  }
  const record = result as Record<string, unknown>;
  return (
    isInputReviewResult(record) ||
    isMultipartyFormResult(record) ||
    isMarkdownDraftPreviewResult(record) ||
    record.recommended_template !== undefined ||
    record.full_draft !== undefined ||
    record.clause_reviews !== undefined ||
    (record.deviations !== undefined && record.is_compliant !== undefined) ||
    record.has_conflict !== undefined ||
    record.verification_status !== undefined ||
    record.is_complete !== undefined ||
    record.draft_type === "legal_submission_email" ||
    // FE-1: QnA agent result
    typeof record.answer === "string"
  );
}

function isMultipartyFormResult(result: unknown): boolean {
  if (!result || typeof result !== "object") {
    return false;
  }
  const record = result as Record<string, unknown>;
  return (
    record.variant !== undefined &&
    (hasPartySlotContract(record) ||
      (record.prefill_values !== undefined &&
        record.box_count !== undefined))
  );
}

function hasPartySlotContract(
  result: Record<string, unknown>,
): boolean {
  return (
    Array.isArray(result.parties) &&
    result.parties.length > 0 &&
    typeof (result.parties[0] as Record<string, unknown>)?.index === "number"
  );
}

type PartySlot = {
  index: number;
  label: string;
  active: boolean;
  editable: boolean;
  complete: boolean;
  retained?: boolean;
  name?: string;
  entity_type?: string;
  jurisdiction?: string;
  registration_number?: string;
  address?: string;
  kyc_completed?: boolean | null;
};

const COMPANY_LETTER_BY_SLOT_INDEX = ["A", "B", "C"] as const;

function isMarkdownDraftPreviewResult(result: unknown): boolean {
  if (!result || typeof result !== "object") {
    return false;
  }
  const record = result as Record<string, unknown>;
  return typeof record.markdown === "string" && record.docx_available !== undefined;
}

function getMarkdownDraftPreviewFromResponse(response: any): Record<string, unknown> | null {
  if (!Array.isArray(response)) return null;
  for (let i = response.length - 1; i >= 0; i -= 1) {
    const item = response[i];
    if (
      item &&
      typeof item === "object" &&
      item.agent_id === "nda_markdown_draft_preview" &&
      item.result &&
      isMarkdownDraftPreviewResult(item.result)
    ) {
      return item.result as Record<string, unknown>;
    }
  }
  return null;
}

type BuilderNavigationStep = {
  id: number;
  label: string;
  /** When false, completed tab cannot restore (no backend snapshot). */
  navigable?: boolean;
};

type BuilderNavigationState = {
  current_step: number;
  // N5-12: typed surface discriminator the FE renders by (null while BE rollout is partial).
  surface_type?: string | null;
  total_steps: number;
  can_go_back: boolean;
  undo_available?: boolean;
  steps: BuilderNavigationStep[];
};

function isDestructiveNdaTypeSwitch(
  currentNdaType: string,
  templateName: string,
  navigation: BuilderNavigationState | null | undefined,
): boolean {
  const nextType = extractNdaType(templateName).toLowerCase();
  if (!nextType || nextType === currentNdaType.toLowerCase()) {
    return false;
  }
  const step = navigation?.current_step ?? 0;
  return step >= 4;
}

function extractBuilderNavigation(
  data: any,
  currentChat: ChatMessage[],
): BuilderNavigationState | null {
  const direct =
    data?.builder_navigation ??
    data?.finalAnswer?.builder_navigation ??
    null;
  if (direct) {
    return direct as BuilderNavigationState;
  }

  const latestAgent = [...currentChat]
    .reverse()
    .find((message) => message.sender === "agent");
  const parsed =
    latestAgent?.parsedContent?.finalAnswer ?? latestAgent?.parsedContent;
  const fromChat = parsed?.builder_navigation;
  return fromChat ? (fromChat as BuilderNavigationState) : null;
}

function BuilderStepper({
  navigation,
  onStepClick,
}: {
  navigation: BuilderNavigationState;
  onStepClick?: (stepLabel: string) => void;
}) {
  if (!navigation?.steps?.length) {
    return null;
  }

  const totalSteps = navigation.total_steps || navigation.steps.length;
  const currentStep = navigation.current_step;
  const currentLabel =
    navigation.steps.find((step) => step.id === currentStep)?.label ?? "";

  const renderStepNode = (step: BuilderNavigationStep, index: number) => {
    const isCompleted = step.id < currentStep;
    const isCurrent = step.id === currentStep;
    const isFuture = step.id > currentStep;
    // Template step is clickable if completed and callback provided
    const isTemplateStep = step.label.toLowerCase() === "template";
    const isClickable = isTemplateStep && isCompleted && onStepClick;

    const handleClick = () => {
      if (isClickable) {
        onStepClick(step.label);
      }
    };

    return (
      <React.Fragment key={step.id}>
        {index > 0 && (
          <span
            className={`${styles.builderStepConnector} ${
              isFuture ? styles.builderStepConnectorFuture : ""
            }`}
            aria-hidden="true"
          />
        )}
        <div
          className={`${styles.builderStepNode} ${
            isCompleted ? styles.builderStepNodeDone : ""
          } ${isCurrent ? styles.builderStepNodeCurrent : ""} ${
            isFuture ? styles.builderStepNodeFuture : ""
          } ${isClickable ? styles.builderStepNodeClickable : ""}`}
          aria-current={isCurrent ? "step" : undefined}
          aria-label={`Step ${step.id}, ${step.label}${
            isCurrent ? ", current" : isCompleted ? ", completed" : ", upcoming"
          }${isClickable ? ", click to change" : ""}`}
          onClick={handleClick}
          style={isClickable ? { cursor: "pointer" } : undefined}
        >
          <span className={styles.builderStepIcon} aria-hidden="true">
            {isCompleted ? "✓" : isCurrent ? "●" : step.id}
          </span>
          <span className={styles.builderStepLabel}>{step.label}</span>
        </div>
      </React.Fragment>
    );
  };

  return (
    <Box className={styles.builderStepper}>
      <Typography className={styles.builderStepperHeading} component="p">
        Step {currentStep} of {totalSteps}
        {currentLabel ? `: ${currentLabel}` : ""}
      </Typography>
      <Typography className={styles.builderStepperHint} component="p">
        Your progress through the NDA workflow
      </Typography>

      <Box
        className={styles.builderStepperTrack}
        component="nav"
        aria-label="NDA builder progress"
      >
        {navigation.steps.map(renderStepNode)}
      </Box>
    </Box>
  );
}

const BOOLEAN_PROMPT_FIELDS: Array<{
  id: BooleanPromptFieldId;
  label: string;
  matchers: string[];
}> = [
  {
    id: "counterparty_confidentiality_required",
    label: "Counterparty Confidentiality Required",
    matchers: [
      "does the counterparty also need to protect their confidential information under the nda",
      "counterparty also need to protect",
    ],
  },
  {
    id: "kyc_completed",
    label: "KYC Completed",
    matchers: [
      "has the g42 onboarding/kyc of the counterparty been completed",
      "onboarding/kyc",
    ],
  },
  {
    id: "space42_presight_disclosure",
    label: "Space42 / Presight AI Disclosure",
    matchers: [
      "will any information about space42 or presight ai be disclosed",
      "space42 or presight ai",
    ],
  },
];

const COMPANY_TYPE_OPTIONS = ["LLC", "Ltd", "Inc.", "PJSC", "GmbH"];

const RISK_COLORS: Record<string, string> = {
  High: "#dc3545",
  Medium: "#856404",
  Low: "#155724",
  None: "#6b7280",
};

const RISK_BG: Record<string, string> = {
  High: "#fee",
  Medium: "#fff3cd",
  Low: "#d4edda",
  None: "#ffffff",
};

const REVIEW_TONES = {
  brand: { bg: "#f0fbf9", border: "#b2e5de", text: "#004141" },
  accept: { bg: "#d4edda", border: "#28a745", text: "#155724" },
  reject: { bg: "#fee", border: "#dc3545", text: "#dc3545" },
  modify: { bg: "#fff3cd", border: "#ffc107", text: "#856404" },
};

const getDeviationKey = (dev: any, index: number) =>
  dev.deviation_id ||
  `${dev.clause_ref || "draft"}-${dev.clause_name || index}-${index}`;

const getPriorityTone = (priority?: string) => {
  switch ((priority || "").toLowerCase()) {
    case "must_have":
      return REVIEW_TONES.reject;
    case "nice_to_have":
      return REVIEW_TONES.brand;
    case "negotiable":
      return REVIEW_TONES.modify;
    case "informational":
      return { bg: "#eef2ff", border: "#6366f1", text: "#3730a3" };
    default:
      return REVIEW_TONES.brand;
  }
};

const getChecklistStatusTone = (status?: string) => {
  const normalized = (status || "").toLowerCase();
  if (normalized === "compliant") return REVIEW_TONES.accept;
  if (normalized.includes("partial")) return REVIEW_TONES.modify;
  if (normalized.includes("non") || normalized.includes("not")) {
    return REVIEW_TONES.reject;
  }
  return REVIEW_TONES.brand;
};

function RiskChip({ level }: { level: string }) {
  return (
    <Chip
      label={level}
      size="small"
      sx={{
        backgroundColor: RISK_BG[level] || RISK_BG["None"],
        color: RISK_COLORS[level] || RISK_COLORS["None"],
        border: `1px solid ${RISK_COLORS[level] || RISK_COLORS["None"]}`,
        fontWeight: 600,
        fontSize: "12px",
      }}
    />
  );
}

type RenderAgentResultOptions = {
  messageId?: string;
  builderNavigation?: BuilderNavigationState | null;
  isLatestMessage?: boolean;
  onSendMessage?: (msg: string) => void;
};

// Helper: render a single agent result object by its shape
function renderAgentResult(
  item: { agent_id: string; result: any },
  options: RenderAgentResultOptions = {},
) {
  const result = item.result;
  const { messageId, builderNavigation, isLatestMessage, onSendMessage } = options;
  if (!result) return null;

  // N5-13: agent_name (item.agent_id) is the primary renderer key — render content by which agent
  // produced it, not by sniffing result shape. The shape checks below stay as a fallback for
  // sub-variants (e.g. nda_drafting_agent emits either a markdown preview or a full draft) and for
  // items whose agent_id is not in this registry.
  switch (item.agent_id) {
    case "nda_input_review_agent":
      if (isInputReviewResult(result))
        return (
          <InputReviewView
            data={result}
            onSendMessage={onSendMessage}
            isLatestMessage={isLatestMessage}
          />
        );
      break;
    case "nda_template_selector_agent":
      if (result.recommended_template !== undefined)
        return (
          <TemplateView
            data={result}
            messageId={messageId}
            isLatestMessage={isLatestMessage}
            onSendMessage={onSendMessage}
          />
        );
      break;
    case "nda_validation_agent":
      if (result.deviations !== undefined && result.is_compliant !== undefined)
        return <ValidationView data={result} messageId={messageId} />;
      break;
    case "nda_pre_output_review_agent":
      if (result.clause_reviews !== undefined)
        return <ReviewView data={result} />;
      break;
    case "nda_qa_agent":
      if (typeof result.answer === "string")
        return (
          <Box className={styles.container}>
            <MarkdownRenderer content={result.answer} />
          </Box>
        );
      break;
    default:
      break;
  }

  if (result.draft_type === "legal_submission_email") {
    return <EmailDraftView data={result} />;
  }
  if (isMarkdownDraftPreviewResult(result)) {
    return (
      <MarkdownDraftPreviewView
        data={result}
        isLatestMessage={isLatestMessage}
        onSendMessage={onSendMessage}
      />
    );
  }
  if (result.full_draft !== undefined) {
    return (
      <DraftView
        data={result}
        hideDownload={result.markdown_approval_pending === true}
      />
    );
  }
  if (result.clause_reviews !== undefined) return <ReviewView data={result} />;
  if (result.deviations !== undefined && result.is_compliant !== undefined)
    return <ValidationView data={result} messageId={messageId} />;
  if (result.has_conflict !== undefined)
    return <ConflictView data={result} />;
  if (result.recommended_template !== undefined)
    return (
      <TemplateView
        data={result}
        messageId={messageId}
        isLatestMessage={isLatestMessage}
        onSendMessage={options.onSendMessage}
      />
    );
  if (isMultipartyFormResult(result) && options.onSendMessage) {
    const record = result as Record<string, unknown>;
    if (hasPartySlotContract(record)) {
      return (
        <PartySlotStep4View
          parties={record.parties as PartySlot[]}
          variant={typeof record.variant === "string" ? record.variant : null}
          validationIssues={Array.isArray(record.validation_issues) ? (record.validation_issues as MultipartyValidationIssue[]) : undefined}
          onSendMessage={options.onSendMessage}
        />
      );
    }
    const composerProps = resolveMultipartyComposerProps(record);
    return (
      <MultipartyTemplateComposer
        savedValues={mapMultipartyPrefillValues(record.prefill_values)}
        variant={composerProps.variant}
        boxCountOverride={composerProps.boxCountOverride}
        editablePartyIndices={composerProps.editablePartyIndices}
        onSendMessage={options.onSendMessage}
      />
    );
  }
  if (result.verification_status !== undefined)
    return <CounterpartyView data={result} />;
  if (isInputReviewResult(result)) {
    return (
      <InputReviewView
        data={result}
        onSendMessage={options.onSendMessage}
        isLatestMessage={options.isLatestMessage}
      />
    );
  }
  if (result.is_complete !== undefined) return <IntakeView data={result} />;
  // FE-1: QnA render — nda_qa_agent returns {answer: string}
  if (
    (item.agent_id === "nda_qa_agent" || typeof result.answer === "string") &&
    typeof result.answer === "string"
  ) {
    return (
      <Box className={styles.container}>
        <MarkdownRenderer content={result.answer} />
      </Box>
    );
  }
  return null;
}

function EmailDraftView({ data }: { data: any }) {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents.currentAgent,
  );
  const numericAgentId = currentAgent?.id || 11;
  const [copied, setCopied] = useState(false);

  const bodyText = [
    data.greeting,
    ...(Array.isArray(data.body_paragraphs) ? data.body_paragraphs : []),
    [data.closing, data.signature_block].filter(Boolean).join("\n"),
  ]
    .filter(Boolean)
    .join("\n\n");

  const handleCopy = async () => {
    const formattedEmail = [
      `To: ${data.recipient || ""}`,
      ...(data.cc ? [`CC: ${data.cc}`] : []),
      `Subject: ${data.subject || ""}`,
      "",
      bodyText,
    ].join("\n");

    await navigator.clipboard.writeText(formattedEmail);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleOpenDraft = () => {
    // Record email action - fire and forget (non-blocking)
    dispatch(
      recordAgentAction({
        agentId: numericAgentId,
        actionType: "email",
        content: JSON.stringify({
          to: data.recipient || "",
          cc: data.cc || "",
          subject: data.subject || "",
          body: bodyText,
        }),
      })
    );

    const outlookUrl =
      `https://outlook.office365.com/mail/deeplink/compose?to=${encodeURIComponent(data.recipient || "")}` +
      (data.cc ? `&cc=${encodeURIComponent(data.cc)}` : "") +
      `&subject=${encodeURIComponent(data.subject || "")}` +
      `&body=${encodeURIComponent(bodyText)}`;
    window.open(outlookUrl, "_blank");
  };

  const handleDownloadDraft = () => {
    if (!data.draft_download_url) return;
    triggerNdaFileDownload(
      data.draft_download_url,
      dispatch,
      numericAgentId,
      "nda_draft",
      "nda_draft.docx",
    );
  };

  const handleDownloadRedline = () => {
    // Record download action - fire and forget (non-blocking)
    dispatch(
      recordAgentAction({
        agentId: numericAgentId,
        actionType: "download",
        content: JSON.stringify({
          type: "template_redline",
          url: data.template_redline_download_url,
        }),
      }),
    );

    window.open(data.template_redline_download_url, "_blank");
  };

  return (
    <Box className={styles.emailDraftCard}>
      <Box className={styles.emailDraftHeader}>
        <Box>
          <Typography className={styles.emailDraftTitle}>
            {data.title || "Legal Submission Email Draft"}
          </Typography>
        </Box>
        <Stack direction="row" gap={1} flexWrap="wrap">
          <Button
            size="small"
            variant="outlined"
            startIcon={<ContentCopyIcon />}
            onClick={handleCopy}
            sx={{
              textTransform: "none",
              borderColor: "#b2e5de",
              color: "#004141",
              "&:hover": {
                borderColor: "#00a888",
                backgroundColor: "#f0fbf9",
              },
            }}
          >
            {copied ? "Copied" : "Copy Email"}
          </Button>
          <Button
            size="small"
            variant="contained"
            startIcon={<OpenInNewIcon />}
            onClick={handleOpenDraft}
            sx={{
              textTransform: "none",
              backgroundColor: "#00a888",
              "&:hover": {
                backgroundColor: "#00947a",
              },
            }}
          >
            Open Email Draft
          </Button>
        </Stack>
      </Box>

      <Box className={styles.emailDraftBlock}>
        <Typography className={styles.emailDraftLabel}>To</Typography>
        <Typography className={styles.emailDraftValue}>
          {data.recipient || "G42NDAs@g42.ai"}
        </Typography>
      </Box>

      {data.cc && (
        <Box className={styles.emailDraftBlock}>
          <Typography className={styles.emailDraftLabel}>CC</Typography>
          <Typography className={styles.emailDraftValue}>{data.cc}</Typography>
        </Box>
      )}

      <Box className={styles.emailDraftBlock}>
        <Typography className={styles.emailDraftLabel}>Subject</Typography>
        <Typography className={styles.emailDraftValue}>
          {data.subject}
        </Typography>
      </Box>

      <Box className={styles.emailDraftBlock}>
        <Typography className={styles.emailDraftLabel}>Email Body</Typography>
        <Box className={styles.emailDraftBody}>
          {data.greeting && <Typography>{data.greeting}</Typography>}
          {(data.body_paragraphs || []).map(
            (paragraph: string, index: number) => (
              <Typography
                key={`${paragraph}-${index}`}
                sx={{ whiteSpace: "pre-line" }}
              >
                {paragraph}
              </Typography>
            ),
          )}
          {(data.closing || data.signature_block) && (
            <Typography sx={{ whiteSpace: "pre-line" }}>
              {[data.closing, data.signature_block].filter(Boolean).join("\n")}
            </Typography>
          )}
        </Box>
      </Box>

      {(data.included_items?.length > 0 ||
        data.draft_download_url ||
        data.template_redline_download_url ||
        data.note) && (
        <Box className={styles.emailDraftFooter}>
          {data.included_items?.length > 0 && (
            <Box>
              <Typography className={styles.emailDraftLabel}>
                Included In Draft
              </Typography>
              <Stack direction="row" gap={0.75} flexWrap="wrap" mt={1}>
                {data.included_items.map((item: string) => (
                  <Chip
                    key={item}
                    label={item}
                    size="small"
                    sx={{
                      backgroundColor: "#f0fbf9",
                      color: "#004141",
                      border: "1px solid #b2e5de",
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {data.draft_download_url && (
            <Button
              size="small"
              variant="text"
              startIcon={<DescriptionIcon />}
              onClick={handleDownloadDraft}
              sx={{
                marginTop: 1,
                alignSelf: "flex-start",
                textTransform: "none",
                color: "#00a888",
              }}
            >
              Download Draft
            </Button>
          )}

          {data.template_redline_download_url && (
            <Button
              size="small"
              variant="text"
              startIcon={<DescriptionIcon />}
              onClick={handleDownloadRedline}
              sx={{
                marginTop: 1,
                alignSelf: "flex-start",
                textTransform: "none",
                color: "#00a888",
              }}
            >
              Open Template Redline
            </Button>
          )}

          {data.note && (
            <Typography className={styles.emailDraftNote}>
              {data.note}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}

// HITL Conversation View - for all NDA HITL steps
// ---- Draft Confirm View ----
// Parses the "Final NDA Draft Inputs" markdown into SOW-style accordion rows

function parseDraftConfirmFields(
  text: string,
): { label: string; value: string; id: string }[] {
  const fields: { label: string; value: string; id: string }[] = [];
  const lines = text.split("\n");
  for (const line of lines) {
    const match = line.match(/^[-*]?\s*\*\*([^*]+?)\*\*:?\s+(.+)$/);
    if (!match) continue;
    const label = match[1].trim().replace(/:$/, "");
    const value = match[2].trim();
    if (!value || value === "Not specified") continue;
    const id = label.toLowerCase().replace(/[^a-z0-9]+/g, "_");
    fields.push({ label, value, id });
  }
  return fields;
}

type ApprovalReviewField = {
  id: string;
  label: string;
  value: string;
  hasWarning?: boolean;
};

type ApprovalPartyGroup = {
  sectionLabel: string;
  partyLetter: string;
  fields: ApprovalReviewField[];
  showEmptyPlaceholder?: boolean;
};

function partySlotToApprovalFields(slot: PartySlot): ApprovalReviewField[] {
  const letter = COMPANY_LETTER_BY_SLOT_INDEX[slot.index] || "A";
  const prefix = `Company ${letter}`;
  const rows: ApprovalReviewField[] = [];
  const push = (suffix: string, raw: string | undefined | null, fieldKey: string) => {
    const value = (raw || "").trim();
    if (!value && !slot.editable) return;
    rows.push({
      id: `company_${letter.toLowerCase()}_${fieldKey}`,
      label: `${prefix} ${suffix}`,
      value: value || "—",
    });
  };
  push("Name", slot.name, "name");
  // C1: Company Type removed from UI (internally inferred from counterparty name)
  push("Country", slot.jurisdiction, "jurisdiction");
  push(
    "Registration",
    slot.registration_number,
    "registration_number",
  );
  push("Principal Address", slot.address, "address");
  const kyc =
    slot.kyc_completed === true
      ? "Yes"
      : slot.kyc_completed === false
        ? "No"
        : "";
  push("KYC", kyc, "kyc");
  return rows;
}

function buildPartyGroupsFromSlots(
  parties: PartySlot[],
  variant?: string | null,
): ApprovalPartyGroup[] {
  const kind = normalizeNdaVariantKind(variant);
  const maxIndex = kind === "mutual" ? 1 : kind === "multiparty" ? 2 : 0;
  const groups: ApprovalPartyGroup[] = [];

  for (const slot of parties) {
    if (!slot.active || slot.index > maxIndex) continue;
    const letter = COMPANY_LETTER_BY_SLOT_INDEX[slot.index] || "A";
    const name = (slot.name || "").trim();
    const sectionLabel = name
      ? `Company ${letter} - ${name}`
      : `Company ${letter}`;
    const fields = partySlotToApprovalFields(slot);
    const hasContent = fields.some((f) => f.value && f.value !== "—");
    if (!hasContent && !slot.editable && slot.index !== 0) continue;
    if (!hasContent && slot.editable) {
      groups.push({
        sectionLabel,
        partyLetter: letter,
        fields: [
          {
            id: `company_${letter.toLowerCase()}_pending`,
            label: `${sectionLabel} details`,
            value: "Please complete the fields below",
          },
        ],
        showEmptyPlaceholder: true,
      });
      continue;
    }
    if (slot.index === 0 && !hasContent && slot.editable) {
      groups.push({
        sectionLabel,
        partyLetter: letter,
        fields: partySlotToApprovalFields(slot),
        showEmptyPlaceholder: true,
      });
      continue;
    }
    if (hasContent || slot.index === 0) {
      groups.push({ sectionLabel, partyLetter: letter, fields });
    }
  }
  return groups;
}

function groupDraftConfirmFields(
  fields: { label: string; value: string; id: string }[],
): { general: ApprovalReviewField[]; parties: ApprovalPartyGroup[] } {
  const general: ApprovalReviewField[] = [];
  const partyMap = new Map<string, ApprovalReviewField[]>();

  for (const field of fields) {
    const companyMatch = field.label.match(/^Company\s+([ABC])\s*[-–]?\s*(.*)$/i);
    if (companyMatch) {
      const letter = companyMatch[1].toUpperCase();
      const rest = companyMatch[2].trim();
      const suffixMatch = rest.match(
        /^(Name|Type|Jurisdiction|Registration(?:\s+Number)?|Address|KYC)\s*:?\s*(.*)$/i,
      );
      const sectionKey = letter;
      if (!partyMap.has(sectionKey)) partyMap.set(sectionKey, []);
      if (suffixMatch) {
        partyMap.get(sectionKey)!.push({
          id: field.id,
          label: `Company ${letter} ${suffixMatch[1]}`,
          value: suffixMatch[2].trim() || field.value,
        });
      } else if (rest) {
        partyMap.get(sectionKey)!.push({
          id: field.id,
          label: field.label,
          value: field.value,
        });
      }
      continue;
    }
    const looseMatch = field.label.match(
      /^(Company\s+[ABC])\s+(.+)$/i,
    );
    if (looseMatch) {
      const letter = looseMatch[1].slice(-1).toUpperCase();
      if (!partyMap.has(letter)) partyMap.set(letter, []);
      partyMap.get(letter)!.push({
        id: field.id,
        label: field.label,
        value: field.value,
      });
      continue;
    }
    general.push({
      id: field.id,
      label: field.label,
      value: field.value,
    });
  }

  const parties: ApprovalPartyGroup[] = [];
  for (const letter of ["A", "B", "C"]) {
    const partyFields = partyMap.get(letter);
    if (!partyFields?.length) continue;
    const nameField = partyFields.find((f) =>
      f.label.toLowerCase().includes("name"),
    );
    const sectionLabel = nameField?.value
      ? `Company ${letter} - ${nameField.value}`
      : `Company ${letter}`;
    parties.push({
      sectionLabel,
      partyLetter: letter,
      fields: partyFields,
    });
  }

  return { general, parties };
}

// Display-time normalization. The backend labels some fields with internal vocabulary
// ("Jurisdiction", "Legal Name", "Principal Address"); the UI standardizes on
// "Country" / "Name" / "Registered Address". This only affects RENDERED text — the raw
// label is still used verbatim when building submit messages, so the backend parser
// contract is untouched.
function normalizeNdaFieldLabel(label?: string | null): string {
  if (!label) return "";
  return label
    .replace(/\bJurisdiction\b/gi, "Country")
    .replace(/\bPrincipal Address\b/gi, "Registered Address")
    .replace(/\bLegal Name\b/gi, "Name");
}

function ApprovalReviewRow({
  field,
  onEdit,
  edited = false,
}: {
  field: ApprovalReviewField;
  onEdit?: (field: ApprovalReviewField) => void;
  edited?: boolean;
}) {
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        py: 1,
        px: 1.5,
        borderBottom: "1px solid #e8f4f2",
        "&:last-child": { borderBottom: "none" },
      }}
    >
      <Typography
        sx={{
          fontSize: "13px",
          fontWeight: 500,
          color: "#004141",
          minWidth: "38%",
          flexShrink: 0,
        }}
      >
        {normalizeNdaFieldLabel(field.label)}
      </Typography>
      <Typography
        sx={{
          fontSize: "13px",
          color: field.hasWarning ? "#856404" : "#403f3f",
          flex: 1,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={field.value}
      >
        {field.value}
      </Typography>
      {edited && (
        <Chip
          label="Edited"
          size="small"
          sx={{
            height: 20,
            fontSize: "0.7rem",
            fontWeight: 600,
            color: "#00695f",
            backgroundColor: "#d7f5ef",
            border: "1px solid #00a888",
            flexShrink: 0,
          }}
        />
      )}
      {onEdit && (
        <Tooltip title="Edit this field">
          <IconButton
            size="small"
            onClick={() => onEdit(field)}
            sx={{ padding: "4px", flexShrink: 0 }}
          >
            <EditIcon sx={{ fontSize: "16px", color: "#6b7280" }} />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
}

// Backend party field_map keys (colon format) for company_a/b/c_* edits. Display labels
// diverge ("Country"/"Registration"), so the collated batch is built from the canonical
// field id, not the label, to hit _extract_multiparty_fields_from_feedback deterministically.
const PARTY_EDIT_COLON_LABEL: Record<string, string> = {
  name: "Legal Name",
  legal_name: "Legal Name",
  jurisdiction: "Jurisdiction",
  registration_number: "Registration Number",
  address: "Address",
  type: "Type",
  kyc: "KYC",
};

function GroupedPartyApprovalView({
  groups,
  generalFields = [],
  warningFieldNames,
  onSendMessage,
  collateEdits = false,
}: {
  groups: ApprovalPartyGroup[];
  generalFields?: ApprovalReviewField[];
  warningFieldNames?: Set<string>;
  onSendMessage?: (msg: string) => void;
  collateEdits?: boolean;
}) {
  const [editingField, setEditingField] = useState<ApprovalReviewField | null>(
    null,
  );
  const [editValue, setEditValue] = useState("");
  // collateEdits: stage edits locally and send them in one message (multiparty review).
  const [pendingEdits, setPendingEdits] = useState<Record<string, string>>({});
  const pendingCount = Object.keys(pendingEdits).length;

  const handleEdit = (field: ApprovalReviewField) => {
    setEditingField(field);
    setEditValue(
      pendingEdits[field.id] ?? (field.value === "—" ? "" : field.value),
    );
  };

  const handleSaveEdit = () => {
    if (!editingField || !editValue.trim() || !onSendMessage) return;
    if (collateEdits) {
      setPendingEdits((prev) => ({
        ...prev,
        [editingField.id]: editValue.trim(),
      }));
      setEditingField(null);
      setEditValue("");
      return;
    }
    onSendMessage(`Update ${editingField.label} to "${editValue.trim()}"`);
    setEditingField(null);
    setEditValue("");
  };

  const handleApplyPendingEdits = () => {
    if (!onSendMessage || pendingCount === 0) return;
    const fieldById = new Map<string, ApprovalReviewField>();
    generalFields.forEach((f) => fieldById.set(f.id, f));
    groups.forEach((g) => g.fields.forEach((f) => fieldById.set(f.id, f)));
    const entries = Object.entries(pendingEdits);
    const isPartyId = (id: string) => /^company_[abc]_/i.test(id);
    // All-party batch → deterministic colon format; otherwise fall back to the NL form
    // (avoids mixing colon + "Update" lines, which the backend would split into two paths).
    const allParty = entries.every(([id]) => isPartyId(id));
    const lines = entries.map(([id, value]) => {
      const match = allParty ? id.match(/^company_([abc])_(.+)$/i) : null;
      if (match) {
        const label =
          PARTY_EDIT_COLON_LABEL[match[2].toLowerCase()] ||
          fieldById.get(id)?.label ||
          id;
        return `Company ${match[1].toUpperCase()} ${label}: ${value}`;
      }
      return `Update ${fieldById.get(id)?.label ?? id} to "${value}"`;
    });
    onSendMessage(lines.join("\n"));
    setPendingEdits({});
  };

  return (
    <Box>
      {generalFields.map((field) => (
        <ApprovalReviewRow
          key={field.id}
          field={{
            ...field,
            value: pendingEdits[field.id] ?? field.value,
            hasWarning: warningFieldNames?.has(field.label.toLowerCase()),
          }}
          onEdit={onSendMessage ? handleEdit : undefined}
          edited={field.id in pendingEdits}
        />
      ))}
      {groups.map((group) => (
        <Box
          key={group.partyLetter}
          sx={{
            mb: 2,
            border: "1px solid #b2e5de",
            borderRadius: "8px",
            overflow: "hidden",
            backgroundColor: "#fff",
          }}
        >
          <Box
            sx={{
              px: 1.5,
              py: 1,
              backgroundColor: "#f0fbf9",
              borderBottom: "1px solid #d0e8e4",
            }}
          >
            <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#004141" }}>
              {group.sectionLabel}
            </Typography>
          </Box>
          <Box sx={{ px: 0.5 }}>
            {group.fields.map((field) => (
              <ApprovalReviewRow
                key={field.id}
                field={{
                  ...field,
                  value: pendingEdits[field.id] ?? field.value,
                  hasWarning:
                    warningFieldNames?.has(field.label.toLowerCase()) ||
                    warningFieldNames?.has(field.id.toLowerCase()),
                }}
                onEdit={onSendMessage ? handleEdit : undefined}
                edited={field.id in pendingEdits}
              />
            ))}
          </Box>
        </Box>
      ))}

      {collateEdits && onSendMessage && pendingCount > 0 && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1.5 }}>
          <Button
            variant="contained"
            onClick={handleApplyPendingEdits}
            sx={{
              textTransform: "none",
              background: "#00a888",
              "&:hover": { background: "#00947a" },
            }}
          >
            Save {pendingCount} change{pendingCount === 1 ? "" : "s"}
          </Button>
        </Box>
      )}

      <Dialog
        open={Boolean(editingField)}
        onClose={() => setEditingField(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ fontSize: "15px", fontWeight: 600, color: "#004141" }}>
          Edit: {editingField?.label}
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setEditingField(null)}
            sx={{ color: "#6b7280", textTransform: "none" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={!editValue.trim()}
            sx={{
              textTransform: "none",
              background: "#00a888",
              "&:hover": { background: "#00947a" },
            }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const DraftConfirmAccordionRow = ({
  label,
  value,
  hasWarning,
  onEdit,
}: {
  label: string;
  value: string;
  hasWarning?: boolean;
  onEdit?: (label: string, currentValue: string) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const handleCopy = (e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(value);
    setCopySuccess(true);
    setTimeout(() => setCopySuccess(false), 2000);
  };

  const borderColor = hasWarning ? "#ffc107" : "#b2e5de";
  const headerBg = hasWarning ? "#fffbea" : "#f0fbf9";

  return (
    <div
      style={{
        border: `1px solid ${borderColor}`,
        borderRadius: "8px",
        marginBottom: "10px",
        overflow: "hidden",
        backgroundColor: "#fff",
      }}
    >
      <div
        style={{
          background: headerBg,
          padding: "12px 16px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: "8px",
          transition: "background 0.2s",
        }}
        onClick={() => setIsOpen(!isOpen)}
        onMouseEnter={(e) => (e.currentTarget.style.background = "#f3f4f6")}
        onMouseLeave={(e) => (e.currentTarget.style.background = headerBg)}
      >
        {isOpen ? (
          <ExpandLessIcon htmlColor="#006d77" sx={{ fontSize: "20px" }} />
        ) : (
          <ExpandMoreIcon htmlColor="#006d77" sx={{ fontSize: "20px" }} />
        )}
        <Typography
          sx={{ fontSize: "14px", fontWeight: 500, color: "#004141", flex: 1 }}
        >
          {normalizeNdaFieldLabel(label)}
        </Typography>
        {hasWarning && (
          <Tooltip title="This field needs correction">
            <WarningAmberIcon
              sx={{ fontSize: "18px", color: "#856404", mr: 0.5 }}
            />
          </Tooltip>
        )}
        {!isOpen && (
          <Typography
            sx={{
              fontSize: "13px",
              color: "#6b7280",
              maxWidth: "45%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {value}
          </Typography>
        )}
        {onEdit && (
          <Tooltip title="Edit this field">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(label, value);
              }}
              sx={{ padding: "3px", "&:hover": { backgroundColor: "#e5e7eb" } }}
            >
              <EditIcon sx={{ fontSize: "16px", color: "#6b7280" }} />
            </IconButton>
          </Tooltip>
        )}
      </div>
      {isOpen && (
        <div
          style={{
            padding: "14px 16px",
            background: "#ffffff",
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              position: "absolute",
              top: "10px",
              right: "12px",
            }}
          >
            {onEdit && (
              <Tooltip title="Edit this field">
                <IconButton
                  size="small"
                  onClick={() => onEdit(label, value)}
                  sx={{
                    padding: "3px",
                    "&:hover": { backgroundColor: "#f3f4f6" },
                  }}
                >
                  <EditIcon style={{ color: "#6b7280", fontSize: "18px" }} />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title={copySuccess ? "Copied!" : "Copy"}>
              <IconButton
                size="small"
                onClick={handleCopy}
                sx={{
                  padding: "3px",
                  "&:hover": { backgroundColor: "#f3f4f6" },
                }}
              >
                <ContentCopyIcon
                  style={{
                    color: copySuccess ? "#00a888" : "#9ca3af",
                    fontSize: "18px",
                  }}
                />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography
            sx={{
              fontSize: "14px",
              color: "#403f3f",
              lineHeight: 1.6,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              paddingRight: "60px",
            }}
          >
            {value}
          </Typography>
        </div>
      )}
    </div>
  );
};

function DraftConfirmView({
  text,
  onSendMessage,
}: {
  text: string;
  onSendMessage?: (msg: string) => void;
}) {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents.currentAgent,
  );
  const numericAgentId = currentAgent?.id || 11;

  const handleLinkClick = (url: string) => {
    dispatch(
      recordAgentAction({
        agentId: numericAgentId,
        actionType: "download",
        content: JSON.stringify({
          type: "markdown_link",
          url: url,
        }),
      }),
    );
  };

  const fields = parseDraftConfirmFields(text);
  if (fields.length === 0) return <MarkdownRenderer content={text} onLinkClick={handleLinkClick} />;

  const warningFieldNames = new Set<string>();
  const warningSection =
    text.split("**Please Fix These Items Before Drafting**")[1] ||
    text.split("**Please Review These Issues Before Drafting**")[1] ||
    "";
  warningSection.split("\n").forEach((line) => {
    const m = line.match(/^[-*]?\s*([^:]+):/);
    if (m) warningFieldNames.add(m[1].trim().toLowerCase());
  });

  const { general, parties } = groupDraftConfirmFields(fields);

  return (
    <Box className={styles.draftConfirmContainer}>
      <Box className={styles.sectionHeader}>
        <DescriptionIcon sx={{ color: "#004141", fontSize: 20, mr: 1 }} />
        <Typography className={styles.sectionTitle}>
          Final NDA Draft Inputs
        </Typography>
        <Chip
          label="Review before drafting"
          size="small"
          sx={{
            backgroundColor: "#f0fbf9",
            color: "#004141",
            border: "1px solid #b2e5de",
            ml: 1,
          }}
        />
      </Box>
      <GroupedPartyApprovalView
        groups={parties}
        generalFields={general}
        warningFieldNames={warningFieldNames}
        onSendMessage={onSendMessage}
      />
    </Box>
  );
}

function hasPartyFieldCorrectionGate(question?: string): boolean {
  const questionText = (question || "").toLowerCase();
  return questionText.includes("following fields need correction before drafting");
}

function isContextPromptQuestion(question?: string): boolean {
  const questionText = (question || "").toLowerCase();
  return (
    questionText.includes("what is the purpose of this nda") &&
    questionText.includes("who is the counterparty") &&
    questionText.includes("does the counterparty also need to protect") &&
    questionText.includes("has the g42 onboarding/kyc") &&
    (questionText.includes("presight ai") ||
      questionText.includes("presightai"))
  );
}

function getBooleanPromptFields(question?: string) {
  const questionText = (question || "").toLowerCase();
  return BOOLEAN_PROMPT_FIELDS.filter((field) =>
    field.matchers.some((matcher) => questionText.includes(matcher)),
  );
}

function isCompanyTypePrompt(question?: string): boolean {
  const questionText = (question || "").toLowerCase().trim();
  if (!questionText) return false;

  if (
    questionText.includes("what is the company type for ") &&
    !questionText.includes("counterparty's company type") &&
    !questionText.includes("company type for that g42 entity")
  ) {
    return true;
  }

  return (
    questionText.includes("what is the company type for that g42 entity") ||
    questionText.includes("what is the counterparty's company type")
  );
}

function inferCompanyTypeFromName(companyName?: string): string {
  const normalizedName = (companyName || "").trim().toLowerCase();
  if (!normalizedName) return "";

  const suffixPatterns: Array<[string, RegExp]> = [
    [
      "private company limited by shares",
      /\bprivate company limited by shares\b/i,
    ],
    ["PJSC", /\bpjsc\b/i],
    ["LLC", /\bllc\b/i],
    ["Ltd", /\bltd\b/i],
    ["Limited", /\blimited\b/i],
    ["Inc.", /\binc\.?\b/i],
    ["Corp.", /\bcorp(?:oration)?\.?\b/i],
    ["PLC", /\bplc\b/i],
    ["GmbH", /\bgmbh\b/i],
    ["S.A.", /\bs\.?a\.?\b/i],
  ];

  const matchedType = suffixPatterns.find(([, pattern]) =>
    pattern.test(normalizedName),
  );
  return matchedType?.[0] || "";
}

function getLatestResponseResult(
  response: any,
  matcher: (result: any) => boolean,
) {
  if (!Array.isArray(response)) return null;

  for (let i = response.length - 1; i >= 0; i -= 1) {
    const result = response[i]?.result;
    if (result && matcher(result)) {
      return result;
    }
  }

  return null;
}

function getLatestIntakePrefill(
  response: any,
): MultipartyTemplateIntakePrefill | null {
  return getLatestResponseResult(
    response,
    (result) =>
      result?.is_complete !== undefined &&
      (result?.purpose ||
        result?.counterparty_name ||
        result?.counterparty_country ||
        result?.counterparty_registration_number ||
        result?.counterparty_entity_type ||
        result?.counterparty_address ||
        result?.counterparty_confidentiality_required != null ||
        result?.kyc_completed != null ||
        result?.space42_presight_disclosure != null),
  );
}

// N3-07 / Phase 4: Extract nda_multiparty_form — party slots are authoritative
function getMultipartyFormFromResponse(response: any): {
  variant?: string;
  parties?: PartySlot[];
  boxCount?: number;
  editablePartyIndices?: number[];
  missingPartyIndices?: number[];
  partiesStepComplete?: boolean;
  prefillValues?: any;
  validationIssues?: MultipartyValidationIssue[];
  viewMode?: Step4ViewMode;
} | null {
  if (!Array.isArray(response)) return null;

  for (let i = response.length - 1; i >= 0; i -= 1) {
    const item = response[i];
    if (
      item &&
      typeof item === "object" &&
      item.agent_id === "nda_multiparty_form" &&
      item.result
    ) {
      const result = item.result;
      if (
        result.variant !== undefined &&
        Array.isArray(result.parties) &&
        result.parties.length > 0
      ) {
        return {
          variant: result.variant,
          parties: result.parties as PartySlot[],
          boxCount: result.box_count,
          editablePartyIndices: Array.isArray(result.editable_party_indices)
            ? result.editable_party_indices
            : undefined,
          missingPartyIndices: Array.isArray(result.missing_party_indices)
            ? result.missing_party_indices
            : undefined,
          partiesStepComplete: result.parties_step_complete === true,
          prefillValues: result.prefill_values,
          // N4-11: structured validation issues for Step 4
          validationIssues: Array.isArray(result.validation_issues)
            ? result.validation_issues
            : undefined,
          viewMode:
            result.view_mode === "collect" ||
            result.view_mode === "correct" ||
            result.view_mode === "approve"
              ? result.view_mode
              : undefined,
        };
      }
    }
  }

  return null;
}

function partySlotsToFormValues(parties: PartySlot[]): Partial<MultipartyFormValues> {
  const values: Partial<MultipartyFormValues> = {};
  for (const slot of parties) {
    const letter = COMPANY_LETTER_BY_SLOT_INDEX[slot.index];
    if (!letter) continue;
    const prefix = `company${letter}` as "companyA" | "companyB" | "companyC";
    (values as Record<string, string>)[`${prefix}Name`] = slot.name || "";
    (values as Record<string, string>)[`${prefix}Type`] = slot.entity_type || "";
    (values as Record<string, string>)[`${prefix}Jurisdiction`] =
      slot.jurisdiction || "";
    (values as Record<string, string>)[`${prefix}RegistrationNumber`] =
      slot.registration_number || "";
    (values as Record<string, string>)[`${prefix}Address`] = slot.address || "";
    (values as Record<string, string>)[`${prefix}Kyc`] =
      slot.kyc_completed === true
        ? "Yes"
        : slot.kyc_completed === false
          ? "No"
          : "";
  }
  return values;
}

function buildPartySlotStep4Intro(
  variant: string | null | undefined,
  parties: PartySlot[],
): string {
  const kind = normalizeNdaVariantKind(variant);
  const editable = parties.filter((slot) => slot.editable);
  if (editable.length === 0) {
    return "Please review all party details below.";
  }
  if (kind === "mutual") {
    return (
      "This is a two-party Mutual NDA between G42 and the counterparty. " +
      "Please review the counterparty details below."
    );
  }
  if (kind === "multiparty" && editable.length === 1 && editable[0].index === 1) {
    return (
      "This is a Multiparty NDA involving three total parties, including G42. " +
      "Party A was captured during intake. Please provide Party B details below."
    );
  }
  if (kind === "multiparty") {
    return (
      "This is a Multiparty NDA involving three total parties, including G42. " +
      "Please review and provide the remaining party details below."
    );
  }
  return "Please provide the remaining party details below.";
}

// N3-07: Search entire chat history for multiparty form with prefill_values (Company A data)
function getMultipartyFormFromChat(
  currentChat: ChatMessage[],
): {
  variant?: string;
  boxCount?: number;
  prefillValues?: any;
  prefill_values?: any;
} | null {
  for (let i = currentChat.length - 1; i >= 0; i -= 1) {
    const message = currentChat[i];
    if (message?.sender !== "agent") continue;

    const parsed = message.parsedContent?.finalAnswer ?? message.parsedContent;
    if (!parsed || !Array.isArray(parsed.response)) continue;

    const formData = getMultipartyFormFromResponse(parsed.response);
    if (formData?.prefillValues) {
      return {
        ...formData,
        prefill_values: formData.prefillValues, // Backend sends snake_case
      };
    }
  }

  return null;
}

function getCounterpartyPrefillFromChat(
  currentChat: import("../../../store/userSlice").ChatMessage[],
): MultipartyTemplateIntakePrefill | null {
  for (let i = currentChat.length - 1; i >= 0; i -= 1) {
    const msg = currentChat[i];
    if (msg.sender !== "user" || !msg.message) continue;

    const result: MultipartyTemplateIntakePrefill = {};
    for (const rawLine of msg.message.split("\n")) {
      const line = rawLine.trim();
      if (!line.includes(":")) continue;
      const [rawKey, ...rest] = line.split(":");
      const key = rawKey.trim().toLowerCase();
      const value = rest.join(":").trim();
      if (!value) continue;
      if (key === "counterparty name") result.counterparty_name = value;
      else if (key === "counterparty registration number")
        result.counterparty_registration_number = value;
      else if (key === "counterparty country")
        result.counterparty_country = value;
      else if (key === "counterparty address")
        result.counterparty_address = value;
    }

    if (result.counterparty_name || result.counterparty_country) return result;
  }
  return null;
}

function getLatestMultipartyFormPrefill(
  currentChat: ChatMessage[],
): Partial<MultipartyFormValues> | null {
  const fieldMap: Record<string, keyof MultipartyFormValues> = {
    "company a legal name": "companyAName",
    "company a name": "companyAName",
    "company a type": "companyAType",
    "company a jurisdiction": "companyAJurisdiction",
    "company a registration number": "companyARegistrationNumber",
    "company a address": "companyAAddress",
    "company b legal name": "companyBName",
    "company b name": "companyBName",
    "company b type": "companyBType",
    "company b jurisdiction": "companyBJurisdiction",
    "company b registration number": "companyBRegistrationNumber",
    "company b address": "companyBAddress",
    "company c legal name": "companyCName",
    "company c name": "companyCName",
    "company c type": "companyCType",
    "company c jurisdiction": "companyCJurisdiction",
    "company c registration number": "companyCRegistrationNumber",
    "company c address": "companyCAddress",
    "company a kyc": "companyAKyc",
    "company b kyc": "companyBKyc",
    "company c kyc": "companyCKyc",
  };

  for (let i = currentChat.length - 1; i >= 0; i -= 1) {
    const message = currentChat[i];
    if (message?.sender !== "user" || !message.message) continue;

    const parsedValues: Partial<MultipartyFormValues> = {};
    message.message.split("\n").forEach((rawLine) => {
      const line = rawLine.trim();
      if (!line || !line.includes(":")) return;

      const [rawKey, ...rawValueParts] = line.split(":");
      const mappedField = fieldMap[rawKey.trim().toLowerCase()];
      const mappedValue = rawValueParts.join(":").trim();
      if (mappedField && mappedValue) {
        parsedValues[mappedField] = mappedValue;
      }
    });

    if (Object.keys(parsedValues).length > 0) {
      return parsedValues;
    }
  }

  return null;
}

function mapMultipartyPrefillValues(
  raw: unknown,
): Partial<MultipartyFormValues> | null {
  if (!raw || typeof raw !== "object") {
    return null;
  }

  const values = raw as Record<string, unknown>;
  const readString = (key: string) =>
    typeof values[key] === "string" ? values[key] as string : "";
  const readKyc = (key: string): "Yes" | "No" | "" => {
    const value = values[key];
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (typeof value !== "string") return "";
    const normalized = value.trim().toLowerCase();
    if (normalized === "yes" || normalized === "true") return "Yes";
    if (normalized === "no" || normalized === "false") return "No";
    return "";
  };

  return {
    companyAName: readString("company_a_name"),
    companyAType: readString("company_a_type"),
    companyAJurisdiction: readString("company_a_jurisdiction"),
    companyARegistrationNumber: readString("company_a_registration_number"),
    companyAAddress: readString("company_a_address"),
    companyAKyc: readKyc("company_a_kyc"),
    companyBName: readString("company_b_name"),
    companyBType: readString("company_b_type"),
    companyBJurisdiction: readString("company_b_jurisdiction"),
    companyBRegistrationNumber: readString("company_b_registration_number"),
    companyBAddress: readString("company_b_address"),
    companyBKyc: readKyc("company_b_kyc"),
    companyCName: readString("company_c_name"),
    companyCType: readString("company_c_type"),
    companyCJurisdiction: readString("company_c_jurisdiction"),
    companyCRegistrationNumber: readString("company_c_registration_number"),
    companyCAddress: readString("company_c_address"),
    companyCKyc: readKyc("company_c_kyc"),
  };
}

function getNdaVariantFromResponse(response: any): string | null {
  const intake = getLatestIntakePrefill(response);
  if (typeof intake?.nda_type === "string" && intake.nda_type.trim()) {
    return intake.nda_type;
  }
  const form = getMultipartyFormFromResponse(response);
  if (typeof form?.variant === "string" && form.variant.trim()) {
    return form.variant;
  }
  return null;
}

function normalizeNdaVariantKind(
  variant?: string | null,
): "unilateral" | "mutual" | "multiparty" | "unknown" {
  const normalized = (variant || "").toLowerCase();
  if (normalized.includes("unilateral")) return "unilateral";
  if (normalized.includes("multiparty")) return "multiparty";
  if (normalized.includes("mutual")) return "mutual";
  return "unknown";
}

/** Only true for Step 4 party-collection HITL — not draft-confirm field corrections mentioning "Company B". */
function isPartyDetailsCollectionQuestion(question?: string): boolean {
  const questionText = (question || "").toLowerCase().trim();
  if (!questionText) return false;

  return (
    questionText.includes("please provide details for all parties") ||
    questionText.includes("please review and confirm all party details") ||
    questionText.includes("provide the remaining party details") ||
    questionText.includes("parties below")
  );
}

function isPreDraftFieldCorrectionQuestion(question?: string): boolean {
  return (question || "").toLowerCase().includes("fields need correction before drafting");
}

function filterEditablePartyIndicesForVariant(
  indices: number[] | undefined,
  variant?: string | null,
): number[] | undefined {
  if (!indices) return indices;
  const kind = normalizeNdaVariantKind(variant);
  if (kind === "mutual") return indices.filter((index) => index <= 1);
  if (kind === "unilateral") return indices.filter((index) => index === 0);
  return indices;
}

function resolveMultipartyComposerProps(
  result: Record<string, unknown>,
  response?: unknown,
): {
  variant: string | null;
  boxCountOverride?: number;
  editablePartyIndices?: number[];
} {
  const variant =
    getNdaVariantFromResponse(response) ??
    (typeof result.variant === "string" ? result.variant : null);

  const editablePartyIndices = filterEditablePartyIndicesForVariant(
    Array.isArray(result.editable_party_indices)
      ? (result.editable_party_indices as number[])
      : undefined,
    variant,
  );

  let boxCountOverride =
    typeof result.box_count === "number" ? (result.box_count as number) : undefined;
  if (boxCountOverride !== undefined && normalizeNdaVariantKind(variant) === "mutual") {
    boxCountOverride = Math.max(0, boxCountOverride);
  }

  return { variant, boxCountOverride, editablePartyIndices };
}

function isMultipartyTemplateFieldQuestion({
  question,
}: {
  question?: string;
  response?: any;
}): boolean {
  return isPartyDetailsCollectionQuestion(question);
}

function YesNoToggle({
  value,
  onChange,
}: {
  value: "Yes" | "No" | null;
  onChange: (v: "Yes" | "No") => void;
}) {
  const btnBase = {
    textTransform: "none" as const,
    minWidth: 64,
    fontWeight: 500,
    fontSize: "13px",
    borderColor: "#b2e5de",
  };
  const activeStyle = {
    ...btnBase,
    backgroundColor: "#00a888",
    color: "#fff",
    borderColor: "#00a888",
    "&:hover": { backgroundColor: "#00947a", borderColor: "#00947a" },
  };
  const inactiveStyle = {
    ...btnBase,
    color: "#004141",
    backgroundColor: "#fff",
    "&:hover": { backgroundColor: "#f0fbf9", borderColor: "#00a888" },
  };
  return (
    <Stack direction="row" gap={1}>
      <Button
        size="small"
        variant="outlined"
        sx={value === "Yes" ? activeStyle : inactiveStyle}
        onClick={() => onChange("Yes")}
      >
        Yes
      </Button>
      <Button
        size="small"
        variant="outlined"
        sx={value === "No" ? activeStyle : inactiveStyle}
        onClick={() => onChange("No")}
      >
        No
      </Button>
    </Stack>
  );
}

function boolToYesNo(value?: boolean | null): "Yes" | "No" | null {
  if (value === true) return "Yes";
  if (value === false) return "No";
  return null;
}

function ContextPromptComposer({
  onSendMessage,
  intakePrefill,
}: {
  onSendMessage: (msg: string) => void;
  intakePrefill?: MultipartyTemplateIntakePrefill | null;
}) {
  const [purpose, setPurpose] = useState(intakePrefill?.purpose || "");
  const [counterpartyName, setCounterpartyName] = useState(
    intakePrefill?.counterparty_name || "",
  );
  const [counterpartyRegistrationNumber, setCounterpartyRegistrationNumber] =
    useState(intakePrefill?.counterparty_registration_number || "");
  const [counterpartyCountry, setCounterpartyCountry] = useState(
    intakePrefill?.counterparty_country || "",
  );
  const [counterpartyAddress, setCounterpartyAddress] = useState(
    intakePrefill?.counterparty_address || "",
  );
  const [
    counterpartyConfidentialityRequired,
    setCounterpartyConfidentialityRequired,
  ] = useState<"Yes" | "No" | null>(
    boolToYesNo(intakePrefill?.counterparty_confidentiality_required),
  );
  const [kycCompleted, setKycCompleted] = useState<"Yes" | "No" | null>(
    boolToYesNo(intakePrefill?.kyc_completed),
  );
  const [space42, setSpace42] = useState<"Yes" | "No" | null>(
    boolToYesNo(intakePrefill?.space42_presight_disclosure),
  );
  const isComplete =
    purpose.trim() &&
    counterpartyName.trim() &&
    counterpartyRegistrationNumber.trim() &&
    counterpartyCountry.trim() &&
    counterpartyAddress.trim() &&
    counterpartyConfidentialityRequired !== null &&
    kycCompleted !== null &&
    space42 !== null;

  const handleSubmit = () => {
    if (!isComplete) return;
    const parts = [
      `Purpose: ${purpose.trim()}`,
      `Counterparty Name: ${counterpartyName.trim()}`,
      `Counterparty Registration Number: ${counterpartyRegistrationNumber.trim()}`,
      `Counterparty Country: ${counterpartyCountry.trim()}`,
      `Counterparty Address: ${counterpartyAddress.trim()}`,
      `Counterparty Confidentiality Required: ${counterpartyConfidentialityRequired}`,
      `KYC Completed: ${kycCompleted}`,
      `Space42 / Presight AI Disclosure: ${space42}`,
    ];
    onSendMessage(parts.join("\n\n"));
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      fontSize: "14px",
      "& fieldset": { borderColor: "#d0e8e4" },
      "&:hover fieldset": { borderColor: "#00a888" },
      "&.Mui-focused fieldset": { borderColor: "#00a888" },
    },
  };

  const questionSx = {
    fontWeight: 600,
    fontSize: "13px",
    color: "#004141",
    mb: 1,
  };

  return (
    <Box className={styles.intakeComposer}>
      {/* Header */}
      <Box className={styles.intakeComposerHeader}>
        <Box>
          <Typography className={styles.intakeComposerEyebrow}>
            NDA Request
          </Typography>
          <Typography className={styles.intakeComposerTitle}>
            Let's get the details
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#6b7280", mt: 0.5 }}>
            Fill in the fields below to create your NDA.
          </Typography>
        </Box>
      </Box>

      {/* Fields */}
      <Box sx={{ px: "20px", pt: "16px" }}>
        <Box mb={2.5}>
          <Typography sx={questionSx}>
            NDA Purpose
            <span style={{ color: "#dc3545" }}> *</span>
          </Typography>
          <Typography
            sx={{ fontSize: "12px", color: "#6b7280", mb: 1, lineHeight: 1.4 }}
          >
            Include the commercial context.
          </Typography>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            value={purpose}
            onChange={(e) => setPurpose(e.target.value)}
            placeholder="e.g. Sharing technical specs to evaluate a potential partnership for cloud infrastructure services."
            sx={fieldSx}
          />
        </Box>

        <Box mb={2.5}>
          <Typography sx={questionSx}>
            Counterparty Details
            <span style={{ color: "#dc3545" }}> *</span>
          </Typography>
          <Stack gap={1.5}>
            <TextField
              fullWidth
              size="small"
              label="Counterparty Name"
              value={counterpartyName}
              onChange={(e) => setCounterpartyName(e.target.value)}
              placeholder="e.g. Acme Technologies LLC"
              sx={fieldSx}
            />
            <Box
              sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}
            >
              <TextField
                fullWidth
                size="small"
                label="Registration Number"
                value={counterpartyRegistrationNumber}
                onChange={(e) =>
                  setCounterpartyRegistrationNumber(e.target.value)
                }
                placeholder="e.g. CN-2749815"
                sx={fieldSx}
              />
              <TextField
                fullWidth
                size="small"
                label="Country"
                value={counterpartyCountry}
                onChange={(e) => setCounterpartyCountry(e.target.value)}
                placeholder="e.g. United Arab Emirates"
                sx={fieldSx}
              />
            </Box>
            <TextField
              fullWidth
              size="small"
              label="Registered Address"
              value={counterpartyAddress}
              onChange={(e) => setCounterpartyAddress(e.target.value)}
              placeholder="e.g. Al Maryah Island, Abu Dhabi, UAE"
              sx={fieldSx}
            />
          </Stack>
        </Box>

        <Box mb={2}>
          <Typography sx={{ ...questionSx, mb: 1.5 }}>
            Quick Questions
            <span style={{ color: "#dc3545" }}> *</span>
          </Typography>
          <Stack gap={1.5}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Typography sx={{ fontSize: "13px", color: "#374151", flex: 1 }}>
                Does the counterparty also need to protect their confidential
                information under the NDA?
              </Typography>
              <YesNoToggle
                value={counterpartyConfidentialityRequired}
                onChange={setCounterpartyConfidentialityRequired}
              />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Typography sx={{ fontSize: "13px", color: "#374151", flex: 1 }}>
                Has the G42 onboarding/KYC of the counterparty been completed?
              </Typography>
              <YesNoToggle value={kycCompleted} onChange={setKycCompleted} />
            </Box>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 2,
              }}
            >
              <Typography sx={{ fontSize: "13px", color: "#374151", flex: 1 }}>
                Will any information about Space42 or Presight AI be disclosed?
              </Typography>
              <YesNoToggle value={space42} onChange={setSpace42} />
            </Box>
          </Stack>
        </Box>
      </Box>

      {/* Footer */}
      <Box className={styles.intakeComposerFooter}>
        {!isComplete && (
          <Typography sx={{ fontSize: "12px", color: "#9ca3af" }}>
            Fill in all fields to continue
          </Typography>
        )}
        <Button
          variant="contained"
          size="small"
          disabled={!isComplete}
          onClick={handleSubmit}
          sx={{
            textTransform: "none",
            backgroundColor: "#00a888",
            fontWeight: 600,
            px: 4,
            py: 1,
            borderRadius: "8px",
            "&:hover": { backgroundColor: "#00947a" },
            "&.Mui-disabled": { backgroundColor: "#b2e5de", color: "#fff" },
          }}
        >
          Continue →
        </Button>
      </Box>
    </Box>
  );
}

function PartySlotStep4View({
  parties,
  variant,
  validationIssues,
  onSendMessage,
}: {
  parties: PartySlot[];
  variant?: string | null;
  validationIssues?: MultipartyValidationIssue[];
  onSendMessage?: (msg: string) => void;
}) {
  const activeParties = parties.filter((slot) => slot.active);
  const editableParties = activeParties.filter((slot) => slot.editable);
  const variantKind = normalizeNdaVariantKind(variant);
  const validationState = buildValidationIssueLookup(validationIssues);
  const hasValidationIssues = validationState.blockingIssues.length > 0;
  const hasBlockingIssues = hasDraftBlockingIssues(validationIssues);

  const emptyFormValues = (): MultipartyFormValues => ({
    companyAName: "",
    companyAType: "",
    companyAJurisdiction: "",
    companyARegistrationNumber: "",
    companyAAddress: "",
    companyAKyc: "",
    companyBName: "",
    companyBType: "",
    companyBJurisdiction: "",
    companyBRegistrationNumber: "",
    companyBAddress: "",
    companyBKyc: "",
    companyCName: "",
    companyCType: "",
    companyCJurisdiction: "",
    companyCRegistrationNumber: "",
    companyCAddress: "",
    companyCKyc: "",
  });

  const applySlotToFormValues = (
    values: MultipartyFormValues,
    slot: PartySlot,
  ): MultipartyFormValues => {
    const kyc =
      slot.kyc_completed === true
        ? "Yes"
        : slot.kyc_completed === false
          ? "No"
          : "";
    if (slot.index === 0) {
      return {
        ...values,
        companyAName: slot.name || "",
        companyAType: slot.entity_type || inferCompanyTypeFromName(slot.name),
        companyAJurisdiction: slot.jurisdiction || "",
        companyARegistrationNumber: slot.registration_number || "",
        companyAAddress: slot.address || "",
        companyAKyc: kyc,
      };
    }
    if (slot.index === 1) {
      return {
        ...values,
        companyBName: slot.name || "",
        companyBType: slot.entity_type || inferCompanyTypeFromName(slot.name),
        companyBJurisdiction: slot.jurisdiction || "",
        companyBRegistrationNumber: slot.registration_number || "",
        companyBAddress: slot.address || "",
        companyBKyc: kyc,
      };
    }
    return {
      ...values,
      companyCName: slot.name || "",
      companyCType: slot.entity_type || inferCompanyTypeFromName(slot.name),
      companyCJurisdiction: slot.jurisdiction || "",
      companyCRegistrationNumber: slot.registration_number || "",
      companyCAddress: slot.address || "",
      companyCKyc: kyc,
    };
  };

  const [draftByIndex, setDraftByIndex] = useState<
    Record<number, MultipartyFormValues>
  >(() => {
    const initial: Record<number, MultipartyFormValues> = {};
    for (const slot of activeParties) {
      initial[slot.index] = applySlotToFormValues(emptyFormValues(), slot);
    }
    return initial;
  });

  useEffect(() => {
    setDraftByIndex((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const slot of activeParties) {
        if (!next[slot.index]) {
          next[slot.index] = applySlotToFormValues(emptyFormValues(), slot);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [parties]);

  const [duplicateError, setDuplicateError] = useState<string | null>(null);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  // BUG-07: Reset the submit lock when the server delivers a new response (parties or
  // validation_issues update), which covers both the normal success path and the case
  // where the SSE stream hung/errored and a subsequent payload arrives.
  useEffect(() => {
    setPendingSubmit(false);
  }, [parties, validationIssues]);

  const [editingCorrection, setEditingCorrection] = useState<{
    slotIndex: number;
    fieldKey: string;
    label: string;
  } | null>(null);
  const [correctionValue, setCorrectionValue] = useState("");
  // FE-7 C2: inline confirm prompt state for "confirm" severity issues
  const [showConfirmPrompt, setShowConfirmPrompt] = useState(false);
  const confirmIssues = (validationIssues ?? []).filter(
    (i) => i.severity === "confirm" && i.confirm_prompt,
  );

  // N4-19: Track pending edits from pencil UI (not yet sent to backend)
  const [pendingPartyEdits, setPendingPartyEdits] = useState<Array<{
    field_id: string;      // e.g., "company_b_address"
    party_index: number;   // 0=A, 1=B, 2=C
    value: string;         // New value
  }>>([]);

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      fontSize: "14px",
      "& fieldset": { borderColor: "#d0e8e4" },
      "&:hover fieldset": { borderColor: "#00a888" },
      "&.Mui-focused fieldset": { borderColor: "#00a888" },
    },
  };

  const questionSx = {
    fontWeight: 600,
    fontSize: "13px",
    color: "#004141",
    mb: 1,
  };

  const fieldHasWarning = (slotIndex: number, fieldKey: string): boolean => {
    const normalizedFieldKey = normalizeValidationIssueField(fieldKey);
    if (!normalizedFieldKey) return false;
    return validationState.warningFieldKeysBySlot.has(
      `${slotIndex}:${normalizedFieldKey}`,
    );
  };

  const warningCardSx = {
    borderColor: "#f59e0b",
    backgroundColor: "#fffaf0",
  };

  const warningRowSx = {
    px: 1,
    py: 0.75,
    borderRadius: "6px",
    backgroundColor: "#fff7e6",
  };

  const warningTextFieldSx = (hasWarning: boolean) => ({
    "& .MuiOutlinedInput-root": {
      backgroundColor: hasWarning ? "#fffaf0" : "#fff",
      "& fieldset": {
        borderColor: hasWarning ? "#f59e0b" : "#d0e8e4",
      },
      "&:hover fieldset": {
        borderColor: hasWarning ? "#d97706" : "#00a888",
      },
      "&.Mui-focused fieldset": {
        borderColor: hasWarning ? "#d97706" : "#00a888",
      },
    },
  });

  const slotFieldKeys = (index: number): MultipartyPartyFieldKeys => {
    const letter = COMPANY_LETTER_BY_SLOT_INDEX[index];
    return {
      name: `company${letter}Name` as keyof MultipartyFormValues,
      type: `company${letter}Type` as keyof MultipartyFormValues,
      jurisdiction: `company${letter}Jurisdiction` as keyof MultipartyFormValues,
      registrationNumber:
        `company${letter}RegistrationNumber` as keyof MultipartyFormValues,
      address: `company${letter}Address` as keyof MultipartyFormValues,
    };
  };

  const slotKycKey = (
    index: number,
  ): keyof Pick<
    MultipartyFormValues,
    "companyAKyc" | "companyBKyc" | "companyCKyc"
  > => `company${COMPANY_LETTER_BY_SLOT_INDEX[index]}Kyc` as "companyAKyc";

  const summaryRowValue = (
    slot: PartySlot,
    fieldKey: string,
    fallback?: string | null,
  ): string => {
    const draft = draftByIndex[slot.index];
    if (!draft) return fallback || "";
    const letter = COMPANY_LETTER_BY_SLOT_INDEX[slot.index];
    const fieldKeyMap: Record<string, keyof MultipartyFormValues> = {
      name: `company${letter}Name` as keyof MultipartyFormValues,
      type: `company${letter}Type` as keyof MultipartyFormValues,
      entity_type: `company${letter}Type` as keyof MultipartyFormValues,
      jurisdiction: `company${letter}Jurisdiction` as keyof MultipartyFormValues,
      registration_number:
        `company${letter}RegistrationNumber` as keyof MultipartyFormValues,
      address: `company${letter}Address` as keyof MultipartyFormValues,
      kyc: `company${letter}Kyc` as keyof MultipartyFormValues,
    };
    const draftKey = fieldKeyMap[fieldKey];
    if (!draftKey) return fallback || "";
    const draftValue = String(draft[draftKey] ?? "").trim();
    return draftValue || fallback || "";
  };

  const updateDraft = (
    index: number,
    field: keyof MultipartyFormValues,
    value: string,
  ) => {
    setDraftByIndex((prev) => {
      const current = prev[index] || ({} as MultipartyFormValues);
      const next = { ...current, [field]: value };
      const nameMatch = String(field).match(/^(company[ABC])Name$/);
      if (nameMatch) {
        const typeField =
          `${nameMatch[1]}Type` as keyof MultipartyFormValues;
        if (!String(current[typeField]).trim()) {
          next[typeField] = inferCompanyTypeFromName(value);
        }
      }
      return { ...prev, [index]: next };
    });
  };

  const slotIsFilled = (index: number): boolean => {
    const draft = draftByIndex[index];
    if (!draft) return false;
    const keys = slotFieldKeys(index);
    const kyc = draft[slotKycKey(index)];
    // C1: company type is inferred backend-side and no longer a UI gate.
    return Boolean(
      String(draft[keys.name]).trim() &&
        String(draft[keys.jurisdiction]).trim() &&
        String(draft[keys.registrationNumber]).trim() &&
        String(draft[keys.address]).trim() &&
        kyc,
    );
  };

  const isComplete =
    editableParties.length === 0 ||
    editableParties.every((slot) => slotIsFilled(slot.index));

  const handleSubmit = () => {
    // N4-19: Allow submission when there are pending edits, even if validation issues exist
    if (!isComplete || (hasBlockingIssues && pendingPartyEdits.length === 0) || !onSendMessage) return;
    // FE-7 C2: if confirm-severity issues exist and not yet acknowledged, show inline prompt
    if (confirmIssues.length > 0 && !showConfirmPrompt) {
      setShowConfirmPrompt(true);
      return;
    }
    setShowConfirmPrompt(false);

    // N4-19: Mode-aware Continue with structured edit support
    // Approval mode: no editable parties, all active parties complete
    if (editableParties.length === 0) {
      if (pendingPartyEdits.length > 0) {
        // Send accumulated structured edits
        // TODO: This needs to be implemented via Redux action that supports FormData
        // For now, fallback to natural language until Redux supports structured edits
        const naturalLanguageEdits = pendingPartyEdits.map(edit => {
          const match = edit.field_id.match(/^company_([abc])_(.+)$/);
          const letter = match ? match[1].toUpperCase() : 'A';
          const fieldName = match ? match[2].replace(/_/g, ' ') : 'field';
          return `Update Company ${letter} ${fieldName} to "${edit.value}"`;
        });
        onSendMessage(naturalLanguageEdits.join('\n'));
        setPendingPartyEdits([]);  // Clear after sending
        return;
      }
      // No pending edits - confirm progression
      onSendMessage("Confirmed. Please proceed to drafting.");
      return;
    }

    // Collect mode: editable parties exist → submit party details
    const parts: string[] = [];
    for (const slot of editableParties) {
      const letter = COMPANY_LETTER_BY_SLOT_INDEX[slot.index];
      const draft = draftByIndex[slot.index];
      const keys = slotFieldKeys(slot.index);
      const resolvedType =
        String(draft[keys.type]).trim() ||
        inferCompanyTypeFromName(String(draft[keys.name]));
      parts.push(
        `Company ${letter} Legal Name: ${String(draft[keys.name]).trim()}`,
        ...(resolvedType ? [`Company ${letter} Type: ${resolvedType}`] : []),
        `Company ${letter} Jurisdiction: ${String(draft[keys.jurisdiction]).trim()}`,
        `Company ${letter} Registration Number: ${String(draft[keys.registrationNumber]).trim()}`,
        `Company ${letter} Address: ${String(draft[keys.address]).trim()}`,
        `Company ${letter} KYC: ${draft[slotKycKey(slot.index)]}`,
      );
    }
    // N4-19 fix: inline pencil edits to already-captured, non-editable parties
    // (e.g. Party A from intake) accumulate in pendingPartyEdits. Collect-mode submit
    // only serialized editableParties, so those edits were dropped. Emit them as
    // structured "Company X <Field>: value" lines — the same colon format the backend
    // party parser (_extract_multiparty_fields_from_feedback) already applies for A/B/C.
    const editFieldLabels: Record<string, string> = {
      name: "Legal Name",
      type: "Type",
      jurisdiction: "Jurisdiction",
      registration_number: "Registration Number",
      address: "Address",
      kyc: "KYC",
    };
    for (const edit of pendingPartyEdits) {
      if (editableParties.some((slot) => slot.index === edit.party_index)) continue;
      const match = edit.field_id.match(/^company_([abc])_(.+)$/);
      if (!match) continue;
      const label = editFieldLabels[match[2]];
      if (!label) continue;
      parts.push(`Company ${match[1].toUpperCase()} ${label}: ${edit.value}`);
    }
    if (pendingPartyEdits.length > 0) setPendingPartyEdits([]);
    setDuplicateError(null);
    setPendingSubmit(true);
    onSendMessage(parts.join("\n\n"));
  };

  const startCorrectionEdit = (
    slotIndex: number,
    fieldKey: string,
    label: string,
    value: string,
  ) => {
    setEditingCorrection({ slotIndex, fieldKey, label });
    setCorrectionValue(value);
  };

  const cancelCorrectionEdit = () => {
    setEditingCorrection(null);
    setCorrectionValue("");
  };

  const saveCorrectionEdit = () => {
    if (!editingCorrection) return;
    const nextValue = correctionValue.trim();
    if (!nextValue) return;
    const letter = COMPANY_LETTER_BY_SLOT_INDEX[editingCorrection.slotIndex];
    if (!letter) return;

    // N4-19: Accumulate edit locally instead of sending immediately
    const fieldId = `company_${letter.toLowerCase()}_${editingCorrection.fieldKey}`;
    setPendingPartyEdits((prev) => {
      // Replace if same field already pending, otherwise append
      const filtered = prev.filter((e) => e.field_id !== fieldId);
      return [
        ...filtered,
        {
          field_id: fieldId,
          party_index: editingCorrection.slotIndex,
          value: nextValue,
        },
      ];
    });

    // Update local draft immediately for instant UI feedback
    setDraftByIndex((prev) => {
      const currentDraft = prev[editingCorrection.slotIndex];
      if (!currentDraft) return prev;

      const fieldKeyMap: Record<string, keyof MultipartyFormValues> = {
        name: `company${letter}Name` as keyof MultipartyFormValues,
        entity_type: `company${letter}Type` as keyof MultipartyFormValues,
        jurisdiction: `company${letter}Jurisdiction` as keyof MultipartyFormValues,
        registration_number: `company${letter}RegistrationNumber` as keyof MultipartyFormValues,
        address: `company${letter}Address` as keyof MultipartyFormValues,
        kyc: `company${letter}Kyc` as keyof MultipartyFormValues,
      };

      const formFieldKey = fieldKeyMap[editingCorrection.fieldKey];
      if (!formFieldKey) return prev;

      return {
        ...prev,
        [editingCorrection.slotIndex]: {
          ...currentDraft,
          [formFieldKey]: nextValue,
        },
      };
    });

    cancelCorrectionEdit();
  };

  const renderReviewRow = (
    slotIndex: number,
    row: { key: string; label: string; value: string },
  ) => {
    const hasWarning = fieldHasWarning(slotIndex, row.key);
    const isEditing =
      editingCorrection?.slotIndex === slotIndex &&
      editingCorrection.fieldKey === row.key;
    const canEdit = Boolean(onSendMessage);

    return (
      <Box
        key={row.key}
        sx={{
          display: "flex",
          gap: 1,
          alignItems: isEditing ? "center" : "flex-start",
          ...(hasWarning ? warningRowSx : {}),
        }}
      >
        <Typography
          sx={{
            fontSize: "13px",
            fontWeight: 600,
            minWidth: "96px",
            color: hasWarning ? "#9a3412" : "#2a312b",
          }}
        >
          {row.label}:
        </Typography>
        {isEditing ? (
          <>
            <TextField
              size="small"
              value={correctionValue}
              onChange={(event) => setCorrectionValue(event.target.value)}
              sx={{ ...fieldSx, flex: 1 }}
            />
            <Button
              size="small"
              variant="contained"
              onClick={saveCorrectionEdit}
              sx={{ textTransform: "none", backgroundColor: "#00a888" }}
            >
              Save
            </Button>
            <Button
              size="small"
              variant="outlined"
              onClick={cancelCorrectionEdit}
              sx={{ textTransform: "none" }}
            >
              Cancel
            </Button>
          </>
        ) : (
          <>
            <Typography
              sx={{
                fontSize: "13px",
                color: hasWarning ? "#7c2d12" : "#2a312b",
                wordBreak: "break-word",
                flex: 1,
              }}
            >
              {row.value}
            </Typography>
            {canEdit && (
              <Tooltip title={`Edit ${row.label.toLowerCase()}`}>
                <IconButton
                  size="small"
                  onClick={() =>
                    startCorrectionEdit(slotIndex, row.key, row.label, row.value)
                  }
                  sx={{ padding: "4px", color: "#9a3412" }}
                >
                  <EditIcon sx={{ fontSize: "16px" }} />
                </IconButton>
              </Tooltip>
            )}
          </>
        )}
      </Box>
    );
  };

  const variantLabel =
    variantKind === "mutual"
      ? "Mutual NDA"
      : variantKind === "multiparty"
        ? "Multiparty NDA"
        : variantKind === "unilateral"
          ? "Unilateral NDA"
          : "NDA";

  return (
    <Box className={styles.intakeComposer}>
      <Box className={styles.intakeComposerHeader}>
        <Box>
          <Typography className={styles.intakeComposerEyebrow}>
            {variantLabel}
          </Typography>
          <Typography className={styles.intakeComposerTitle}>
            Counterparty Details
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#6b7280", mt: 0.5 }}>
            {buildPartySlotStep4Intro(variant, activeParties)}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: "20px", pt: "16px" }}>
        {hasValidationIssues && (() => {
          const blockingIssues = validationState.blockingIssues;
          const warningIssues = validationState.issues.filter(
            (i) => i.severity === "warning",
          );
          const infoIssues = validationState.issues.filter(
            (i) => i.severity === "info",
          );

          return (
            <>
              {blockingIssues.length > 0 && (
                <Box className={styles.validationPanel} role="alert" sx={{ mb: 2 }}>
                  <Box className={styles.validationPanelHeader}>
                    <WarningAmberIcon sx={{ color: "#b45309", fontSize: 20 }} />
                    <Typography className={styles.validationPanelTitle}>
                      Action Required
                    </Typography>
                  </Box>
                  <Stack gap={1}>
                    {blockingIssues.map((issue, idx) => (
                      <Typography
                        key={`${issue.type}-${idx}`}
                        className={styles.validationPanelText}
                      >
                        {issue.message}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}

              {warningIssues.length > 0 && (
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    backgroundColor: "#fffbf0",
                    border: "1px solid #fbbf24",
                    borderRadius: "8px",
                  }}
                  role="status"
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#92400e" }}>
                      ⚠️ Please Review
                    </Typography>
                  </Box>
                  <Stack gap={1}>
                    {warningIssues.map((issue, idx) => (
                      <Typography
                        key={`${issue.type}-${idx}`}
                        sx={{ fontSize: "13px", color: "#78350f", lineHeight: 1.5 }}
                      >
                        {issue.message}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}

              {infoIssues.length > 0 && (
                <Box
                  sx={{
                    mb: 2,
                    p: 2,
                    backgroundColor: "#eff6ff",
                    border: "1px solid #3b82f6",
                    borderRadius: "8px",
                  }}
                  role="status"
                >
                  <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1e40af" }}>
                      ℹ️ For Your Information
                    </Typography>
                  </Box>
                  <Stack gap={1}>
                    {infoIssues.map((issue, idx) => (
                      <Typography
                        key={`${issue.type}-${idx}`}
                        sx={{ fontSize: "13px", color: "#1e3a8a", lineHeight: 1.5 }}
                      >
                        {issue.message}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}
            </>
          );
        })()}
        {activeParties.map((slot) => {
          if (slot.editable && pendingSubmit) {
            const draft = draftByIndex[slot.index];
            if (!draft) return null;
            const keys = slotFieldKeys(slot.index);
            return (
              <Box
                key={slot.index}
                mb={2}
                sx={{
                  p: 2,
                  borderRadius: "8px",
                  border: "1px solid #d0e8e4",
                  backgroundColor: "#f8fcfb",
                  ...(fieldHasWarning(slot.index, "name") ||
                  fieldHasWarning(slot.index, "type") ||
                  fieldHasWarning(slot.index, "jurisdiction") ||
                  fieldHasWarning(slot.index, "registration_number") ||
                  fieldHasWarning(slot.index, "address") ||
                  fieldHasWarning(slot.index, "kyc")
                    ? warningCardSx
                    : {}),
                }}
              >
                <Typography sx={{ ...questionSx, mb: 1 }}>{slot.label}</Typography>
                <Stack gap={0.5}>
                  {[
                    { key: "name", label: "Counterparty Name", value: String(draft[keys.name]) },
                    // C1: Company Type removed from UI (internally inferred)
                    {
                      key: "jurisdiction",
                      label: "Country",
                      value: String(draft[keys.jurisdiction]),
                    },
                    {
                      key: "registration_number",
                      label: "Registration Number",
                      value: String(draft[keys.registrationNumber]),
                    },
                    {
                      key: "address",
                      label: "Registered Address",
                      value: String(draft[keys.address]),
                    },
                    {
                      key: "kyc",
                      label: "KYC",
                      value: String(draft[slotKycKey(slot.index)]),
                    },
                  ].map((row) => (
                    renderReviewRow(slot.index, row)
                  ))}
                </Stack>
              </Box>
            );
          }

          if (slot.editable) {
            const draft = draftByIndex[slot.index];
            if (!draft) return null;
            const keys = slotFieldKeys(slot.index);
            const kycKey = slotKycKey(slot.index);
            return (
              <Box key={slot.index} mb={2.5}>
                <Typography sx={questionSx}>
                  {slot.label}
                  <span style={{ color: "#dc3545" }}> *</span>
                </Typography>
                <Stack gap={1.5}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Counterparty Name"
                    value={String(draft[keys.name])}
                    onChange={(e) =>
                      updateDraft(slot.index, keys.name, e.target.value)
                    }
                    sx={[fieldSx, fieldHasWarning(slot.index, "name") ? warningTextFieldSx(true) : null]}
                  />
                  {/* C1: Company Type field removed from UI (internally inferred) */}
                  <TextField
                    fullWidth
                    size="small"
                    label="Country"
                    value={String(draft[keys.jurisdiction])}
                    onChange={(e) =>
                      updateDraft(
                        slot.index,
                        keys.jurisdiction,
                        e.target.value,
                      )
                    }
                    sx={[fieldSx, fieldHasWarning(slot.index, "jurisdiction") ? warningTextFieldSx(true) : null]}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Registration Number"
                    value={String(draft[keys.registrationNumber])}
                    onChange={(e) =>
                      updateDraft(
                        slot.index,
                        keys.registrationNumber,
                        e.target.value,
                      )
                    }
                    sx={[fieldSx, fieldHasWarning(slot.index, "registration_number") ? warningTextFieldSx(true) : null]}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Registered Address"
                    value={String(draft[keys.address])}
                    onChange={(e) =>
                      updateDraft(slot.index, keys.address, e.target.value)
                    }
                    sx={[fieldSx, fieldHasWarning(slot.index, "address") ? warningTextFieldSx(true) : null]}
                  />
                  <Box>
                    <Typography sx={questionSx} mb={0.5}>
                      G42 onboarding / KYC completed for this party?
                      <span style={{ color: "#dc3545" }}> *</span>
                    </Typography>
                    <Stack direction="row" gap={1}>
                      {(["Yes", "No"] as const).map((option) => (
                        <Button
                          key={option}
                          size="small"
                          variant={
                            draft[kycKey] === option ? "contained" : "outlined"
                          }
                          onClick={() =>
                            updateDraft(slot.index, kycKey, option)
                          }
                          sx={{
                            textTransform: "none",
                            minWidth: 72,
                            backgroundColor:
                              draft[kycKey] === option
                                ? "#00a888"
                                : "transparent",
                            borderColor: "#00a888",
                            color:
                              draft[kycKey] === option ? "#fff" : "#004141",
                          }}
                        >
                          {option}
                        </Button>
                      ))}
                    </Stack>
                  </Box>
                </Stack>
              </Box>
            );
          }

          if (!slot.complete) return null;

          return (
            <Box
              key={slot.index}
              mb={2}
              sx={{
                p: 2,
                borderRadius: "8px",
                border: "1px solid #d0e8e4",
                backgroundColor: "#f8fcfb",
              }}
            >
              <Typography sx={{ ...questionSx, mb: 1 }}>{slot.label}</Typography>
              <Stack gap={0.5}>
                {[
                  {
                    key: "name",
                    label: "Counterparty Name",
                    value: summaryRowValue(slot, "name", slot.name),
                  },
                  // C1: Company Type removed from UI (internally inferred)
                  {
                    key: "jurisdiction",
                    label: "Country",
                    value: summaryRowValue(slot, "jurisdiction", slot.jurisdiction),
                  },
                  {
                    key: "registration_number",
                    label: "Registration Number",
                    value: summaryRowValue(
                      slot,
                      "registration_number",
                      slot.registration_number,
                    ),
                  },
                  {
                    key: "address",
                    label: "Registered Address",
                    value: summaryRowValue(slot, "address", slot.address),
                  },
                  {
                    key: "kyc",
                    label: "KYC",
                    value: summaryRowValue(
                      slot,
                      "kyc",
                      slot.kyc_completed !== null &&
                        slot.kyc_completed !== undefined
                        ? slot.kyc_completed
                          ? "Yes"
                          : "No"
                        : "",
                    ),
                  },
                ].map((row) => {
                  if (!row.value && !fieldHasWarning(slot.index, row.key)) return null;
                  return renderReviewRow(slot.index, row);
                })}
              </Stack>
            </Box>
          );
        })}

        {duplicateError && (
          <Typography sx={{ color: "#dc3545", fontSize: "13px", mb: 1 }}>
            {duplicateError}
          </Typography>
        )}
      </Box>

      <Box className={styles.intakeComposerFooter}>
        {!hasValidationIssues && editableParties.length === 0 && (
          <Typography sx={{ fontSize: "12px", color: "#6b7280", mb: 1 }}>
            Review each party below. Use the pencil icon to edit any field before continuing.
          </Typography>
        )}
        {hasValidationIssues && (
          <Typography sx={{ fontSize: "12px", color: pendingPartyEdits.length > 0 ? "#00a888" : (hasBlockingIssues ? "#b45309" : "#78350f") }}>
            {pendingPartyEdits.length > 0
              ? "Ready to submit your corrections. Click Continue to apply pending edits."
              : hasBlockingIssues
                ? "Please address the issues highlighted above before proceeding."
                : "Please review the information above. You may continue or make corrections if needed."}
          </Typography>
        )}
        {/* FE-7 C2: Inline confirm prompt for "confirm" severity issues */}
        {showConfirmPrompt && confirmIssues.length > 0 && (
          <Box
            sx={{
              mt: 1,
              p: 1.5,
              backgroundColor: "#fffaf0",
              border: "1px solid #fbbf24",
              borderRadius: "8px",
              display: "flex",
              flexDirection: "column",
              gap: 1,
            }}
          >
            {confirmIssues.map((issue, idx) => (
              <Typography
                key={idx}
                sx={{ fontSize: "13px", color: "#92400e", lineHeight: 1.5 }}
              >
                {issue.confirm_prompt}
              </Typography>
            ))}
            <Stack direction="row" gap={1} justifyContent="flex-end">
              <Button
                size="small"
                variant="outlined"
                onClick={() => setShowConfirmPrompt(false)}
                sx={{ textTransform: "none", borderColor: "#9ca3af", color: "#6b7280" }}
              >
                Go back
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={() => {
                  setShowConfirmPrompt(false);
                  // Re-invoke submit logic directly, skipping confirm gate
                  setPendingSubmit(true);
                  const parts: string[] = [];
                  for (const slot of editableParties) {
                    const letter = COMPANY_LETTER_BY_SLOT_INDEX[slot.index];
                    const draft = draftByIndex[slot.index];
                    const keys = slotFieldKeys(slot.index);
                    const resolvedType =
                      String(draft[keys.type]).trim() ||
                      inferCompanyTypeFromName(String(draft[keys.name]));
                    parts.push(
                      `Company ${letter} Legal Name: ${String(draft[keys.name]).trim()}`,
                      ...(resolvedType ? [`Company ${letter} Type: ${resolvedType}`] : []),
                      `Company ${letter} Jurisdiction: ${String(draft[keys.jurisdiction]).trim()}`,
                      `Company ${letter} Registration Number: ${String(draft[keys.registrationNumber]).trim()}`,
                      `Company ${letter} Address: ${String(draft[keys.address]).trim()}`,
                      `Company ${letter} KYC: ${draft[slotKycKey(slot.index)]}`,
                    );
                  }
                  if (parts.length === 0) {
                    onSendMessage?.("Confirmed. Please proceed to drafting.");
                  } else {
                    onSendMessage?.(parts.join("\n\n"));
                  }
                }}
                sx={{
                  textTransform: "none",
                  backgroundColor: "#f59e0b",
                  "&:hover": { backgroundColor: "#d97706" },
                }}
              >
                Yes, continue
              </Button>
            </Stack>
          </Box>
        )}
        <Button
          variant="contained"
          size="small"
          disabled={!isComplete || pendingSubmit || (hasBlockingIssues && pendingPartyEdits.length === 0) || !onSendMessage}
          onClick={handleSubmit}
          sx={{
            textTransform: "none",
            backgroundColor: "#00a888",
            fontWeight: 600,
            px: 4,
            py: 1,
            borderRadius: "8px",
            "&:hover": { backgroundColor: "#00947a" },
            "&.Mui-disabled": { backgroundColor: "#f7d9a8", color: "#8a4b00" },
          }}
        >
          Continue{pendingPartyEdits.length > 0 ? ` (${pendingPartyEdits.length} edit${pendingPartyEdits.length === 1 ? '' : 's'} pending)` : ' →'}
        </Button>
      </Box>
    </Box>
  );
}

function MultipartyTemplateComposer({
  intakePrefill,
  savedValues,
  variant,
  boxCountOverride,
  editablePartyIndices,
  missingPartyIndices,
  onSendMessage,
}: {
  intakePrefill?: MultipartyTemplateIntakePrefill | null;
  savedValues?: Partial<MultipartyFormValues> | null;
  variant?: string | null;
  boxCountOverride?: number;
  editablePartyIndices?: number[];
  missingPartyIndices?: number[];
  onSendMessage: (msg: string) => void;
}) {
  const [formValues, setFormValues] = useState<MultipartyFormValues>(() => ({
    companyAName:
      savedValues?.companyAName || intakePrefill?.counterparty_name || "",
    companyAType:
      savedValues?.companyAType ||
      inferCompanyTypeFromName(
        savedValues?.companyAName || intakePrefill?.counterparty_name,
      ),
    companyAJurisdiction:
      savedValues?.companyAJurisdiction ||
      intakePrefill?.counterparty_country ||
      "",
    companyARegistrationNumber:
      savedValues?.companyARegistrationNumber ||
      intakePrefill?.counterparty_registration_number ||
      "",
    companyAAddress:
      savedValues?.companyAAddress || intakePrefill?.counterparty_address || "",
    companyBName: savedValues?.companyBName || "",
    companyBType:
      savedValues?.companyBType ||
      inferCompanyTypeFromName(savedValues?.companyBName),
    companyBJurisdiction: savedValues?.companyBJurisdiction || "",
    companyBRegistrationNumber: savedValues?.companyBRegistrationNumber || "",
    companyBAddress: savedValues?.companyBAddress || "",
    companyCName: savedValues?.companyCName || "",
    companyCType:
      savedValues?.companyCType ||
      inferCompanyTypeFromName(savedValues?.companyCName),
    companyCJurisdiction: savedValues?.companyCJurisdiction || "",
    companyCRegistrationNumber: savedValues?.companyCRegistrationNumber || "",
    companyCAddress: savedValues?.companyCAddress || "",
    companyAKyc: savedValues?.companyAKyc || "",
    companyBKyc: savedValues?.companyBKyc || "",
    companyCKyc: savedValues?.companyCKyc || "",
  }));

  const [duplicateError, setDuplicateError] = useState<string | null>(null);

  const hasCompanyAPrefill = Boolean(
    intakePrefill?.counterparty_name ||
      intakePrefill?.counterparty_country ||
      intakePrefill?.counterparty_registration_number ||
      intakePrefill?.counterparty_address ||
      savedValues?.companyAName ||
      savedValues?.companyAJurisdiction ||
      savedValues?.companyARegistrationNumber ||
      savedValues?.companyAAddress,
  );

  const handleFieldChange = (
    field: keyof MultipartyFormValues,
    value: string,
  ) => {
    setFormValues((prev) => {
      const next = { ...prev, [field]: value };
      const nameFieldMatch = String(field).match(/^(company[ABC])Name$/);
      if (nameFieldMatch) {
        const typeField =
          `${nameFieldMatch[1]}Type` as keyof MultipartyFormValues;
        if (!String(prev[typeField]).trim()) {
          next[typeField] = inferCompanyTypeFromName(value);
        }
      }
      return next;
    });
  };

  const resolvedCompanyBType =
    formValues.companyBType.trim() ||
    inferCompanyTypeFromName(formValues.companyBName);
  const resolvedCompanyCType =
    formValues.companyCType.trim() ||
    inferCompanyTypeFromName(formValues.companyCName);

  const companyCFilled =
    formValues.companyCName.trim() &&
    formValues.companyCJurisdiction.trim() &&
    formValues.companyCRegistrationNumber.trim() &&
    formValues.companyCAddress.trim();

  const variantKind = normalizeNdaVariantKind(variant);
  const additionalPartySlots =
    boxCountOverride ??
    (variantKind === "unilateral"
      ? 0
      : variantKind === "mutual"
        ? 0
        : variantKind === "multiparty"
          ? 1
          : 1);

  const showCompanyA =
    editablePartyIndices !== undefined
      ? editablePartyIndices.includes(0)
      : false;
  const showCompanyB =
    editablePartyIndices !== undefined
      ? editablePartyIndices.includes(1)
      : additionalPartySlots >= 1;
  const showCompanyC =
    variantKind === "multiparty" &&
    (editablePartyIndices !== undefined
      ? editablePartyIndices.includes(2)
      : additionalPartySlots >= 2);
  const intakePartyIncomplete =
    Array.isArray(missingPartyIndices) &&
    missingPartyIndices.includes(0) &&
    !showCompanyA;

  const boxCount = additionalPartySlots;
  const variantLabel =
    variantKind === "mutual"
      ? "Mutual NDA"
      : variantKind === "multiparty"
        ? "Multiparty NDA"
        : variantKind === "unilateral"
          ? "Unilateral NDA"
          : "NDA";

  const normalizeIdentity = (value: string) =>
    value.trim().toLowerCase().replace(/[^a-z0-9]/g, "");

  const validateParties = (): string | null => {
    const parties = [
      {
        label: "Company B",
        name: formValues.companyBName,
        reg: formValues.companyBRegistrationNumber,
        address: formValues.companyBAddress,
      },
      {
        label: "Company C",
        name: formValues.companyCName,
        reg: formValues.companyCRegistrationNumber,
        address: formValues.companyCAddress,
      },
    ];

    for (let i = 0; i < parties.length; i += 1) {
      for (let j = i + 1; j < parties.length; j += 1) {
        const left = parties[i];
        const right = parties[j];
        if (
          normalizeIdentity(left.name) &&
          normalizeIdentity(left.name) === normalizeIdentity(right.name)
        ) {
          return `${left.label} and ${right.label} cannot share the same legal name.`;
        }
        if (
          normalizeIdentity(left.reg) &&
          normalizeIdentity(left.reg) === normalizeIdentity(right.reg)
        ) {
          return `${left.label} and ${right.label} cannot share the same registration number.`;
        }
        if (
          normalizeIdentity(left.address) &&
          normalizeIdentity(left.address) === normalizeIdentity(right.address)
        ) {
          return `${left.label} and ${right.label} cannot share the same address.`;
        }
      }
    }
    return null;
  };

  const companyAFilled =
    formValues.companyAName.trim() &&
    formValues.companyAJurisdiction.trim() &&
    formValues.companyARegistrationNumber.trim() &&
    formValues.companyAAddress.trim() &&
    formValues.companyAKyc;

  const isComplete =
    (!showCompanyA && !showCompanyB && !showCompanyC) ||
    ((showCompanyA ? companyAFilled : true) &&
      (!showCompanyB ||
        (formValues.companyBName.trim() &&
          formValues.companyBJurisdiction.trim() &&
          formValues.companyBRegistrationNumber.trim() &&
          formValues.companyBAddress.trim() &&
          formValues.companyBKyc)) &&
      (!showCompanyC || (companyCFilled && formValues.companyCKyc)));

  const handleSubmit = () => {
    if (!isComplete) return;

    const dedupIssue = validateParties();
    if (dedupIssue) {
      setDuplicateError(dedupIssue);
      return;
    }
    setDuplicateError(null);

    const parts: string[] = [];

    if (!showCompanyA && !showCompanyB && !showCompanyC) {
      onSendMessage("Confirmed. Please proceed to drafting.");
      return;
    }

    if (showCompanyA) {
      parts.push(
        `Company A Legal Name: ${formValues.companyAName.trim()}`,
        ...(formValues.companyAType
          ? [`Company A Type: ${formValues.companyAType.trim()}`]
          : []),
        `Company A Jurisdiction: ${formValues.companyAJurisdiction.trim()}`,
        `Company A Registration Number: ${formValues.companyARegistrationNumber.trim()}`,
        `Company A Address: ${formValues.companyAAddress.trim()}`,
        `Company A KYC: ${formValues.companyAKyc}`,
      );
    }

    if (showCompanyB) {
      parts.push(
        `Company B Legal Name: ${formValues.companyBName.trim()}`,
        ...(resolvedCompanyBType
          ? [`Company B Type: ${resolvedCompanyBType}`]
          : []),
        `Company B Jurisdiction: ${formValues.companyBJurisdiction.trim()}`,
        `Company B Registration Number: ${formValues.companyBRegistrationNumber.trim()}`,
        `Company B Address: ${formValues.companyBAddress.trim()}`,
        `Company B KYC: ${formValues.companyBKyc}`,
      );
    }

    if (showCompanyC) {
      parts.push(
        `Company C Legal Name: ${formValues.companyCName.trim()}`,
        ...(resolvedCompanyCType
          ? [`Company C Type: ${resolvedCompanyCType}`]
          : []),
        `Company C Jurisdiction: ${formValues.companyCJurisdiction.trim()}`,
        `Company C Registration Number: ${formValues.companyCRegistrationNumber.trim()}`,
        `Company C Address: ${formValues.companyCAddress.trim()}`,
        `Company C KYC: ${formValues.companyCKyc}`,
      );
    }

    onSendMessage(parts.join("\n\n"));
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      fontSize: "14px",
      "& fieldset": { borderColor: "#d0e8e4" },
      "&:hover fieldset": { borderColor: "#00a888" },
      "&.Mui-focused fieldset": { borderColor: "#00a888" },
    },
  };

  const questionSx = {
    fontWeight: 600,
    fontSize: "13px",
    color: "#004141",
    mb: 1,
  };

  const renderPartySection = ({
    title,
    description,
    keys,
    kycKey,
    partyIndex,
    optional,
    onRemove,
  }: {
    title: string;
    description: string;
    keys: MultipartyPartyFieldKeys;
    kycKey: keyof Pick<
      MultipartyFormValues,
      "companyAKyc" | "companyBKyc" | "companyCKyc"
    >;
    partyIndex: number;
    optional?: boolean;
    onRemove?: () => void;
  }) => (
    <Box mb={2.5}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 1,
        }}
      >
        <Typography sx={questionSx} mb={0}>
          {title}
          {!optional && <span style={{ color: "#dc3545" }}> *</span>}
        </Typography>
        {optional && onRemove && (
          <Button
            size="small"
            onClick={onRemove}
            sx={{
              textTransform: "none",
              fontSize: "12px",
              color: "#6b7280",
              minWidth: "auto",
              px: 1,
              py: 0.25,
              "&:hover": { color: "#dc3545", backgroundColor: "transparent" },
            }}
          >
            Remove
          </Button>
        )}
      </Box>
      <Typography
        sx={{ fontSize: "12px", color: "#6b7280", mb: 1.5, lineHeight: 1.4 }}
      >
        {description}
      </Typography>
      <Stack gap={1.5}>
        <TextField
          fullWidth
          size="small"
          label="Counterparty Name"
          value={formValues[keys.name]}
          onChange={(e) => handleFieldChange(keys.name, e.target.value)}
          placeholder={`e.g. ${title} Holdings LLC`}
          sx={fieldSx}
        />
        {/* C1: Company Type field removed from UI (internally inferred) */}
        <TextField
          fullWidth
          size="small"
          label="Country"
          value={formValues[keys.jurisdiction]}
          onChange={(e) =>
            handleFieldChange(keys.jurisdiction, e.target.value)
          }
          placeholder="e.g. United Arab Emirates"
          sx={fieldSx}
        />
        <TextField
          fullWidth
          size="small"
          label="Registration Number"
          value={formValues[keys.registrationNumber]}
          onChange={(e) =>
            handleFieldChange(keys.registrationNumber, e.target.value)
          }
          placeholder="e.g. CN-2749815"
          sx={fieldSx}
        />
        <TextField
          fullWidth
          size="small"
          label="Registered Address"
          value={formValues[keys.address]}
          onChange={(e) => handleFieldChange(keys.address, e.target.value)}
          placeholder="e.g. Al Maryah Island, Abu Dhabi, UAE"
          sx={fieldSx}
        />
        <Box>
          <Typography sx={questionSx} mb={0.5}>
            G42 onboarding / KYC completed for this party?
            <span style={{ color: "#dc3545" }}> *</span>
          </Typography>
          <Stack direction="row" gap={1}>
            {(["Yes", "No"] as const).map((option) => (
              <Button
                key={option}
                size="small"
                variant={
                  formValues[kycKey] === option ? "contained" : "outlined"
                }
                onClick={() => handleFieldChange(kycKey, option)}
                sx={{
                  textTransform: "none",
                  minWidth: 72,
                  backgroundColor:
                    formValues[kycKey] === option ? "#00a888" : "transparent",
                  borderColor: "#00a888",
                  color: formValues[kycKey] === option ? "#fff" : "#004141",
                }}
              >
                {option}
              </Button>
            ))}
          </Stack>
        </Box>
        {/* N3-07 Task 0: Upload control with party_index */}
        <Box sx={{ mt: 1.5 }}>
          <Typography sx={{ ...questionSx, mb: 0.5 }}>
            Upload trade licence / incorporation doc (optional)
          </Typography>
          <Button
            size="small"
            variant="outlined"
            component="label"
            sx={{
              textTransform: "none",
              borderColor: "#00a888",
              color: "#004141",
              fontSize: "13px",
              px: 2,
              py: 0.75,
              "&:hover": { borderColor: "#00947a", backgroundColor: "#f0fbf9" },
            }}
          >
            Choose File for Party {partyIndex + 1}
            <input
              type="file"
              hidden
              accept=".pdf,.docx,.doc"
              data-party-index={partyIndex}
            />
          </Button>
        </Box>
      </Stack>
    </Box>
  );

  return (
    <Box className={styles.intakeComposer}>
      <Box className={styles.intakeComposerHeader}>
        <Box>
          <Typography className={styles.intakeComposerEyebrow}>
            {variantLabel}
          </Typography>
          <Typography className={styles.intakeComposerTitle}>
            Add the counterparty details
          </Typography>
          <Typography sx={{ fontSize: "12px", color: "#6b7280", mt: 0.5 }}>
            Fill this in once and we&apos;ll use it to continue the draft.
          </Typography>
        </Box>
      </Box>

      <Box sx={{ px: "20px", pt: "16px" }}>
        <Box
          sx={{
            mb: 2.5,
            p: 1.5,
            borderRadius: "10px",
            backgroundColor: "#f0fbf9",
            border: "1px solid #b2e5de",
          }}
        >
          <Typography
            sx={{ fontSize: "12px", color: "#004141", lineHeight: 1.5 }}
          >
            {/* N3-07: Intake already captured the first counterparty. This form collects additional parties only. */}
            {intakePartyIncomplete
              ? "Some intake party details are still required. Please go back to NDA details or complete the fields below."
              : showCompanyA
              ? "Please complete the first counterparty (Party A) details below."
              : showCompanyB && !showCompanyC
              ? "The first counterparty was captured during intake. Enter the remaining counterparty details below."
              : showCompanyB && showCompanyC
              ? "The first counterparty was captured during intake. Enter the second and third counterparty details below."
              : "All required counterparty details are ready. Click Continue to proceed to drafting."}
          </Typography>
        </Box>

        {/* N3-07: Company A (intake party) is not shown here - it was already captured at intake.
            We only show Company B (party 2) and Company C (party 3) for additional parties beyond intake. */}

        {showCompanyA && renderPartySection({
          title: "Company A",
          description: "First counterparty from intake — complete any missing fields.",
          keys: {
            name: "companyAName",
            type: "companyAType",
            jurisdiction: "companyAJurisdiction",
            registrationNumber: "companyARegistrationNumber",
            address: "companyAAddress",
          },
          kycKey: "companyAKyc",
          partyIndex: 0,
        })}

        {/* N3-07: Render Company B (party 2) if boxCount >= 1 (Mutual or Multiparty) */}
        {showCompanyB && renderPartySection({
          title: "Company B",
          description: "Additional counterparty details to include in the NDA.",
          keys: {
            name: "companyBName",
            type: "companyBType",
            jurisdiction: "companyBJurisdiction",
            registrationNumber: "companyBRegistrationNumber",
            address: "companyBAddress",
          },
          kycKey: "companyBKyc",
          partyIndex: 1,
        })}

        {/* N3-07: Render Company C (party 3) only if boxCount >= 2 (Multiparty) */}
        {showCompanyC && renderPartySection({
          title: "Company C",
          description: "Additional counterparty details to include in the NDA.",
          keys: {
            name: "companyCName",
            type: "companyCType",
            jurisdiction: "companyCJurisdiction",
            registrationNumber: "companyCRegistrationNumber",
            address: "companyCAddress",
          },
          kycKey: "companyCKyc",
          partyIndex: 2,
        })}
      </Box>

      <Box className={styles.intakeComposerFooter}>
        {duplicateError && (
          <Typography sx={{ fontSize: "12px", color: "#dc3545", mb: 1 }}>
            {duplicateError}
          </Typography>
        )}
        {!isComplete && (
          <Typography sx={{ fontSize: "12px", color: "#9ca3af" }}>
            {/* N3-07 Task 0: Dynamic footer text based on boxCount */}
            {showCompanyB && !showCompanyC && "Fill in Company B fields to continue"}
            {showCompanyB && showCompanyC && "Fill in Company B and C fields to continue"}
          </Typography>
        )}
        <Button
          variant="contained"
          size="small"
          disabled={!isComplete}
          onClick={handleSubmit}
          sx={{
            textTransform: "none",
            backgroundColor: "#00a888",
            fontWeight: 600,
            px: 4,
            py: 1,
            borderRadius: "8px",
            "&:hover": { backgroundColor: "#00947a" },
            "&.Mui-disabled": { backgroundColor: "#b2e5de", color: "#fff" },
          }}
        >
          Continue →
        </Button>
      </Box>
    </Box>
  );
}

function BooleanPromptComposer({
  fields,
  onSendMessage,
}: {
  fields: Array<{ id: BooleanPromptFieldId; label: string }>;
  onSendMessage: (msg: string) => void;
}) {
  const [answers, setAnswers] = useState<
    Record<string, "Yes" | "No" | undefined>
  >({});

  const handleSelect = (fieldId: BooleanPromptFieldId, value: "Yes" | "No") => {
    setAnswers((prev) => ({
      ...prev,
      [fieldId]: value,
    }));
  };

  const handleSubmit = () => {
    const lines = fields
      .map((field) => {
        const value = answers[field.id];
        if (!value) return null;
        return `${field.label}: ${value}`;
      })
      .filter(Boolean);

    if (lines.length !== fields.length) return;

    onSendMessage(lines.join("\n\n"));
    setAnswers({});
  };

  const isComplete = fields.every((field) => answers[field.id]);

  return (
    <Box className={styles.booleanComposer}>
      {fields.map((field) => (
        <Box key={field.id} className={styles.booleanComposerRow}>
          <Typography className={styles.booleanComposerLabel}>
            {normalizeNdaFieldLabel(field.label)}
          </Typography>
          <Stack direction="row" gap={1} flexWrap="wrap">
            {(["Yes", "No"] as const).map((option) => (
              <Button
                key={`${field.id}-${option}`}
                size="small"
                variant={
                  answers[field.id] === option ? "contained" : "outlined"
                }
                onClick={() => handleSelect(field.id, option)}
                sx={{
                  textTransform: "none",
                  minWidth: 72,
                  borderColor: "#b2e5de",
                  color: answers[field.id] === option ? "#fff" : "#004141",
                  backgroundColor:
                    answers[field.id] === option ? "#00a888" : "#fff",
                  "&:hover": {
                    borderColor: "#00a888",
                    backgroundColor:
                      answers[field.id] === option ? "#00947a" : "#f0fbf9",
                  },
                }}
              >
                {option}
              </Button>
            ))}
          </Stack>
        </Box>
      ))}

      <Box className={styles.booleanComposerFooter}>
        <Button
          variant="contained"
          size="small"
          disabled={!isComplete}
          onClick={handleSubmit}
          sx={{
            textTransform: "none",
            backgroundColor: "#00a888",
            "&:hover": {
              backgroundColor: "#00947a",
            },
          }}
        >
          Continue
        </Button>
      </Box>
    </Box>
  );
}

function CompanyTypeQuickActions({
  onSendMessage,
}: {
  onSendMessage: (msg: string) => void;
}) {
  return (
    <Box className={styles.guidedFieldComposer}>
      <Stack
        direction="row"
        gap={1}
        flexWrap="wrap"
        className={styles.guidedFieldOptions}
      >
        {COMPANY_TYPE_OPTIONS.map((option) => (
          <Button
            key={option}
            size="small"
            variant="outlined"
            onClick={() => onSendMessage(option)}
            sx={{
              textTransform: "none",
              minWidth: 72,
              borderColor: "#b2e5de",
              color: "#004141",
              backgroundColor: "#fff",
              "&:hover": {
                borderColor: "#00a888",
                backgroundColor: "#f0fbf9",
              },
            }}
          >
            {option}
          </Button>
        ))}
      </Stack>
    </Box>
  );
}
// Replace "type **'keyword'**" / "type 'keyword'" with "click the **Keyword** button"
// so users know they can click the button instead of typing. Applied to display text only —
// raw question is still used for button detection in getConversationActions.
function sanitizeQuestionText(text: string): string {
  if (!text) return text;
  const capitalize = (s: string) =>
    s.trim().replace(/\b\w/g, (c) => c.toUpperCase());
  return text
    .replace(/\btype\s+\*\*'([^'*]+)'\*\*/gi, (m, kw) => {
      const startsUpper =
        m.charAt(0) === m.charAt(0).toUpperCase() && /[A-Z]/.test(m.charAt(0));
      return `${startsUpper ? "C" : "c"}lick the **${capitalize(kw)}** button`;
    })
    .replace(/\btype\s+'([^']+)'/gi, (m, kw) => {
      const startsUpper =
        m.charAt(0) === m.charAt(0).toUpperCase() && /[A-Z]/.test(m.charAt(0));
      return `${startsUpper ? "C" : "c"}lick the **${capitalize(kw)}** button`;
    })
    .replace(
      /click\s+the\s+\*\*Draft Email\*\*\s+button/gi,
      "click the **Prepare Legal Email** button",
    );
}

function getConversationActions(
  question?: string,
  response?: string | null,
): ConversationAction[] {
  const questionText = (question || "").toLowerCase();
  const responseText = (response || "").toLowerCase();

  if (
    questionText.includes("type **'confirm'** to proceed, or tell me to use") ||
    questionText.includes("click the **confirm** button to proceed") ||
    questionText.includes("or describe any corrections needed") ||
    responseText.includes("is recommended because")
  ) {
    return [{ label: "Confirm", message: "confirm", variant: "contained" }];
  }

  if (questionText.includes("please confirm the above details are correct")) {
    return [{ label: "Confirm", message: "confirm", variant: "contained" }];
  }

  if (
    questionText.includes(
      "please confirm these details before i create the final nda draft",
    ) ||
    questionText.includes(
      "type **'confirm'** to continue, or send the corrections you want me to make",
    )
  ) {
    return [{ label: "Confirm", message: "confirm", variant: "contained" }];
  }

  // BUG-21: Confidentiality-resolution prompt — instructs user to switch NDA type
  // or reply "proceed". Surface a Proceed quick-action so users don't have to type.
  if (
    questionText.includes("switch") &&
    (questionText.includes("'proceed'") || questionText.includes("proceed")) &&
    (questionText.includes("nda type") || questionText.includes("template"))
  ) {
    return [{ label: "Proceed", message: "proceed", variant: "contained" }];
  }

  if (questionText.includes("purpose appears broad")) {
    return [
      {
        label: "Proceed Anyway",
        message: "proceed anyway",
        variant: "contained",
      },
    ];
  }
  if (
    questionText.includes(
      "type **'confirm'** to proceed with this counterparty",
    )
  ) {
    return [
      { label: "Confirm", message: "confirm", variant: "contained" },
      { label: "Manual Review", message: "manual" },
    ];
  }

  if (
    questionText.includes("no existing nda") &&
    questionText.includes("existing nda")
  ) {
    return [
      {
        label: "No Existing NDA",
        message: "no existing nda",
        variant: "contained",
      },
      { label: "Existing NDA", message: "existing nda" },
    ];
  }

  if (
    questionText.includes(
      "shall i proceed to select the appropriate nda template",
    )
  ) {
    return [
      { label: "Yes", message: "yes", variant: "contained" },
      { label: "No", message: "no" },
    ];
  }

  if (
    questionText.includes(
      "shall i proceed with this template to generate the nda draft",
    ) ||
    questionText.includes("shall i proceed with the recommended template") ||
    questionText.includes("review the selected template and choose how to proceed")
  ) {
    return [];
  }

  if (
    questionText.includes("which template would you like to use instead") ||
    questionText.includes("type the template name, or")
  ) {
    return [{ label: "Use Recommended", message: "yes", variant: "outlined" }];
  }

  if (
    questionText.includes("your nda draft is ready for download") ||
    questionText.includes("download and review") ||
    questionText.includes("download the draft")
  ) {
    return [];
  }

  if (
    questionText.includes(
      "click **'draft email'** to prepare the legal submission email",
    ) ||
    questionText.includes(
      "click **'prepare legal email'** to prepare the legal submission package",
    )
  ) {
    return [
      {
        label: "Prepare Legal Email",
        message: "draft email",
        variant: "contained",
      },
    ];
  }

  if (questionText.includes("type **'skip'** to continue without sending")) {
    return [{ label: "Skip", message: "skip" }];
  }

  if (
    (questionText.includes("type **'review again'**") &&
      questionText.includes("type **'proceed'**")) ||
    (questionText.includes("keep the draft as-is") &&
      questionText.includes("rerun the compliance checklist"))
  ) {
    return [
      { label: "Review Again", message: "review again" },
      { label: "Proceed", message: "proceed", variant: "contained" },
    ];
  }

  if (
    questionText.includes("type **'proceed'** to continue to the next step") ||
    questionText.includes(
      "type **'proceed'** to continue with the current draft",
    ) ||
    questionText.includes("type **'proceed'** to keep the current draft") ||
    questionText.includes(
      "please send the corrected values again, or type **'proceed'** to keep the current draft",
    ) ||
    questionText.includes(
      "if you want to keep the draft exactly as it is, type **'proceed'**",
    ) ||
    questionText.includes(
      "if you want to keep the draft exactly as it is, type 'proceed'",
    ) ||
    responseText.includes(
      "if you want to keep the draft exactly as it is, type 'proceed'",
    ) ||
    responseText.includes(
      "if you want to keep the draft exactly as it is, type **'proceed'**",
    )
  ) {
    return [{ label: "Proceed", message: "proceed", variant: "contained" }];
  }

  if (
    questionText.includes("download and review") ||
    questionText.includes("download the draft")
  ) {
    return [];
  }

  if (questionText.includes("would you like to restart the drafting step")) {
    return [
      { label: "Restart Drafting", message: "yes", variant: "contained" },
    ];
  }

  if (questionText.includes("once verification is complete")) {
    return [
      { label: "Proceed", message: "proceed", variant: "contained" },
      { label: "Cancel", message: "cancel" },
    ];
  }

  if (
    questionText.includes("to begin a new nda request") ||
    questionText.includes("type **'restart'**")
  ) {
    return [
      { label: "New Request", message: "restart", variant: "contained" },
      { label: "Cancel", message: "cancel" },
    ];
  }

  return [];
}
function ConversationView({
  data,
  messageId,
}: {
  data: any;
  messageId?: string;
}) {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents.currentAgent,
  );
  const currentChat = useSelector((state: RootState) => state.user.currentChat);
  const responseItems = getNdaResponseItems(data);

  // NDA responses may arrive under data.response or finalAnswer.response.
  // First turn: ["Welcome greeting string"], subsequent turns: [{agent_id, result}, ...]
  // Extract plain string greeting if present
  const greeting = Array.isArray(responseItems)
    ? (responseItems.find((r: any) => typeof r === "string") ?? null)
    : typeof data.response === "string"
      ? data.response
      : typeof data.finalAnswer?.response === "string"
        ? data.finalAnswer.response
      : null;

  // Structured SSE contracts (REG-01/02) take precedence over prose/question parsing
  const templateSelection = extractTemplateSelection(data);
  const downloadDraftAction = extractDownloadDraftAction(data);
  // FE-4 C1: per-step template selector (persistent chips)
  const templateSelector = extractTemplateSelector(data as Record<string, unknown>);
  // FE-5 C3: NL party-edit feedback hint
  const editHelp = extractEditHelp(data as Record<string, unknown>);
  const templateResult = templateSelection
    ? {
        agent_id: "nda_template_selector_agent",
        result: templateSelection,
      }
    : findTemplateAgentResult(responseItems);
  const latestResult = responseItems.length > 0
    ? (() => {
        const objectItems = responseItems.filter(
          (r: any) => r && typeof r === "object" && r.result,
        );
        // N4-16: Prioritize by discriminator field (email > markdown > multiparty > last)
        // Email uses draft_type, markdown/multiparty use specific result fields
        const emailResult = objectItems.find((r: any) => r.result?.draft_type === "legal_submission_email");
        if (emailResult) return emailResult;
        const inputReviewResult = objectItems.find(
          (r: any) =>
            r.agent_id === "nda_input_review_agent" ||
            isInputReviewResult(r.result),
        );
        if (inputReviewResult) return inputReviewResult;
        const markdownResult = objectItems.find((r: any) => isMarkdownDraftPreviewResult(r.result));
        if (markdownResult) return markdownResult;
        const multipartyResult = objectItems.find((r: any) => r.agent_id === "nda_multiparty_form");
        if (multipartyResult) return multipartyResult;
        return objectItems.length > 0
          ? objectItems[objectItems.length - 1]
          : null;
      })()
    : null;

  const isDraftConfirm =
    typeof greeting === "string" && greeting.includes("Final NDA Draft Inputs");
  const latestAgentMessage = [...currentChat]
    .reverse()
    .find((message) => message.sender === "agent");
  const isLatestMessage = latestAgentMessage?.id === messageId;
  const builderNavigation = extractBuilderNavigation(data, currentChat);
  // N5-12: Render surfaces from the BE's typed surface_type discriminator, not question prose.
  // When surface_type is present it is authoritative; while the BE rollout is partial (null) we
  // fall back to the step / prose heuristics. Phase 1 covers intake_form + intake_review.
  const surfaceType: string | undefined =
    builderNavigation?.surface_type ?? undefined;
  const isIntakeFormSurface =
    surfaceType === "intake_form" ||
    (surfaceType == null &&
      (builderNavigation?.current_step === NDA_BUILDER_STEP_INTAKE_FORM ||
        isContextPromptQuestion(data.question)));
  // N5-11: present the Step-1 intake form (input boxes) for the whole intake step, not only when
  // the prose still lists all five sub-prompts (the narrowed prompt would drop the form otherwise).
  const showContextComposer =
    latestAgentMessage?.id === messageId && isIntakeFormSurface;
  const multipartyIntakePrefill =
    getLatestIntakePrefill(responseItems) ??
    getCounterpartyPrefillFromChat(currentChat);
  const latestMultipartyFormPrefill =
    getLatestMultipartyFormPrefill(currentChat);
  // N3-07: Check if backend sent nda_multiparty_form event (current message or chat history)
  const multipartyFormFromResponse = getMultipartyFormFromResponse(responseItems);
  const hasPartySlots = Boolean(multipartyFormFromResponse?.parties?.length);
  const multipartyFormState = multipartyFormFromResponse;
  const markdownDraftPreview = getMarkdownDraftPreviewFromResponse(responseItems);
  const booleanPromptFields = getBooleanPromptFields(data.question);
  const showBooleanComposer =
    latestAgentMessage?.id === messageId &&
    !showContextComposer &&
    booleanPromptFields.length > 0;
  // N3-07: Keep composer visible after submission so user can review populated fields
  // But NOT at Draft/Legal handoff steps (5, 6) where party details are finalized
  const partiesStepComplete = multipartyFormFromResponse?.partiesStepComplete === true;
  const step4ViewMode = multipartyFormFromResponse?.viewMode ?? null;
  const step4ValidationIssues = multipartyFormFromResponse?.validationIssues;
  const partyFieldCorrectionActive = hasPartyFieldCorrectionGate(data.question);
  const resolvedNdaVariant =
    getNdaVariantFromResponse(responseItems) ??
    multipartyFormFromResponse?.variant ??
    data.nda_type ??
    null;
  const isPartyDetailsStep = builderNavigation?.current_step === 4;
  const hasPartySlotData = resolveHasPartySlotData({
    currentStep: builderNavigation?.current_step,
    partiesStepComplete,
    hasPartySlots,
    isDownloadStep: isNdaDownloadStep(builderNavigation?.current_step),
    viewMode: step4ViewMode,
    validationIssues: step4ValidationIssues,
  });
  const hasPartySummaryData = resolveHasPartySummaryData({
    currentStep: builderNavigation?.current_step,
    partiesStepComplete,
    hasPartySlots,
    isDownloadStep: isNdaDownloadStep(builderNavigation?.current_step),
    viewMode: step4ViewMode,
    partyFieldCorrectionActive,
  });
  // Interactive controls still gated by isLatestMessage
  const showPartySlotStep4 =
    latestAgentMessage?.id === messageId &&
    !showContextComposer &&
    !showBooleanComposer &&
    hasPartySlotData;
  const showMultipartyPartiesConfirm =
    latestAgentMessage?.id === messageId &&
    hasPartySummaryData;
  // Legacy chat-prefill summary only when party-slot contract is absent
  const showMultipartyIntakeSummary =
    latestAgentMessage?.id === messageId &&
    Boolean(latestMultipartyFormPrefill) &&
    !hasPartySlots &&
    !showPartySlotStep4 &&
    !showMultipartyPartiesConfirm &&
    !showContextComposer &&
    !isNdaDownloadStep(builderNavigation?.current_step);
  const showCompanyTypeActions =
    latestAgentMessage?.id === messageId &&
    !showContextComposer &&
    !showBooleanComposer &&
    !showPartySlotStep4 &&
    !shouldSuppressStaleStep4Fallback({
      hasPartySlots,
      viewMode: step4ViewMode,
    }) &&
    isCompanyTypePrompt(data.question);
  const showsTemplateSelection =
    Boolean(templateSelection) || responseIncludesTemplateSelection(responseItems);
  const payloadDraftUrl = extractHitlDraftDownloadUrl(data);
  const draftDownloadUrl =
    payloadDraftUrl ||
    getLatestDraftDownloadUrl(responseItems, currentChat) ||
    extractUrlFromMarkdown(typeof greeting === "string" ? greeting : null);
  const templateProceedQuestion =
    Boolean(templateSelection) || isTemplateProceedQuestion(data.question);
  const quickActions = templateSelection
    ? []
    : getConversationActions(data.question, greeting);
  const numericAgentId = currentAgent?.id || 11;
  const structuredLatestResult =
    latestResult?.result && isStructuredNdaAgentResult(latestResult.result)
      ? latestResult
      : null;
  // N4-19: Extract email draft separately to ensure it always renders
  const emailDraftResult = responseItems.find(
    (r: any) => r && typeof r === "object" && r.result?.draft_type === "legal_submission_email"
  );
  const inputReviewResult = findInputReviewAgentResult(responseItems);
  const intakeAgentResult = findIntakeAgentResult(responseItems);
  // N5-12: prefer the BE's surface_type; fall back to the step heuristic during partial rollout.
  const isIntakeSummaryStep =
    surfaceType === "intake_review" ||
    (surfaceType == null &&
      builderNavigation?.current_step === NDA_BUILDER_STEP_INTAKE_SUMMARY);
  const inputReviewPanelResult =
    inputReviewResult ||
    (isIntakeSummaryStep && intakeAgentResult ? intakeAgentResult : null);
  const suppressStaleIntakeForStep4 = shouldSuppressStaleStep4Fallback({
    hasPartySlots,
    viewMode: step4ViewMode,
  });
  const showLatestStructuredResult =
    structuredLatestResult &&
    !isMultipartyFormResult(structuredLatestResult.result) &&
    !isMarkdownDraftPreviewResult(structuredLatestResult.result) &&
    !(
      markdownDraftPreview &&
      structuredLatestResult.result?.full_draft !== undefined
    ) &&
    !(
      isNdaDownloadStep(builderNavigation?.current_step) &&
      structuredLatestResult.result?.full_draft !== undefined &&
      downloadDraftAction?.available &&
      downloadDraftAction?.draft_download_url
    ) &&
    structuredLatestResult !== templateResult &&
    !(
      templateSelection &&
        structuredLatestResult.result?.recommended_template !== undefined
    ) &&
    !(
      suppressStaleIntakeForStep4 &&
      structuredLatestResult.result?.is_complete !== undefined
    ) &&
    structuredLatestResult !== inputReviewPanelResult;
  const showStructuredUi = Boolean(
    templateResult ||
    downloadDraftAction ||
    showLatestStructuredResult ||
    inputReviewPanelResult,
  );
  const showGreetingMarkdown = Boolean(
    greeting &&
    !isDraftConfirm &&
    !showStructuredUi &&
    !shouldSuppressStep4GreetingMarkdown({
      hasPartySlotData,
      isLatestMessage,
    }),
  );
  // C5: approval gate removed — download shown as soon as draft is present
  const showDownloadDraftAction = Boolean(
    downloadDraftAction?.available &&
    downloadDraftAction?.draft_download_url &&
    isNdaDownloadStep(builderNavigation?.current_step),
  );
  const showStep5ApprovedPanel =
    isLatestMessage &&
    builderNavigation?.current_step === NDA_BUILDER_STEP_DRAFT_READY &&
    showDownloadDraftAction;

  const handleSendMessage = (msg: string) => {
    dispatch(
      sendMessageToAgent({
        agentId: currentAgent?.id || NDA_AGENT_ID,
        message: msg,
        questionId: uuid(),
        timestamp: new Date().toISOString(),
        agentEndpoint: NDA_ENDPOINT,
        sessionId: null,
      }),
    );
  };

  const handleLinkClick = (url: string) => {
    triggerNdaFileDownload(url, dispatch, numericAgentId, "markdown_link");
  };

  const handleDownloadDraft = () => {
    const url =
      draftDownloadUrl ||
      extractUrlFromMarkdown(typeof greeting === "string" ? greeting : null);
    if (!url) return;
    triggerNdaFileDownload(
      url,
      dispatch,
      numericAgentId,
      "nda_draft",
      downloadDraftAction?.filename || "nda_draft.docx",
    );
  };

  const handleTemplateStepClick = (stepLabel: string) => {
    if (stepLabel.toLowerCase() === "template") {
      handleSendMessage("I want to change the template type");
    }
  };

  // FE-1: On a QnA turn the backend re-emits the whole accumulated builder obj_store
  // (input-review, party-form, email, ...), which the generic render would surface as
  // stale "Party Summary"/"Confirm and Continue" controls. Render ONLY the answer,
  // but also surface data.question so the pending step prompt is not lost (BUG-20).
  const qaAnswer = selectQaAnswerForTurn(data);
  if (qaAnswer !== null) {
    return (
      <Box className={styles.conversationContainer}>
        {builderNavigation && isLatestMessage && (
          <BuilderStepper
            navigation={builderNavigation}
            onStepClick={handleTemplateStepClick}
          />
        )}
        <Box className={styles.container}>
          <MarkdownRenderer content={qaAnswer} />
        </Box>
        {data.question && (
          <Box
            className={
              isLatestMessage
                ? styles.questionBox
                : `${styles.questionBox} ${styles.questionBoxAnswered}`
            }
          >
            <Box className={styles.questionIcon}>
              <InfoOutlinedIcon sx={{ color: "#6b7280", fontSize: 18 }} />
            </Box>
            <Box className={styles.questionContent}>
              <MarkdownRenderer
                content={sanitizeQuestionText(data.question)}
                onLinkClick={handleLinkClick}
              />
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  return (
    <Box className={styles.conversationContainer}>
      {builderNavigation && isLatestMessage && (
        <BuilderStepper
          navigation={builderNavigation}
          onStepClick={handleTemplateStepClick}
        />
      )}
      {/* C6/C7: Template switcher shown only at step 4 (post-template-selection,
          pre-draft). Hidden at steps 1-3 (too early) and steps 5-6 (at/after draft). */}
      {templateSelector &&
        isLatestMessage &&
        builderNavigation?.current_step === NDA_BUILDER_STEP_PARTY_COLLECTION && (
        <Box sx={{ mb: 1.5 }}>
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 600,
              color: "#004141",
              textTransform: "uppercase",
              letterSpacing: "0.5px",
              mb: 0.75,
            }}
          >
            NDA type — click to switch
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1}>
            {templateSelector.options.map((option) => {
              const isSelected =
                option.variant.toLowerCase() ===
                templateSelector.current_variant.toLowerCase();
              return (
                <Chip
                  key={option.variant}
                  label={option.display_label || option.variant}
                  size="small"
                  onClick={
                    templateSelector.switchable
                      ? () => handleSendMessage(option.variant)
                      : undefined
                  }
                  sx={{
                    backgroundColor: isSelected ? "#e6f2f2" : "#f9fafb",
                    color: isSelected ? "#004141" : "#2a312b",
                    border: isSelected
                      ? "1px solid #004141"
                      : "1px solid #e5e7eb",
                    fontWeight: isSelected ? 600 : 400,
                    cursor: templateSelector.switchable ? "pointer" : "default",
                    "&:hover": templateSelector.switchable
                      ? {
                          backgroundColor: "#e6f2f2",
                          borderColor: "#004141",
                          color: "#004141",
                        }
                      : {},
                  }}
                />
              );
            })}
          </Stack>
        </Box>
      )}
      {/* FE-5 C3: NL party-edit feedback — hint + template chip */}
      {editHelp && (
        <Box
          sx={{
            mb: 1.5,
            p: 1.5,
            backgroundColor: editHelp.ambiguous ? "#fff7e6" : "#f0fbf9",
            border: `1px solid ${editHelp.ambiguous ? "#fbbf24" : "#b2e5de"}`,
            borderRadius: "8px",
          }}
        >
          <Typography sx={{ fontSize: "13px", color: "#374151", lineHeight: 1.5 }}>
            {editHelp.hint}
          </Typography>
          {editHelp.variant && (
            <Chip
              label={editHelp.variant}
              size="small"
              sx={{
                mt: 1,
                backgroundColor: "#e6f2f2",
                color: "#004141",
                border: "1px solid #004141",
                fontWeight: 600,
              }}
            />
          )}
        </Box>
      )}
      {isDraftConfirm && greeting && (
        <DraftConfirmView text={greeting} onSendMessage={handleSendMessage} />
      )}
      {showGreetingMarkdown && (
        <Box className={styles.responseText}>
          <MarkdownRenderer content={sanitizeQuestionText(greeting)} onLinkClick={handleLinkClick} />
        </Box>
      )}
      {/* Standalone download card is for Step 5 only. At Step 6 (Legal handoff) the
          email draft card already provides Download Draft + Open Template Redline, so the
          centered button here would be redundant. */}
      {showDownloadDraftAction &&
        downloadDraftAction &&
        !showStep5ApprovedPanel &&
        isLatestMessage &&
        builderNavigation?.current_step !== NDA_BUILDER_STEP_LEGAL_HANDOFF && (
          <DownloadDraftActionCard
            download={downloadDraftAction}
            onDownload={handleDownloadDraft}
          />
        )}
      {templateResult &&
        renderAgentResult(templateResult, {
          messageId,
          builderNavigation,
          isLatestMessage,
          onSendMessage: handleSendMessage,
        })}
      {showLatestStructuredResult &&
        renderAgentResult(showLatestStructuredResult, {
          messageId,
          builderNavigation,
          isLatestMessage,
          onSendMessage: handleSendMessage,
        })}
      {/* N5-01: Always render AI input review at Step 2 (do not rely on latestResult alone). */}
      {/* N5-10: Suppress the review panel when the Step-1 intake composer is showing — both
          render the same fields, so they would otherwise appear twice on a fresh intake. */}
      {inputReviewPanelResult &&
        !showContextComposer &&
        inputReviewPanelResult !== showLatestStructuredResult &&
        renderAgentResult(inputReviewPanelResult, {
          messageId,
          builderNavigation,
          isLatestMessage,
          onSendMessage: handleSendMessage,
        })}
      {/* N4-19: Render email draft separately to ensure it always shows */}
      {emailDraftResult && emailDraftResult !== showLatestStructuredResult &&
        renderAgentResult(emailDraftResult, {
          messageId,
          builderNavigation,
          isLatestMessage,
          onSendMessage: handleSendMessage,
        })}
      {/* Intake composer renders as its own full card — no questionBox nesting */}
      {showContextComposer && (
        <ContextPromptComposer
          onSendMessage={handleSendMessage}
          intakePrefill={multipartyIntakePrefill}
        />
      )}
      {markdownDraftPreview && (
        <MarkdownDraftPreviewView
          data={markdownDraftPreview}
          isLatestMessage={isLatestMessage}
          onSendMessage={handleSendMessage}
          compactAfterApproval={false}
          onDownloadDraft={
            showStep5ApprovedPanel ? handleDownloadDraft : undefined
          }
          showApprovedFooter={showStep5ApprovedPanel && isLatestMessage}
        />
      )}
      {/* N4-15: Render party slot view when data exists (not only latest message) */}
      {hasPartySlotData && multipartyFormFromResponse?.parties && (
        <PartySlotStep4View
          parties={multipartyFormFromResponse.parties}
          variant={
            multipartyFormFromResponse.variant ?? resolvedNdaVariant ?? data.nda_type
          }
          validationIssues={multipartyFormFromResponse.validationIssues}
          onSendMessage={isLatestMessage ? handleSendMessage : undefined}
        />
      )}
      {/* N4-15: Render party summary when data exists (not only latest message) */}
      {hasPartySummaryData &&
        multipartyFormFromResponse?.parties &&
        (() => {
        const partyGroups = buildPartyGroupsFromSlots(
          multipartyFormFromResponse.parties,
          multipartyFormFromResponse.variant ?? data.nda_type,
        );

        const validationLookup = buildValidationIssueLookup(
          multipartyFormFromResponse.validationIssues,
        );
        const validationIssues = validationLookup.issues;
        const warningFieldNames = validationLookup.warningFieldNames;

        return (
          <>
            <Box className={styles.container}>
              <Box className={styles.sectionHeader}>
                <InfoOutlinedIcon sx={{ color: "#004141", fontSize: 22, mr: 1 }} />
                <Typography className={styles.sectionTitle}>
                  {normalizeNdaVariantKind(multipartyFormFromResponse.variant) === "mutual"
                    ? "Mutual NDA Party Summary"
                    : normalizeNdaVariantKind(multipartyFormFromResponse.variant) === "multiparty"
                      ? "Multiparty NDA Party Summary"
                      : "Party Summary"}
                </Typography>
              </Box>
              {/* N4-11: Render validation issues - separate blocking errors from warnings */}
              {(() => {
                const blockingIssues = validationIssues.filter(isDraftBlockingIssue);
                const warningIssues = validationIssues.filter(i => i.severity === "warning");
                const infoIssues = validationIssues.filter(i => i.severity === "info");

                return (
                  <>
                    {blockingIssues.length > 0 && (
                      <Box
                        sx={{
                          mb: 2,
                          p: 2,
                          backgroundColor: "#fff3e0",
                          border: "2px solid #ff9800",
                          borderRadius: "8px",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                          <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#e65100" }}>
                            ⚠️ Action Required
                          </Typography>
                        </Box>
                        {blockingIssues.map((issue, idx) => (
                          <Box key={idx} sx={{ mt: idx > 0 ? 1.5 : 1 }}>
                            <Typography sx={{ fontSize: "14px", color: "#5d4037", lineHeight: 1.6 }}>
                              {issue.message}
                            </Typography>
                            {issue.party_indices.length > 0 && (
                              <Typography
                                sx={{
                                  fontSize: "12px",
                                  color: "#6d4c41",
                                  mt: 0.5,
                                  fontStyle: "italic",
                                }}
                              >
                                Please correct the highlighted fields below to continue.
                              </Typography>
                            )}
                          </Box>
                        ))}
                      </Box>
                    )}

                    {warningIssues.length > 0 && (
                      <Box
                        sx={{
                          mb: 2,
                          p: 2,
                          backgroundColor: "#fffbf0",
                          border: "1px solid #fbbf24",
                          borderRadius: "8px",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                          <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#92400e" }}>
                            ⚠️ Please Review
                          </Typography>
                        </Box>
                        {warningIssues.map((issue, idx) => (
                          <Box key={idx} sx={{ mt: idx > 0 ? 1 : 0.5 }}>
                            <Typography sx={{ fontSize: "13px", color: "#78350f", lineHeight: 1.5 }}>
                              {issue.message}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}

                    {infoIssues.length > 0 && (
                      <Box
                        sx={{
                          mb: 2,
                          p: 2,
                          backgroundColor: "#eff6ff",
                          border: "1px solid #3b82f6",
                          borderRadius: "8px",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                          <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1e40af" }}>
                            ℹ️ For Your Information
                          </Typography>
                        </Box>
                        {infoIssues.map((issue, idx) => (
                          <Box key={idx} sx={{ mt: idx > 0 ? 1 : 0.5 }}>
                            <Typography sx={{ fontSize: "13px", color: "#1e3a8a", lineHeight: 1.5 }}>
                              {issue.message}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </>
                );
              })()}
              <GroupedPartyApprovalView
                groups={partyGroups}
                warningFieldNames={warningFieldNames}
                onSendMessage={isLatestMessage ? handleSendMessage : undefined}
                collateEdits
              />
            </Box>
            {isLatestMessage && (() => {
              const hasBlockingIssues = hasDraftBlockingIssues(validationIssues);
              return (
                <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
                  <button
                    className={styles.submitButton}
                    onClick={() =>
                      handleSendMessage("Confirmed. Please proceed to drafting.")
                    }
                    disabled={hasBlockingIssues}
                    style={{
                      padding: "10px 24px",
                      backgroundColor: hasBlockingIssues ? "#cccccc" : "#00a888",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      cursor: hasBlockingIssues ? "not-allowed" : "pointer",
                      fontWeight: 600,
                      opacity: hasBlockingIssues ? 0.6 : 1,
                    }}
                  >
                    Continue →
                  </button>
                </Box>
              );
            })()}
          </>
        );
      })()}
      {showMultipartyIntakeSummary && latestMultipartyFormPrefill && (() => {
        // N3-07: Merge backend prefill (Party A from intake) with user submission (Party B/C)
        // Backend sends snake_case (company_a_name), frontend expects camelCase (companyAName)
        const backendPrefill = multipartyFormState?.prefill_values || multipartyFormState?.prefillValues || {};
        const convertedBackendValues: Partial<MultipartyFormValues> = {
          companyAName: backendPrefill.company_a_name || "",
          companyAType: backendPrefill.company_a_type || "",
          companyAJurisdiction: backendPrefill.company_a_jurisdiction || "",
          companyARegistrationNumber: backendPrefill.company_a_registration_number || "",
          companyAAddress: backendPrefill.company_a_address || "",
          companyAKyc: backendPrefill.company_a_kyc || "",
          companyBName: backendPrefill.company_b_name || "",
          companyBType: backendPrefill.company_b_type || "",
          companyBJurisdiction: backendPrefill.company_b_jurisdiction || "",
          companyBRegistrationNumber: backendPrefill.company_b_registration_number || "",
          companyBAddress: backendPrefill.company_b_address || "",
          companyBKyc: backendPrefill.company_b_kyc || "",
          companyCName: backendPrefill.company_c_name || "",
          companyCType: backendPrefill.company_c_type || "",
          companyCJurisdiction: backendPrefill.company_c_jurisdiction || "",
          companyCRegistrationNumber: backendPrefill.company_c_registration_number || "",
          companyCAddress: backendPrefill.company_c_address || "",
          companyCKyc: backendPrefill.company_c_kyc || "",
        };

        const mergedValues = {
          // Company A from backend (intake data, converted to camelCase)
          ...convertedBackendValues,
          // Company B/C from user submission (overrides if present)
          ...latestMultipartyFormPrefill,
        };

        return (
          <>
            <MultipartyIntakeSummaryView
              values={mergedValues}
              variant={multipartyFormState?.variant ?? data.nda_type}
            />
            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <button
                className={styles.submitButton}
                onClick={() => handleSendMessage("Confirmed. Please proceed to drafting.")}
                style={{
                  padding: "10px 24px",
                  backgroundColor: "#00a888",
                  color: "white",
                  border: "none",
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontWeight: 600,
                }}
              >
                Continue →
              </button>
            </Box>
          </>
        );
      })()}

      {/* Normal question box for all other question types */}
      {data.question &&
        !showContextComposer &&
        !showPartySlotStep4 &&
        !showMultipartyPartiesConfirm &&
        !hasPartySlotData &&
        !showStep5ApprovedPanel &&
        !templateSelection &&
        !(isIntakeSummaryStep && inputReviewPanelResult && isLatestMessage) && (
          <Box
            className={
              isLatestMessage
                ? styles.questionBox
                : `${styles.questionBox} ${styles.questionBoxAnswered}`
            }
          >
            <Box className={styles.questionIcon}>
              <InfoOutlinedIcon sx={{ color: "#6b7280", fontSize: 18 }} />
            </Box>
            <Box className={styles.questionContent}>
              <MarkdownRenderer content={sanitizeQuestionText(data.question)} onLinkClick={handleLinkClick} />
              {showBooleanComposer && (
                <BooleanPromptComposer
                  fields={booleanPromptFields}
                  onSendMessage={handleSendMessage}
                />
              )}
              {showCompanyTypeActions && (
                <CompanyTypeQuickActions onSendMessage={handleSendMessage} />
              )}
              {quickActions.length > 0 ? (
                <Stack direction="row" flexWrap="wrap" gap={1} mt={2}>
                  {quickActions.map((action) => (
                    <Button
                      key={`${action.label}-${action.message || action.downloadUrl || "action"}`}
                      size="small"
                      variant={action.variant || "outlined"}
                      onClick={() => {
                        if (action.downloadUrl) {
                          triggerNdaFileDownload(
                            action.downloadUrl,
                            dispatch,
                            numericAgentId,
                            "nda_draft",
                          );
                          return;
                        }
                        if (action.message) {
                          handleSendMessage(action.message);
                        }
                      }}
                      sx={{
                        textTransform: "none",
                        borderColor: "#b2e5de",
                        color:
                          action.variant === "contained" ? "#fff" : "#004141",
                        backgroundColor:
                          action.variant === "contained" ? "#00a888" : "#fff",
                        "&:hover": {
                          borderColor: "#00a888",
                          backgroundColor:
                            action.variant === "contained"
                              ? "#00947a"
                              : "#f0fbf9",
                        },
                      }}
                    >
                      {action.label}
                    </Button>
                  ))}
                </Stack>
              ) : null}
            </Box>
          </Box>
        )}
    </Box>
  );
}

function MarkdownDraftPreviewView({
  data,
  isLatestMessage,
  onSendMessage,
  compactAfterApproval = false,
  onDownloadDraft,
  showApprovedFooter = false,
}: {
  data: Record<string, unknown>;
  isLatestMessage?: boolean;
  onSendMessage?: (msg: string) => void;
  compactAfterApproval?: boolean;
  onDownloadDraft?: () => void;
  showApprovedFooter?: boolean;
}) {
  const markdown = typeof data.markdown === "string" ? data.markdown : "";
  const redlineUrl =
    typeof data.template_redline_download_url === "string"
      ? data.template_redline_download_url
      : "";
  // C5: draft shown directly, no approval gate
  const approved = true;
  const [editMode, setEditMode] = useState(false);
  const [editText, setEditText] = useState("");

  const actions = Array.isArray(data.actions) ? data.actions : [];

  return (
    <Box className={styles.container}>
      <Box
        className={styles.sectionHeader}
        sx={{
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 1,
          mb: 0,
        }}
      >
        <DescriptionIcon sx={{ color: "#004141", fontSize: 22 }} />
        <Typography className={styles.sectionTitle} sx={{ flex: 1, minWidth: 0 }}>
          NDA Draft Preview
        </Typography>
      </Box>

      {typeof data.summary === "string" && data.summary && (
        <Box className={styles.summaryBox}>
          <MarkdownRenderer content={data.summary} />
        </Box>
      )}

      {!compactAfterApproval && (
        <Box
          className={styles.responseText}
          sx={{
            maxHeight: 480,
            overflow: "auto",
            p: 2,
            borderRadius: "8px",
            border: "1px solid #d0e8e4",
            backgroundColor: "#fafafa",
          }}
        >
          <Typography
            component="pre"
            sx={{
              m: 0,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: '"SF Mono", "Roboto Mono", Menlo, monospace',
              fontSize: "12px",
              lineHeight: 1.55,
              color: "#1f2937",
            }}
          >
            {sanitizeDraftForDisplay(markdown)}
          </Typography>
        </Box>
      )}

      {isLatestMessage && onSendMessage && !approved && (
        <Stack direction="row" flexWrap="wrap" gap={1} mt={2}>
          {actions.map((action: { label?: string; message?: string; variant?: string }) => (
            <Button
              key={action.label || action.message}
              size="small"
              variant={action.variant === "contained" ? "contained" : "outlined"}
              onClick={() => {
                if (action.message?.startsWith("request markdown edits")) {
                  setEditMode(true);
                  return;
                }
                if (action.message) onSendMessage(action.message);
              }}
              sx={{ textTransform: "none" }}
            >
              {action.label || action.message}
            </Button>
          ))}
          <Button
            size="small"
            variant="text"
            onClick={() => onSendMessage("I want to change the template type")}
            sx={{ textTransform: "none", color: "#004141" }}
          >
            Change template
          </Button>
        </Stack>
      )}

      {editMode && onSendMessage && (
        <Box mt={2}>
          <Typography sx={{ fontSize: "13px", mb: 1, color: "#374151" }}>
            Describe the edits you want applied to the markdown draft:
          </Typography>
          <textarea
            value={editText}
            onChange={(e) => setEditText(e.target.value)}
            rows={4}
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "8px",
              border: "1px solid #b2e5de",
              fontFamily: "inherit",
            }}
          />
          <Button
            size="small"
            variant="contained"
            sx={{ mt: 1, textTransform: "none", backgroundColor: "#00a888" }}
            disabled={!editText.trim()}
            onClick={() => onSendMessage(`request markdown edits: ${editText.trim()}`)}
          >
            Submit edits
          </Button>
        </Box>
      )}

      {approved &&
        data.docx_available === true &&
        !showApprovedFooter && (
          <Typography sx={{ fontSize: "13px", color: "#004141", mt: 2 }}>
            DOCX download is available below.
          </Typography>
        )}

      {showApprovedFooter && isLatestMessage && onSendMessage && (
        <Box
          sx={{
            mt: 2,
            pt: 2,
            borderTop: "1px solid #d0e8e4",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "stretch", sm: "center" },
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <Typography sx={{ fontSize: "13px", color: "#6b7280", lineHeight: 1.5 }}>
            Your DOCX draft is ready. Download a copy, then continue to legal
            handoff.
          </Typography>
          <Stack
            direction="row"
            gap={1}
            sx={{
              flexShrink: 0,
              justifyContent: { xs: "flex-end", sm: "flex-start" },
            }}
          >
            {onDownloadDraft && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<DescriptionIcon />}
                onClick={onDownloadDraft}
                sx={{
                  textTransform: "none",
                  borderColor: "#b2e5de",
                  color: "#004141",
                  "&:hover": { borderColor: "#00a888", backgroundColor: "#f0fbf9" },
                }}
              >
                Download Draft
              </Button>
            )}
            {redlineUrl && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<DescriptionIcon />}
                onClick={() => window.open(redlineUrl, "_blank", "noopener")}
                sx={{
                  textTransform: "none",
                  borderColor: "#b2e5de",
                  color: "#004141",
                  "&:hover": { borderColor: "#00a888", backgroundColor: "#f0fbf9" },
                }}
              >
                Open Template Redline
              </Button>
            )}
            <Button
              size="small"
              variant="contained"
              onClick={() => onSendMessage("proceed")}
              sx={{
                textTransform: "none",
                backgroundColor: "#00a888",
                fontWeight: 600,
                px: 3,
                "&:hover": { backgroundColor: "#00947a" },
              }}
            >
              Continue →
            </Button>
          </Stack>
        </Box>
      )}
    </Box>
  );
}

// NDA Draft View
function DraftView({ data, hideDownload = false }: { data: any; hideDownload?: boolean }) {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents.currentAgent,
  );
  const numericAgentId = currentAgent?.id || 11;
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const cleanedDraft = sanitizeDraftForDisplay(data.full_draft || "");
  const draftDownloadUrl = getDraftDownloadUrlFromResult(data);

  const handleCopy = () => {
    if (cleanedDraft) {
      navigator.clipboard.writeText(cleanedDraft);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownloadDraft = () => {
    if (!draftDownloadUrl) return;
    triggerNdaFileDownload(
      draftDownloadUrl,
      dispatch,
      numericAgentId,
      "nda_draft",
    );
  };

  const handleDownloadRedline = () => {
    if (!data.template_redline_download_url) return;
    triggerNdaFileDownload(
      data.template_redline_download_url,
      dispatch,
      numericAgentId,
      "template_redline",
    );
  };

  const handleLinkClick = (url: string) => {
    triggerNdaFileDownload(url, dispatch, numericAgentId, "markdown_link");
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.sectionHeader}>
        <DescriptionIcon sx={{ color: "#004141", fontSize: 22, mr: 1 }} />
        <Typography className={styles.sectionTitle}>NDA Draft Ready</Typography>
        <Chip
          label="Draft"
          size="small"
          sx={{
            backgroundColor: "#f0fbf9",
            color: "#004141",
            border: "1px solid #b2e5de",
            ml: 1,
          }}
        />
      </Box>

      {data.summary && (
        <Box className={styles.summaryBox}>
          <MarkdownRenderer content={data.summary} onLinkClick={handleLinkClick} />
        </Box>
      )}

      {draftDownloadUrl && !hideDownload && !data.markdown_approval_pending && (
        <Button
          size="small"
          variant="contained"
          startIcon={<DescriptionIcon />}
          onClick={handleDownloadDraft}
          sx={{
            alignSelf: "flex-start",
            textTransform: "none",
            backgroundColor: "#00a888",
            "&:hover": {
              backgroundColor: "#00947a",
            },
          }}
        >
          Download Draft
        </Button>
      )}

      <Box className={styles.detailsGrid}>
        {data.parties?.length > 0 && (
          <Box className={styles.detailItem}>
            <Typography className={styles.detailLabel}>Parties</Typography>
            <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
              {data.parties.map((p: string, i: number) => (
                <Chip
                  key={i}
                  label={p}
                  size="small"
                  sx={{
                    backgroundColor: "#f0fbf9",
                    color: "#004141",
                    border: "1px solid #b2e5de",
                  }}
                />
              ))}
            </Stack>
          </Box>
        )}
        {data.duration && (
          <Box className={styles.detailItem}>
            <Typography className={styles.detailLabel}>Duration</Typography>
            <Typography className={styles.detailValue}>
              {data.duration}
            </Typography>
          </Box>
        )}
        {data.governing_law && (
          <Box className={styles.detailItem}>
            <Typography className={styles.detailLabel}>
              Governing Law
            </Typography>
            <Typography className={styles.detailValue}>
              {data.governing_law}
            </Typography>
          </Box>
        )}
        {data.dispute_resolution && (
          <Box className={styles.detailItem}>
            <Typography className={styles.detailLabel}>
              Dispute Resolution
            </Typography>
            <Typography className={styles.detailValue}>
              {formatDisputeResolution(data.dispute_resolution)}
            </Typography>
          </Box>
        )}
      </Box>

      {data.key_clauses_preview?.length > 0 && (
        <Box className={styles.section}>
          <Typography className={styles.sectionLabel}>Key Clauses</Typography>
          {data.key_clauses_preview.map((clause: string, i: number) => (
            <Box key={i} className={styles.clausePreviewItem}>
              <Box className={styles.bulletDot} />
              <Typography className={styles.clausePreviewText}>
                {clause}
              </Typography>
            </Box>
          ))}
        </Box>
      )}

      {(data.template_redline_summary ||
        data.template_redline_download_url) && (
        <Box className={styles.section}>
          <Typography className={styles.sectionLabel}>
            Legal Review Outputs
          </Typography>
          {data.template_redline_summary && (
            <Box sx={{ mt: 1 }}>
              <MarkdownRenderer content={data.template_redline_summary} onLinkClick={handleLinkClick} />
            </Box>
          )}
          {data.template_redline_download_url && (
            <Button
              size="small"
              variant="text"
              startIcon={<DescriptionIcon />}
              onClick={handleDownloadRedline}
              sx={{
                marginTop: 1,
                alignSelf: "flex-start",
                textTransform: "none",
                color: "#00a888",
                paddingLeft: 0,
              }}
            >
              Open Template Redline
            </Button>
          )}
        </Box>
      )}

      {cleanedDraft && (
        <Box className={styles.section}>
          <Box className={styles.draftHeader}>
            <Typography className={styles.sectionLabel}>Full Draft</Typography>
            <Tooltip title={copied ? "Copied!" : "Copy draft"}>
              <IconButton onClick={handleCopy} size="small">
                <ContentCopyIcon
                  sx={{
                    fontSize: 16,
                    color: copied ? "#267103" : "#004141",
                  }}
                />
              </IconButton>
            </Tooltip>
            <Typography
              className={styles.toggleBtn}
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? "Collapse" : "Expand"}
            </Typography>
          </Box>
          {expanded && (
            <Box className={styles.fullDraftBox}>
              <pre className={styles.draftText}>{cleanedDraft}</pre>
            </Box>
          )}
          {!expanded && (
            <Box className={styles.draftPreview}>
              <Typography className={styles.draftPreviewText}>
                {cleanedDraft.substring(0, 300)}...
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

// NDA Review View
function ReviewView({ data }: { data: any }) {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents.currentAgent,
  );
  const numericAgentId = currentAgent?.id || 11;
  const [expandedClause, setExpandedClause] = useState<number | null>(null);

  const handleLinkClick = (url: string) => {
    dispatch(
      recordAgentAction({
        agentId: numericAgentId,
        actionType: "download",
        content: JSON.stringify({
          type: "markdown_link",
          url: url,
        }),
      }),
    );
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.sectionHeader}>
        <GavelIcon sx={{ color: "#004141", fontSize: 22, mr: 1 }} />
        <Typography className={styles.sectionTitle}>
          NDA Review Results
        </Typography>
        {data.overall_risk_level && (
          <RiskChip level={data.overall_risk_level} />
        )}
      </Box>

      {data.risk_summary && (
        <Box className={styles.summaryBox}>
          <MarkdownRenderer content={data.risk_summary} onLinkClick={handleLinkClick} />
        </Box>
      )}

      <Box className={styles.detailsGrid}>
        {data.nda_type && (
          <Box className={styles.detailItem}>
            <Typography className={styles.detailLabel}>NDA Type</Typography>
            <Typography className={styles.detailValue}>
              {data.nda_type}
            </Typography>
          </Box>
        )}
        {data.governing_law && (
          <Box className={styles.detailItem}>
            <Typography className={styles.detailLabel}>
              Governing Law
            </Typography>
            <Typography className={styles.detailValue}>
              {data.governing_law}
            </Typography>
          </Box>
        )}
        {data.dispute_resolution && (
          <Box className={styles.detailItem}>
            <Typography className={styles.detailLabel}>
              Dispute Resolution
            </Typography>
            <Typography className={styles.detailValue}>
              {formatDisputeResolution(data.dispute_resolution)}
            </Typography>
          </Box>
        )}
        {data.effective_date && (
          <Box className={styles.detailItem}>
            <Typography className={styles.detailLabel}>
              Effective Date
            </Typography>
            <Typography className={styles.detailValue}>
              {data.effective_date}
            </Typography>
          </Box>
        )}
        {data.expiry_date && (
          <Box className={styles.detailItem}>
            <Typography className={styles.detailLabel}>Expiry Date</Typography>
            <Typography className={styles.detailValue}>
              {data.expiry_date}
            </Typography>
          </Box>
        )}
        {data.confidentiality_period && (
          <Box className={styles.detailItem}>
            <Typography className={styles.detailLabel}>
              Confidentiality Period
            </Typography>
            <Typography className={styles.detailValue}>
              {data.confidentiality_period}
            </Typography>
          </Box>
        )}
      </Box>

      {data.parties?.length > 0 && (
        <Box className={styles.section}>
          <Typography className={styles.sectionLabel}>Parties</Typography>
          {data.parties.map((party: any, i: number) => (
            <Box key={i} className={styles.partyRow}>
              <Typography className={styles.partyName}>{party.name}</Typography>
              <Chip
                label={party.role}
                size="small"
                sx={{
                  backgroundColor: "#f0fbf9",
                  color: "#004141",
                  border: "1px solid #b2e5de",
                  fontSize: "11px",
                }}
              />
            </Box>
          ))}
        </Box>
      )}

      {data.clause_reviews?.length > 0 && (
        <Box className={styles.section}>
          <Typography className={styles.sectionLabel}>
            Clause Review ({data.clause_reviews.length})
          </Typography>
          {data.clause_reviews.map((clause: any, i: number) => (
            <Box key={i} className={styles.accordionItem}>
              <Box
                className={styles.accordionHeader}
                onClick={() =>
                  setExpandedClause(expandedClause === i ? null : i)
                }
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flex: 1,
                  }}
                >
                  <Typography className={styles.accordionTitle}>
                    {clause.clause_ref}
                  </Typography>
                  <Typography className={styles.clauseName}>
                    {clause.clause_name}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <RiskChip level={clause.risk_level} />
                  {expandedClause === i ? (
                    <ExpandLessIcon sx={{ fontSize: 18, color: "#004141" }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 18, color: "#004141" }} />
                  )}
                </Box>
              </Box>
              {expandedClause === i && (
                <Box className={styles.accordionBody}>
                  <Box className={styles.fieldRow}>
                    <Typography className={styles.fieldLabel}>
                      Finding
                    </Typography>
                    <Typography className={styles.fieldValue}>
                      {clause.finding}
                    </Typography>
                  </Box>
                  <Box className={styles.fieldRow}>
                    <Typography className={styles.fieldLabel}>
                      Recommendation
                    </Typography>
                    <Typography className={styles.fieldValue}>
                      {clause.recommendation}
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {data.redlines?.length > 0 && (
        <Box className={styles.section}>
          <Typography className={styles.sectionLabel}>
            Suggested Redlines ({data.redlines.length})
          </Typography>
          {data.redlines.map((redline: string, i: number) => (
            <Box key={i} className={styles.redlineItem}>
              <Box className={styles.redlineDot} />
              <Typography className={styles.redlineText}>{redline}</Typography>
            </Box>
          ))}
        </Box>
      )}

      {data.negotiation_priorities?.length > 0 && (
        <Box className={styles.section}>
          <Typography className={styles.sectionLabel}>
            Negotiation Priorities
          </Typography>
          {data.negotiation_priorities.map((priority: string, i: number) => (
            <Box key={i} className={styles.priorityItem}>
              <Typography className={styles.priorityNumber}>{i + 1}</Typography>
              <Typography className={styles.priorityText}>
                {priority}
              </Typography>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

// NDA Validation View
function ValidationView({
  data,
  messageId,
}: {
  data: any;
  messageId?: string;
}) {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents.currentAgent,
  );
  const numericAgentId = currentAgent?.id || 11;
  const currentChat = useSelector((state: RootState) => state.user.currentChat);
  const feedback = useSelector((state: RootState) =>
    messageId ? state.filters.ndaValidationFeedback[messageId] : undefined,
  );
  const accept_array: any[] = feedback?.accept_array ?? [];
  const reject_array: any[] = feedback?.reject_array ?? [];
  const modify_array: any[] = feedback?.modify_array ?? [];

  const [expandedDev, setExpandedDev] = useState<number | null>(null);
  const [modifyTexts, setModifyTexts] = useState<Record<string, string>>({});
  const [modifyingKey, setModifyingKey] = useState<string | null>(null);

  const handleLinkClick = (url: string) => {
    dispatch(
      recordAgentAction({
        agentId: numericAgentId,
        actionType: "download",
        content: JSON.stringify({
          type: "markdown_link",
          url: url,
        }),
      }),
    );
  };

  const isLatestMessage =
    [...currentChat].reverse().find((m) => m.sender === "agent")?.id ===
    messageId;

  const checklistItems = Array.isArray(data.checklist_items)
    ? data.checklist_items
    : [];
  const mustHaveItems = checklistItems.filter(
    (item: any) => (item.priority || "").toLowerCase() === "must_have",
  );
  const remainingChecklistItems = checklistItems.filter(
    (item: any) => (item.priority || "").toLowerCase() !== "must_have",
  );
  const compliantChecklistCount = checklistItems.filter(
    (item: any) => (item.status || "").toLowerCase() === "compliant",
  ).length;

  const getDecisionForDev = (devKey: string) => {
    if (accept_array.some((d) => d._key === devKey)) return "accepted";
    if (reject_array.some((d) => d._key === devKey)) return "rejected";
    if (modify_array.some((d) => d._key === devKey)) return "modified";
    return null;
  };

  const handleAccept = (dev: any, devKey: string) => {
    const current = getDecisionForDev(devKey);
    if (current === "accepted") {
      // undo
      dispatch(
        setNdaValidationFeedback({
          messageId: messageId!,
          feedback: {
            accept_array: accept_array.filter((d) => d._key !== devKey),
            reject_array,
            modify_array,
          },
        }),
      );
    } else {
      dispatch(
        setNdaValidationFeedback({
          messageId: messageId!,
          feedback: {
            accept_array: [
              ...accept_array.filter((d) => d._key !== devKey),
              { ...dev, _key: devKey },
            ],
            reject_array: reject_array.filter((d) => d._key !== devKey),
            modify_array: modify_array.filter((d) => d._key !== devKey),
          },
        }),
      );
      setModifyingKey(null);
    }
  };

  const handleReject = (dev: any, devKey: string) => {
    const current = getDecisionForDev(devKey);
    if (current === "rejected") {
      dispatch(
        setNdaValidationFeedback({
          messageId: messageId!,
          feedback: {
            accept_array,
            modify_array,
            reject_array: reject_array.filter((d) => d._key !== devKey),
          },
        }),
      );
    } else {
      dispatch(
        setNdaValidationFeedback({
          messageId: messageId!,
          feedback: {
            reject_array: [
              ...reject_array.filter((d) => d._key !== devKey),
              { ...dev, _key: devKey },
            ],
            accept_array: accept_array.filter((d) => d._key !== devKey),
            modify_array: modify_array.filter((d) => d._key !== devKey),
          },
        }),
      );
      setModifyingKey(null);
    }
  };

  const handleModifyOpen = (dev: any, devKey: string) => {
    const existing = modify_array.find((d) => d._key === devKey);
    setModifyTexts((prev) => ({
      ...prev,
      [devKey]: existing?.suggested_fix || dev.suggested_fix || "",
    }));
    setModifyingKey(devKey);
  };

  const handleModifySave = (dev: any, devKey: string) => {
    const text = modifyTexts[devKey]?.trim();
    if (!text) return;
    dispatch(
      setNdaValidationFeedback({
        messageId: messageId!,
        feedback: {
          modify_array: [
            ...modify_array.filter((d) => d._key !== devKey),
            { ...dev, suggested_fix: text, _key: devKey },
          ],
          accept_array: accept_array.filter((d) => d._key !== devKey),
          reject_array: reject_array.filter((d) => d._key !== devKey),
        },
      }),
    );
    setModifyingKey(null);
  };

  const handleApplyDecisions = () => {
    if (!messageId) return;
    const metadata = `=== NDA_FEEDBACK_METADATA ===\n${JSON.stringify({ accept_array, reject_array, modify_array })}\n=== END NDA_FEEDBACK_METADATA ===\n`;
    const decisionsText = [
      accept_array.length > 0
        ? `Accepted: ${accept_array.map((d) => d.clause_name || d.deviation_id).join(", ")}`
        : "",
      reject_array.length > 0
        ? `Rejected: ${reject_array.map((d) => d.clause_name || d.deviation_id).join(", ")}`
        : "",
      modify_array.length > 0
        ? `Modified: ${modify_array.map((d) => d.clause_name || d.deviation_id).join(", ")}`
        : "",
    ]
      .filter(Boolean)
      .join(". ");
    dispatch(
      sendMessageToAgent({
        agentId: currentAgent?.id || NDA_AGENT_ID,
        message: `${metadata}${decisionsText || "Applied decisions"}`,
        questionId: uuid(),
        timestamp: new Date().toISOString(),
        agentEndpoint: NDA_ENDPOINT,
        sessionId: null,
      }),
    );
    dispatch(resetNdaValidationFeedback(messageId));
  };

  const totalDecisions =
    accept_array.length + reject_array.length + modify_array.length;

  const renderChecklistItem = (item: any, index: number) => {
    const normalizedStatus = (item.status || "").toLowerCase();
    const checklistStatusTone = getChecklistStatusTone(item.status);
    const priorityTone = getPriorityTone(item.priority);
    let StatusIconComponent = InfoOutlinedIcon;
    let statusIconColor = REVIEW_TONES.brand.text;
    if (normalizedStatus === "compliant") {
      StatusIconComponent = CheckCircleIcon;
      statusIconColor = REVIEW_TONES.accept.text;
    } else if (
      normalizedStatus.includes("non") ||
      normalizedStatus.includes("not")
    ) {
      StatusIconComponent = CancelIcon;
      statusIconColor = REVIEW_TONES.reject.text;
    } else if (normalizedStatus.includes("partial")) {
      StatusIconComponent = WarningAmberIcon;
      statusIconColor = REVIEW_TONES.modify.text;
    }
    return (
      <Box key={item.item_id || index} className={styles.checklistItem}>
        <Box className={styles.checklistHeader}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <StatusIconComponent
              sx={{ color: statusIconColor, fontSize: 18 }}
            />
            <Typography className={styles.checklistTitle}>
              {item.item_text}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Chip
              label={item.priority || "must_have"}
              size="small"
              sx={{
                backgroundColor: priorityTone.bg,
                color: priorityTone.text,
                border: `1px solid ${priorityTone.border}`,
                fontWeight: 600,
              }}
            />
            <Chip
              label={item.status || "Not Applicable"}
              size="small"
              sx={{
                backgroundColor: checklistStatusTone.bg,
                color: checklistStatusTone.text,
                border: `1px solid ${checklistStatusTone.border}`,
                fontWeight: 600,
              }}
            />
          </Box>
        </Box>
        <Typography className={styles.checklistCategory}>
          {item.category}
        </Typography>
        {item.finding_details && (
          <Typography className={styles.checklistDetail}>
            {item.finding_details}
          </Typography>
        )}
      </Box>
    );
  };

  return (
    <Box className={styles.container}>
      <Box className={styles.sectionHeader}>
        {data.is_compliant ? (
          <CheckCircleIcon
            sx={{ color: REVIEW_TONES.accept.text, fontSize: 22, mr: 1 }}
          />
        ) : (
          <WarningAmberIcon
            sx={{ color: REVIEW_TONES.modify.text, fontSize: 22, mr: 1 }}
          />
        )}
        <Typography className={styles.sectionTitle}>
          Policy Validation
        </Typography>
        <Chip
          label={
            data.is_compliant
              ? "Compliant"
              : `${data.deviation_count} Deviation${data.deviation_count !== 1 ? "s" : ""}`
          }
          size="small"
          sx={{
            backgroundColor: data.is_compliant
              ? REVIEW_TONES.accept.bg
              : REVIEW_TONES.modify.bg,
            color: data.is_compliant
              ? REVIEW_TONES.accept.text
              : REVIEW_TONES.modify.text,
            border: `1px solid ${data.is_compliant ? REVIEW_TONES.accept.border : REVIEW_TONES.modify.border}`,
            fontWeight: 600,
            ml: 1,
          }}
        />
      </Box>

      {data.summary && (
        <Box className={styles.summaryBox}>
          <MarkdownRenderer content={data.summary} onLinkClick={handleLinkClick} />
        </Box>
      )}

      <Box className={styles.validationOverview}>
        <Box className={styles.validationStatCard}>
          <Typography className={styles.validationStatLabel}>
            Checklist
          </Typography>
          <Typography className={styles.validationStatValue}>
            {data.checklist_name || "Legal best practices checklist"}
          </Typography>
        </Box>
        <Box className={styles.validationStatCard}>
          <Typography className={styles.validationStatLabel}>Source</Typography>
          <Typography className={styles.validationStatValue}>
            {data.checklist_source || "Generated checklist"}
          </Typography>
        </Box>
        <Box className={styles.validationStatCard}>
          <Typography className={styles.validationStatLabel}>
            Must-Have Checks
          </Typography>
          <Typography className={styles.validationStatValue}>
            {mustHaveItems.length}
          </Typography>
        </Box>
        <Box className={styles.validationStatCard}>
          <Typography className={styles.validationStatLabel}>
            Compliant Checks
          </Typography>
          <Typography className={styles.validationStatValue}>
            {compliantChecklistCount}
          </Typography>
        </Box>
      </Box>

      {mustHaveItems.length > 0 && (
        <Box className={styles.section}>
          <Typography className={styles.sectionLabel}>
            Must-Have Checks
          </Typography>
          <Stack gap={1}>{mustHaveItems.map(renderChecklistItem)}</Stack>
        </Box>
      )}

      {remainingChecklistItems.length > 0 && (
        <Box className={styles.section}>
          <Typography className={styles.sectionLabel}>
            Other Checklist Results
          </Typography>
          <Stack gap={1}>
            {remainingChecklistItems.map(renderChecklistItem)}
          </Stack>
        </Box>
      )}

      {data.deviations?.length > 0 && (
        <Box className={styles.section}>
          <Typography className={styles.sectionLabel}>
            Items Requiring Attention
          </Typography>
          <Typography className={styles.sectionHint}>
            Review each deviation and choose Accept, Modify, or Reject. Then
            click <strong>Apply Selected Decisions</strong>.
          </Typography>

          {data.deviations.map((dev: any, i: number) => {
            const key = getDeviationKey(dev, i);
            const priorityTone = getPriorityTone(dev.priority);
            const decision = getDecisionForDev(key);
            const isModifying = modifyingKey === key;

            const decisionBorder =
              decision === "accepted"
                ? "#28a745"
                : decision === "rejected"
                  ? "#dc3545"
                  : decision === "modified"
                    ? "#ffc107"
                    : "#b2e5de";

            return (
              <Box
                key={key}
                className={styles.accordionItem}
                sx={{
                  border: `1.5px solid ${decisionBorder} !important`,
                  transition: "border-color 0.2s",
                }}
              >
                <Box
                  className={styles.accordionHeader}
                  onClick={() => setExpandedDev(expandedDev === i ? null : i)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Typography className={styles.accordionTitle}>
                      {dev.clause_ref || "Draft"}
                    </Typography>
                    <Typography
                      className={styles.clauseName}
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {dev.clause_name}
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      mr: 1,
                      flexShrink: 0,
                    }}
                  >
                    {dev.priority && (
                      <Chip
                        label={dev.priority}
                        size="small"
                        sx={{
                          backgroundColor: priorityTone.bg,
                          color: priorityTone.text,
                          border: `1px solid ${priorityTone.border}`,
                          fontWeight: 600,
                        }}
                      />
                    )}
                    {decision && (
                      <Chip
                        label={
                          decision.charAt(0).toUpperCase() + decision.slice(1)
                        }
                        size="small"
                        sx={{
                          backgroundColor:
                            decision === "accepted"
                              ? REVIEW_TONES.accept.bg
                              : decision === "rejected"
                                ? REVIEW_TONES.reject.bg
                                : REVIEW_TONES.modify.bg,
                          color:
                            decision === "accepted"
                              ? REVIEW_TONES.accept.text
                              : decision === "rejected"
                                ? REVIEW_TONES.reject.text
                                : REVIEW_TONES.modify.text,
                          border: `1px solid ${decision === "accepted" ? REVIEW_TONES.accept.border : decision === "rejected" ? REVIEW_TONES.reject.border : REVIEW_TONES.modify.border}`,
                          fontWeight: 700,
                        }}
                      />
                    )}
                  </Box>
                  {expandedDev === i ? (
                    <ExpandLessIcon sx={{ fontSize: 18, color: "#004141" }} />
                  ) : (
                    <ExpandMoreIcon sx={{ fontSize: 18, color: "#004141" }} />
                  )}
                </Box>

                {expandedDev === i && (
                  <Box className={styles.accordionBody}>
                    {dev.issue_spotted_early && (
                      <Box className={styles.issueMetaRow}>
                        <Chip
                          icon={
                            <ErrorIcon sx={{ fontSize: "14px !important" }} />
                          }
                          label="Flagged early"
                          size="small"
                          className={styles.earlyFlagChip}
                        />
                        {dev.field_target && (
                          <Chip
                            label={`Field: ${dev.field_target}`}
                            size="small"
                            className={styles.fieldTargetChip}
                          />
                        )}
                      </Box>
                    )}
                    <Box className={styles.fieldRow}>
                      <Typography className={styles.fieldLabel}>
                        Current Text
                      </Typography>
                      <Typography className={styles.fieldValue}>
                        {dev.current_text}
                      </Typography>
                    </Box>
                    <Box className={styles.fieldRow}>
                      <Typography className={styles.fieldLabel}>
                        Policy Rule
                      </Typography>
                      <Typography className={styles.fieldValue}>
                        {dev.policy_rule}
                      </Typography>
                    </Box>
                    <Box className={styles.fieldRow}>
                      <Typography className={styles.fieldLabel}>
                        Recommended Action
                      </Typography>
                      <Typography className={styles.fieldValue}>
                        {dev.recommended_action}
                      </Typography>
                    </Box>
                    {dev.suggested_fix && (
                      <Box className={styles.fieldRow}>
                        <Typography className={styles.fieldLabel}>
                          Suggested Fix
                        </Typography>
                        <Typography className={styles.fieldValue}>
                          {dev.suggested_fix}
                        </Typography>
                      </Box>
                    )}

                    {isLatestMessage && (
                      <Box sx={{ mt: 2 }}>
                        {isModifying ? (
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            <TextField
                              size="small"
                              multiline
                              minRows={2}
                              fullWidth
                              placeholder="Enter the modified text or fix..."
                              value={modifyTexts[key] || ""}
                              onChange={(e) =>
                                setModifyTexts((prev) => ({
                                  ...prev,
                                  [key]: e.target.value,
                                }))
                              }
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  fontSize: 13,
                                  borderColor: "#ffc107",
                                },
                              }}
                            />
                            <Stack direction="row" gap={1}>
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleModifySave(dev, key)}
                                sx={{
                                  textTransform: "none",
                                  backgroundColor: "#ffc107",
                                  color: "#000",
                                  "&:hover": { backgroundColor: "#e0a800" },
                                  fontWeight: 600,
                                }}
                              >
                                Save Modification
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setModifyingKey(null)}
                                sx={{
                                  textTransform: "none",
                                  borderColor: "#ccc",
                                  color: "#555",
                                }}
                              >
                                Cancel
                              </Button>
                            </Stack>
                          </Box>
                        ) : (
                          <Stack direction="row" gap={1} flexWrap="wrap">
                            <Button
                              size="small"
                              variant={
                                decision === "accepted"
                                  ? "contained"
                                  : "outlined"
                              }
                              startIcon={
                                <CheckCircleIcon
                                  sx={{ fontSize: "16px !important" }}
                                />
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAccept(dev, key);
                              }}
                              sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                backgroundColor:
                                  decision === "accepted"
                                    ? REVIEW_TONES.accept.text
                                    : "transparent",
                                color:
                                  decision === "accepted"
                                    ? "#fff"
                                    : REVIEW_TONES.accept.text,
                                borderColor: REVIEW_TONES.accept.border,
                                "&:hover": {
                                  backgroundColor: REVIEW_TONES.accept.bg,
                                },
                              }}
                            >
                              {decision === "accepted"
                                ? "Accepted ✓"
                                : "Accept"}
                            </Button>
                            <Button
                              size="small"
                              variant={
                                decision === "modified"
                                  ? "contained"
                                  : "outlined"
                              }
                              startIcon={
                                <EditIcon
                                  sx={{ fontSize: "16px !important" }}
                                />
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                handleModifyOpen(dev, key);
                              }}
                              sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                backgroundColor:
                                  decision === "modified"
                                    ? "#856404"
                                    : "transparent",
                                color:
                                  decision === "modified"
                                    ? "#fff"
                                    : REVIEW_TONES.modify.text,
                                borderColor: REVIEW_TONES.modify.border,
                                "&:hover": {
                                  backgroundColor: REVIEW_TONES.modify.bg,
                                },
                              }}
                            >
                              {decision === "modified"
                                ? "Modified ✓"
                                : "Modify"}
                            </Button>
                            <Button
                              size="small"
                              variant={
                                decision === "rejected"
                                  ? "contained"
                                  : "outlined"
                              }
                              startIcon={
                                <CancelIcon
                                  sx={{ fontSize: "16px !important" }}
                                />
                              }
                              onClick={(e) => {
                                e.stopPropagation();
                                handleReject(dev, key);
                              }}
                              sx={{
                                textTransform: "none",
                                fontWeight: 600,
                                backgroundColor:
                                  decision === "rejected"
                                    ? "#dc3545"
                                    : "transparent",
                                color:
                                  decision === "rejected"
                                    ? "#fff"
                                    : REVIEW_TONES.reject.text,
                                borderColor: REVIEW_TONES.reject.border,
                                "&:hover": {
                                  backgroundColor: REVIEW_TONES.reject.bg,
                                },
                              }}
                            >
                              {decision === "rejected"
                                ? "Rejected ✓"
                                : "Reject"}
                            </Button>
                          </Stack>
                        )}
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            );
          })}

          {isLatestMessage && totalDecisions > 0 && (
            <Box sx={{ mt: 2, display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                onClick={handleApplyDecisions}
                sx={{
                  textTransform: "none",
                  fontWeight: 700,
                  backgroundColor: "#00a888",
                  "&:hover": { backgroundColor: "#00947a" },
                  px: 3,
                }}
              >
                Apply Selected Decisions ({totalDecisions})
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

function formatDisputeResolution(value: string): string {
  if (!value) return value;
  if (value.trim().toLowerCase() === "adgm courts")
    return "ADGM Courts, Abu Dhabi, UAE";
  return value;
}

// Template Selection View
function extractNdaType(name: string): string {
  if (!name) return "Standard";
  if (/multiparty/i.test(name)) return "Multiparty";
  if (/unilateral/i.test(name)) return "Unilateral";
  // Extract type from first parenthesised token: "(Mutual, v1.0)" → "Mutual"
  const match = name.match(/\(([^,)]+)/);
  if (match) return match[1].trim();
  if (/mutual/i.test(name)) return "Mutual";
  return "Standard";
}

function formatTemplateName(name: string): string {
  if (!name) return name;
  // "G42 - NDA - General Group (Mutual, v1.0)" → "G42 NDA – (Mutual) v1.0"
  // "G42 - NDA - Unilateral (v1.0)"            → "G42 NDA – (Unilateral) v1.0"
  // "G42 - NDA - Multiparty (v1.0)"            → "G42 NDA – (Multiparty) v1.0"
  // "G42 - NDA - KYC"                          → "G42 NDA – (KYC) v1.0"
  const ndaType = extractNdaType(name);
  return `G42 NDA – (${ndaType}) v1.0`;
}

function getTemplateDisplayKey(name: string): string {
  return String(formatTemplateName(name) || "")
    .trim()
    .toLowerCase();
}

function sanitizeDraftForDisplay(text: string): string {
  if (!text) return text;

  const normalized = normalizeTextEncoding(text)
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t]+$/gm, "");

  const cleanedLines = normalized
    .split("\n")
    .filter((line) => !/^\s*(?:[-_*=\u2500-\u257F]){3,}\s*$/.test(line));

  return cleanedLines
    .join("\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

function TemplateView({
  data,
  messageId,
  isLatestMessage: isLatestMessageProp,
  onSendMessage,
}: {
  data: any;
  messageId?: string;
  isLatestMessage?: boolean;
  onSendMessage?: (msg: string) => void;
}) {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents.currentAgent,
  );
  const currentChat = useSelector((state: RootState) => state.user.currentChat);
  const isLatestMessage =
    isLatestMessageProp ??
    [...currentChat].reverse().find((m) => m.sender === "agent")?.id ===
      messageId;
  const [pendingAlternateTemplate, setPendingAlternateTemplate] = useState<
    string | null
  >(null);

  const recommendedTemplateName = data.recommended_template || "";
  const ndaType = extractNdaType(recommendedTemplateName);
  const currentNdaType = ndaType.toLowerCase();
  const templateTypeOptions = NDA_BUILDER_TEMPLATE_OPTIONS.map((option) => ({
    ...option,
    displayLabel: formatTemplateName(option.templateName),
    isSelected:
      extractNdaType(option.templateName).toLowerCase() === currentNdaType,
  }));

  const sendAlternateTemplate = (templateName: string) => {
    dispatch(
      sendMessageToAgent({
        agentId: currentAgent?.id || NDA_AGENT_ID,
        message: templateName,
        questionId: uuid(),
        timestamp: new Date().toISOString(),
        agentEndpoint: NDA_ENDPOINT,
        sessionId: null,
      }),
    );
  };

  const handleSelectAlternate = (templateName: string) => {
    // No-op when the user clicks the already-selected template.
    if (extractNdaType(templateName).toLowerCase() === currentNdaType) {
      return;
    }
    if (isDestructiveNdaTypeSwitch(currentNdaType, templateName, null)) {
      setPendingAlternateTemplate(templateName);
      return;
    }
    sendAlternateTemplate(templateName);
  };

  return (
    <Box className={styles.container}>
      <Dialog
        open={Boolean(pendingAlternateTemplate)}
        onClose={() => setPendingAlternateTemplate(null)}
        aria-labelledby="nda-type-change-title"
      >
        <DialogTitle id="nda-type-change-title">Confirm Template Change</DialogTitle>
        <DialogContent>
          <Typography>
            Changing the NDA template type will remove all party information you've entered so far.
            You'll need to re-enter party details after the switch.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingAlternateTemplate(null)}>
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              if (pendingAlternateTemplate) {
                sendAlternateTemplate(pendingAlternateTemplate);
              }
              setPendingAlternateTemplate(null);
            }}
          >
            Yes, Change Template
          </Button>
        </DialogActions>
      </Dialog>

      <Box className={styles.templateCard}>
        <Typography className={styles.detailLabel}>Selected template</Typography>
        <Typography className={styles.detailValue} sx={{ mt: 0.5 }}>
          {recommendedTemplateName}
        </Typography>
        {data.reason && (
          <Typography className={styles.templateReason} sx={{ mt: 1.5 }}>
            {data.selection_rationale || data.reason}
          </Typography>
        )}
        {data.preselected_from_hint && (
          <Typography className={styles.detailLabel} sx={{ mt: 1.5, color: "#00695c" }}>
            Preselected from your Step 1 input
          </Typography>
        )}
      </Box>

      <Box className={styles.detailsGrid}>
        {data.governing_law && (
          <Box className={styles.detailItem}>
            <Typography className={styles.detailLabel}>
              Governing Law
            </Typography>
            <Typography className={styles.detailValue}>
              {data.governing_law}
            </Typography>
          </Box>
        )}
        {data.dispute_resolution && (
          <Box className={styles.detailItem}>
            <Typography className={styles.detailLabel}>
              Dispute Resolution
            </Typography>
            <Typography className={styles.detailValue}>
              {formatDisputeResolution(data.dispute_resolution)}
            </Typography>
          </Box>
        )}
      </Box>

      <Box className={styles.section}>
          <Typography className={styles.sectionLabel}>
            NDA type — click to switch
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
            {templateTypeOptions.map((option) => (
              <Chip
                key={option.variant}
                label={option.displayLabel}
                size="small"
                onClick={
                  isLatestMessage && onSendMessage
                    ? () => handleSelectAlternate(option.templateName)
                    : undefined
                }
                sx={{
                  backgroundColor: option.isSelected ? "#e6f2f2" : "#f9fafb",
                  color: option.isSelected ? "#004141" : "#2a312b",
                  border: option.isSelected
                    ? "1px solid #004141"
                    : "1px solid #e5e7eb",
                  fontWeight: option.isSelected ? 600 : 400,
                  cursor: "pointer",
                  "&:hover": {
                    backgroundColor: "#e6f2f2",
                    borderColor: "#004141",
                    color: "#004141",
                  },
                }}
              />
            ))}
          </Stack>
        </Box>

      {onSendMessage && isLatestMessage && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button
            variant="contained"
            onClick={() => onSendMessage("yes")}
            sx={{
              textTransform: "none",
              backgroundColor: "#00a888",
              "&:hover": { backgroundColor: "#00947a" },
              px: 3,
              py: 1,
            }}
          >
            Proceed with This Template
          </Button>
        </Box>
      )}

    </Box>
  );
}

// Counterparty Verification View
function CounterpartyView({ data }: { data: any }) {
  const statusTone =
    data.verification_status === "verified"
      ? REVIEW_TONES.accept
      : data.verification_status === "not_found"
        ? REVIEW_TONES.reject
        : REVIEW_TONES.modify;

  return (
    <Box className={styles.container}>
      <Box className={styles.sectionHeader}>
        <VerifiedUserIcon sx={{ color: "#004141", fontSize: 22, mr: 1 }} />
        <Typography className={styles.sectionTitle}>
          Counterparty Verification
        </Typography>
        <Chip
          label={
            data.verification_status === "verified"
              ? "Verified"
              : data.verification_status === "not_found"
                ? "Not Found"
                : "Manual Review"
          }
          size="small"
          sx={{
            backgroundColor: statusTone.bg,
            color: statusTone.text,
            border: `1px solid ${statusTone.border}`,
            fontWeight: 600,
            ml: 1,
          }}
        />
      </Box>

      {data.verification_message && (
        <Box className={styles.summaryBox}>
          <Typography className={styles.summaryText}>
            {data.verification_message}
          </Typography>
        </Box>
      )}

      <Box className={styles.detailsGrid}>
        {data.legal_entity_name && (
          <Box className={styles.detailItem}>
            <Typography className={styles.detailLabel}>
              Counterparty Name
            </Typography>
            <Typography className={styles.detailValue}>
              {data.legal_entity_name}
            </Typography>
          </Box>
        )}
        {data.country_of_incorporation && (
          <Box className={styles.detailItem}>
            <Typography className={styles.detailLabel}>
              Country
            </Typography>
            <Typography className={styles.detailValue}>
              {data.country_of_incorporation}
            </Typography>
          </Box>
        )}
        {data.authorized_signatory_name && (
          <Box className={styles.detailItem}>
            <Typography className={styles.detailLabel}>
              Authorized Signatory
            </Typography>
            <Typography className={styles.detailValue}>
              {data.authorized_signatory_name}
            </Typography>
          </Box>
        )}
        {data.authorized_signatory_designation && (
          <Box className={styles.detailItem}>
            <Typography className={styles.detailLabel}>Designation</Typography>
            <Typography className={styles.detailValue}>
              {data.authorized_signatory_designation}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}

// Conflict Check View
function ConflictView({ data }: { data: any }) {
  const tone =
    data.status === "clear"
      ? REVIEW_TONES.accept
      : data.status === "conflict_found"
        ? REVIEW_TONES.reject
        : REVIEW_TONES.modify;

  return (
    <Box className={styles.container}>
      <Box className={styles.sectionHeader}>
        {data.status === "clear" ? (
          <CheckCircleIcon
            sx={{ color: REVIEW_TONES.accept.text, fontSize: 22, mr: 1 }}
          />
        ) : data.status === "conflict_found" ? (
          <ErrorIcon
            sx={{ color: REVIEW_TONES.reject.text, fontSize: 22, mr: 1 }}
          />
        ) : (
          <WarningAmberIcon
            sx={{ color: REVIEW_TONES.modify.text, fontSize: 22, mr: 1 }}
          />
        )}
        <Typography className={styles.sectionTitle}>Conflict Check</Typography>
        <Chip
          label={
            data.status === "clear"
              ? "Clear"
              : data.status === "conflict_found"
                ? "Conflict Found"
                : "Manual Check"
          }
          size="small"
          sx={{
            backgroundColor: tone.bg,
            color: tone.text,
            border: `1px solid ${tone.border}`,
            fontWeight: 600,
            ml: 1,
          }}
        />
      </Box>

      {data.message && (
        <Box className={styles.summaryBox}>
          <Typography className={styles.summaryText}>{data.message}</Typography>
        </Box>
      )}

      {data.has_conflict && (
        <Box className={styles.detailsGrid}>
          {data.existing_nda_ref && (
            <Box className={styles.detailItem}>
              <Typography className={styles.detailLabel}>
                Existing NDA Reference
              </Typography>
              <Typography className={styles.detailValue}>
                {data.existing_nda_ref}
              </Typography>
            </Box>
          )}
          {data.existing_nda_expiry && (
            <Box className={styles.detailItem}>
              <Typography className={styles.detailLabel}>
                Expiry Date
              </Typography>
              <Typography className={styles.detailValue}>
                {data.existing_nda_expiry}
              </Typography>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
}

function MultipartyIntakeSummaryView({
  values,
  variant,
}: {
  values: Partial<MultipartyFormValues>;
  variant?: string;
}) {
  // Active non-G42 counterparties: Unilateral=1, Mutual=1, Multiparty=2.
  const variantLower = variant?.toLowerCase() || "";
  const isUnilateral = variantLower.includes("unilateral");
  const isMutual = variantLower.includes("mutual");
  const isMultiparty = variantLower.includes("multiparty");
  const partyCount = isUnilateral ? 1 : isMutual ? 1 : isMultiparty ? 2 : 1; // default to 1 if variant unknown

  const allParties = [
    {
      title: "Company A",
      name: values.companyAName,
      type: values.companyAType,
      jurisdiction: values.companyAJurisdiction,
      registration: values.companyARegistrationNumber,
      address: values.companyAAddress,
      kyc: values.companyAKyc,
    },
    {
      title: "Company B",
      name: values.companyBName,
      type: values.companyBType,
      jurisdiction: values.companyBJurisdiction,
      registration: values.companyBRegistrationNumber,
      address: values.companyBAddress,
      kyc: values.companyBKyc,
    },
    {
      title: "Company C",
      name: values.companyCName,
      type: values.companyCType,
      jurisdiction: values.companyCJurisdiction,
      registration: values.companyCRegistrationNumber,
      address: values.companyCAddress,
      kyc: values.companyCKyc,
    },
  ];

  // Only show active non-G42 parties for the selected variant.
  const parties = allParties
    .slice(0, partyCount)
    .filter(
      (party, index) =>
        index === 0 ||
        party.name ||
        party.type ||
        party.jurisdiction ||
        party.registration ||
        party.address ||
        party.kyc,
    );

  // N3-07: Dynamic title based on variant
  const summaryTitle = isUnilateral
    ? "Unilateral NDA Party Summary"
    : isMutual
    ? "Mutual NDA Party Summary"
    : isMultiparty
    ? "Multiparty NDA Party Summary"
    : "Party Summary";

  return (
    <Box className={styles.container}>
      <Box className={styles.sectionHeader}>
        <InfoOutlinedIcon sx={{ color: "#004141", fontSize: 22, mr: 1 }} />
        <Typography className={styles.sectionTitle}>
          {summaryTitle}
        </Typography>
      </Box>
      {parties.length > 0 ? (
        <GroupedPartyApprovalView
          groups={parties.map((party) => {
            const letter = party.title.replace("Company ", "").trim();
            const sectionLabel = party.name
              ? `${party.title} - ${party.name}`
              : party.title;
            const fields: ApprovalReviewField[] = [];
            const add = (suffix: string, val?: string, key?: string) => {
              if (!val) return;
              fields.push({
                id: `${party.title}_${key || suffix}`,
                label: `${party.title} ${suffix}`,
                value: val,
              });
            };
            add("Name", party.name, "name");
            // C1: Company Type removed from UI (internally inferred)
            add("Country", party.jurisdiction, "jurisdiction");
            add("Registration", party.registration, "registration");
            add("Principal Address", party.address, "address");
            add("KYC", party.kyc, "kyc");
            return { sectionLabel, partyLetter: letter, fields };
          })}
        />
      ) : null}
    </Box>
  );
}

// Input AI Review View (N5-01)
const INPUT_REVIEW_SUBMIT_LABELS: Record<string, string> = {
  purpose: "Purpose",
  counterparty_name: "Counterparty",
  counterparty_country: "Jurisdiction",
  counterparty_registration_number: "Registration Number",
  // C1: counterparty_entity_type label removed from UI (internally inferred)
  counterparty_address: "Principal Address",
  counterparty_confidentiality_required: "Counterparty confidentiality",
  kyc_completed: "KYC",
  space42_presight_disclosure: "Space42 disclosure",
  nda_type: "NDA Type",
};

function InputReviewView({
  data,
  onSendMessage,
  isLatestMessage = false,
}: {
  data: any;
  onSendMessage?: (msg: string) => void;
  isLatestMessage?: boolean;
}) {
  const [pendingEdits, setPendingEdits] = useState<Record<string, string>>({});
  const [editingField, setEditingField] = useState<{
    fieldId: string;
    label: string;
    value: string;
  } | null>(null);
  const [editValue, setEditValue] = useState("");

  const renderFieldRow = (field: any) => {
    const fieldId = field.field_id || field.label;
    const displayValue =
      pendingEdits[fieldId] ??
      (field.value === null || field.value === undefined ? "" : String(field.value));
    const canEdit =
      Boolean(isLatestMessage && onSendMessage) &&
      field.source !== "inferred" &&
      Boolean(fieldId);
    return (
      <Box className={styles.detailItem} key={fieldId}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
          <Typography className={styles.detailLabel}>{normalizeNdaFieldLabel(field.label)}</Typography>
          {pendingEdits[fieldId] ? (
            <Chip
              label="Edited"
              size="small"
              sx={{
                backgroundColor: REVIEW_TONES.modify.bg,
                color: REVIEW_TONES.modify.text,
                border: `1px solid ${REVIEW_TONES.modify.border}`,
                fontWeight: 600,
                height: 22,
                fontSize: "0.7rem",
              }}
            />
          ) : null}
          {field.confidence ? (
            <Chip
              label={`Confidence: ${field.confidence}`}
              size="small"
              sx={{
                backgroundColor: REVIEW_TONES.modify.bg,
                color: REVIEW_TONES.modify.text,
                border: `1px solid ${REVIEW_TONES.modify.border}`,
                fontWeight: 600,
                height: 22,
                fontSize: "0.7rem",
              }}
            />
          ) : null}
          {canEdit ? (
            <Tooltip title="Edit this field">
              <IconButton
                size="small"
                onClick={() => {
                  setEditingField({
                    fieldId,
                    label: field.label,
                    value: displayValue,
                  });
                  setEditValue(displayValue);
                }}
                sx={{ padding: "4px", ml: "auto" }}
              >
                <EditIcon sx={{ fontSize: "16px", color: "#6b7280" }} />
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
        <Typography className={styles.detailValue}>
          {displayValue || "—"}
        </Typography>
        {field.warning ? (
          <Typography
            sx={{ color: REVIEW_TONES.modify.text, fontSize: "0.85rem", mt: 0.5 }}
          >
            {field.warning}
          </Typography>
        ) : null}
      </Box>
    );
  };

  const allFields = [
    ...(Array.isArray(data.extracted_fields) ? data.extracted_fields : []),
    ...(Array.isArray(data.inferred_fields) ? data.inferred_fields : []),
  ].filter((f: any) => {
    const id = (f.field_id || "").toLowerCase();
    const label = (f.label || "").toLowerCase().replace(/\s+/g, " ").trim();
    // C1: NDA type + Company/entity type are internal-only — never show them in the input review
    return (
      id !== "nda_type" &&
      label !== "nda type" &&
      label !== "template type" &&
      label !== "template" &&
      !id.endsWith("_type") &&
      label !== "company type"
    );
  });

  const pendingEditCount = Object.keys(pendingEdits).length;

  const handleSaveFieldEdit = () => {
    if (!editingField || !editValue.trim()) return;
    // Reverting to the original value clears any pending edit (drop the "Edited" chip).
    const originalField = allFields.find(
      (f: any) => (f.field_id || f.label) === editingField.fieldId,
    );
    const originalValue = originalField ? String(originalField.value ?? "") : "";
    if (editValue.trim() === originalValue) {
      setPendingEdits((prev) => {
        const next = { ...prev };
        delete next[editingField.fieldId];
        return next;
      });
      setEditingField(null);
      setEditValue("");
      return;
    }
    if (editValue.trim() === editingField.value) {
      setEditingField(null);
      setEditValue("");
      return;
    }
    setPendingEdits((prev) => ({
      ...prev,
      [editingField.fieldId]: editValue.trim(),
    }));
    setEditingField(null);
    setEditValue("");
  };

  const handleApplyPendingEdits = () => {
    if (!onSendMessage || pendingEditCount === 0) return;
    const lines = Object.entries(pendingEdits).map(([fieldId, value]) => {
      const label = INPUT_REVIEW_SUBMIT_LABELS[fieldId] || fieldId.replace(/_/g, " ");
      return `${label}: ${value}`;
    });
    onSendMessage(lines.join("\n"));
    setPendingEdits({});
  };

  const handleConfirm = () => {
    if (!onSendMessage || pendingEditCount > 0) return;
    onSendMessage("confirm");
  };

  return (
    <>
    <Box
      className={styles.container}
      sx={{
        mt: 2,
        p: 2,
        backgroundColor: "#f8fffe",
        border: "1px solid #b2e5de",
        borderRadius: "8px",
      }}
    >
      <Box className={styles.sectionHeader}>
        <InfoOutlinedIcon sx={{ color: "#004141", fontSize: 22, mr: 1 }} />
        <Typography className={styles.sectionTitle}>
          {data.review_copy || "AI reviewed your input"}
        </Typography>
        <Chip
          label={data.is_ready_to_continue ? "Ready to continue" : "Needs attention"}
          size="small"
          sx={{
            backgroundColor: data.is_ready_to_continue
              ? REVIEW_TONES.accept.bg
              : REVIEW_TONES.modify.bg,
            color: data.is_ready_to_continue
              ? REVIEW_TONES.accept.text
              : REVIEW_TONES.modify.text,
            border: `1px solid ${
              data.is_ready_to_continue
                ? REVIEW_TONES.accept.border
                : REVIEW_TONES.modify.border
            }`,
            fontWeight: 600,
            ml: 1,
          }}
        />
      </Box>

      {allFields.length > 0 ? (
        <Box className={styles.detailsGrid}>
          {allFields.map(renderFieldRow)}
        </Box>
      ) : null}

      {Array.isArray(data.warnings) && data.warnings.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <Typography className={styles.detailLabel}>Warnings</Typography>
          {data.warnings.map((warning: string, index: number) => (
            <Typography
              key={`warning-${index}`}
              sx={{ color: REVIEW_TONES.modify.text, fontSize: "0.9rem", mt: 0.5 }}
            >
              {warning}
            </Typography>
          ))}
        </Box>
      ) : null}

      {Array.isArray(data.missing_fields) && data.missing_fields.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <Typography className={styles.detailLabel}>Missing required fields</Typography>
          <Typography sx={{ color: REVIEW_TONES.reject.text, fontSize: "0.9rem" }}>
            {data.missing_fields.join(", ")}
          </Typography>
        </Box>
      ) : null}

      {Array.isArray(data.contradictory_fields) &&
      data.contradictory_fields.length > 0 ? (
        <Box sx={{ mt: 2 }}>
          <Typography className={styles.detailLabel}>Contradictions</Typography>
          {data.contradictory_fields.map((item: string, index: number) => (
            <Typography
              key={`contradiction-${index}`}
              sx={{ color: REVIEW_TONES.reject.text, fontSize: "0.9rem", mt: 0.5 }}
            >
              {item}
            </Typography>
          ))}
        </Box>
      ) : null}

      {!data.is_ready_to_continue ? (
        <Typography
          sx={{ color: REVIEW_TONES.modify.text, fontSize: "0.9rem", mt: 2 }}
        >
          Provide missing details or corrections before continuing to template selection.
        </Typography>
      ) : null}

      {isLatestMessage && onSendMessage ? (
        <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1, justifyContent: "flex-end" }}>
          {pendingEditCount > 0 ? (
            <Typography sx={{ fontSize: "12px", color: "#6b7280", alignSelf: "center", mr: "auto" }}>
              {pendingEditCount} pending edit{pendingEditCount === 1 ? "" : "s"}. Apply all changes before continuing.
            </Typography>
          ) : (
            <Typography sx={{ fontSize: "12px", color: "#6b7280", alignSelf: "center", mr: "auto" }}>
              Use the pencil icon to edit fields. Apply all edits, then confirm to continue.
            </Typography>
          )}
          {pendingEditCount > 0 ? (
            <Button
              size="small"
              variant="contained"
              onClick={handleApplyPendingEdits}
              sx={{
                textTransform: "none",
                backgroundColor: "#00a888",
                "&:hover": { backgroundColor: "#00947a" },
              }}
            >
              Apply {pendingEditCount} change{pendingEditCount === 1 ? "" : "s"}
            </Button>
          ) : null}
          <Button
            size="small"
            variant="contained"
            disabled={pendingEditCount > 0 || !data.is_ready_to_continue}
            onClick={handleConfirm}
            sx={{
              textTransform: "none",
              backgroundColor: "#00a888",
              "&:hover": { backgroundColor: "#00947a" },
              "&.Mui-disabled": { backgroundColor: "#cccccc" },
            }}
          >
            Confirm and continue
          </Button>
        </Box>
      ) : null}
    </Box>

    <Dialog open={Boolean(editingField)} onClose={() => setEditingField(null)} fullWidth maxWidth="sm">
      <DialogTitle>Edit {normalizeNdaFieldLabel(editingField?.label)}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          multiline={Boolean(
            editingField?.fieldId === "purpose" || editingField?.fieldId === "counterparty_address",
          )}
          minRows={editingField?.fieldId === "purpose" ? 3 : 1}
          value={editValue}
          onChange={(event) => setEditValue(event.target.value)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setEditingField(null)} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleSaveFieldEdit}
          variant="contained"
          disabled={!editValue.trim()}
          sx={{ textTransform: "none", backgroundColor: "#00a888", "&:hover": { backgroundColor: "#00947a" } }}
        >
          Save to pending edits
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
}

// Intake Summary View
function IntakeView({ data }: { data: any }) {
  return (
    <Box
      className={styles.container}
      sx={{
        mt: 2,
        p: 2,
        backgroundColor: "#f8fffe",
        border: "1px solid #b2e5de",
        borderRadius: "8px",
      }}
    >
      <Box className={styles.sectionHeader}>
        <InfoOutlinedIcon sx={{ color: "#004141", fontSize: 22, mr: 1 }} />
        <Typography className={styles.sectionTitle}>Intake Summary</Typography>
        <Chip
          label={data.is_complete ? "Complete" : "In Progress"}
          size="small"
          sx={{
            backgroundColor: data.is_complete
              ? REVIEW_TONES.accept.bg
              : REVIEW_TONES.modify.bg,
            color: data.is_complete
              ? REVIEW_TONES.accept.text
              : REVIEW_TONES.modify.text,
            border: `1px solid ${
              data.is_complete
                ? REVIEW_TONES.accept.border
                : REVIEW_TONES.modify.border
            }`,
            fontWeight: 600,
            ml: 1,
          }}
        />
      </Box>

      <Box className={styles.detailsGrid}>
        {data.purpose && (
          <Box className={styles.detailItem}>
            <Typography className={styles.detailLabel}>Purpose</Typography>
            <Typography className={styles.detailValue}>
              {data.purpose}
            </Typography>
          </Box>
        )}
        {data.counterparty_name && (
          <Box className={styles.detailItem}>
            <Typography className={styles.detailLabel}>
              Counterparty Name
            </Typography>
            <Typography className={styles.detailValue}>
              {data.counterparty_name}
            </Typography>
          </Box>
        )}
        {data.counterparty_country && (
          <Box className={styles.detailItem}>
            <Typography className={styles.detailLabel}>Country</Typography>
            <Typography className={styles.detailValue}>
              {data.counterparty_country}
            </Typography>
          </Box>
        )}
        {/* C1: Company Type display removed from UI (internally inferred) */}
        {data.counterparty_registration_number && (
          <Box className={styles.detailItem}>
            <Typography className={styles.detailLabel}>
              Registration Number
            </Typography>
            <Typography className={styles.detailValue}>
              {data.counterparty_registration_number}
            </Typography>
          </Box>
        )}
        {data.counterparty_address && (
          <Box className={styles.detailItem}>
            <Typography className={styles.detailLabel}>Registered Address</Typography>
            <Typography className={styles.detailValue}>
              {data.counterparty_address}
            </Typography>
          </Box>
        )}
        {data.counterparty_confidentiality_required !== null &&
          data.counterparty_confidentiality_required !== undefined && (
            <Box className={styles.detailItem}>
              <Typography className={styles.detailLabel}>
                Counterparty Also Protected
              </Typography>
              <Typography className={styles.detailValue}>
                {data.counterparty_confidentiality_required ? "Yes" : "No"}
              </Typography>
            </Box>
          )}
        {data.kyc_completed !== null && data.kyc_completed !== undefined && (
          <Box className={styles.detailItem}>
            <Typography className={styles.detailLabel}>
              KYC Completed
            </Typography>
            <Typography className={styles.detailValue}>
              {data.kyc_completed === true ||
              String(data.kyc_completed).toLowerCase() === "yes"
                ? "Yes"
                : "No"}
            </Typography>
          </Box>
        )}
        {data.space42_presight_disclosure !== null &&
          data.space42_presight_disclosure !== undefined && (
            <Box className={styles.detailItem}>
              <Typography className={styles.detailLabel}>
                Space42 / Presight AI Disclosure
              </Typography>
              <Typography className={styles.detailValue}>
                {data.space42_presight_disclosure ? "Yes" : "No"}
              </Typography>
            </Box>
          )}
      </Box>
    </Box>
  );
}

// Main component
export default function NdaOrchestratorAgent({
  parsedContent,
  messageId,
}: NdaOrchestratorAgentProps): JSX.Element {
  const dispatch = useAppDispatch();
  const currentAgent = useSelector(
    (state: RootState) => state.agents.currentAgent,
  );
  const currentChat = useSelector((state: RootState) => state.user.currentChat);
  const numericAgentId = currentAgent?.id || 11;
  const data = parsedContent?.finalAnswer || parsedContent;
  const latestAgentMessage = [...currentChat]
    .reverse()
    .find((message) => message.sender === "agent");
  const isLatestMessage = latestAgentMessage?.id === messageId;
  const handleSendMessage = (msg: string) => {
    dispatch(
      sendMessageToAgent({
        agentId: currentAgent?.id || NDA_AGENT_ID,
        message: msg,
        questionId: uuid(),
        timestamp: new Date().toISOString(),
        agentEndpoint: NDA_ENDPOINT,
        sessionId: null,
      }),
    );
  };

  const handleLinkClick = (url: string) => {
    triggerNdaFileDownload(url, dispatch, numericAgentId, "markdown_link");
  };

  if (!data) {
    return (
      <Box className={styles.container}>
        <Typography className={styles.noData}>
          Processing NDA request...
        </Typography>
      </Box>
    );
  }

  // HITL Conversation — any NDA HITL turn has a `question` field (including empty string)
  if (data?.question !== undefined && data?.response !== undefined) {
    return <ConversationView data={data} messageId={messageId} />;
  }

  // NDA Draft
  if (data?.full_draft !== undefined) {
    return <DraftView data={data} />;
  }

  if (isMultipartyFormResult(data)) {
    const record = data as Record<string, unknown>;
    if (hasPartySlotContract(record)) {
      return (
        <PartySlotStep4View
          parties={record.parties as PartySlot[]}
          variant={typeof record.variant === "string" ? record.variant : null}
          validationIssues={Array.isArray(record.validation_issues) ? (record.validation_issues as MultipartyValidationIssue[]) : undefined}
          onSendMessage={handleSendMessage}
        />
      );
    }
    const composerProps = resolveMultipartyComposerProps(record);
    return (
      <MultipartyTemplateComposer
        savedValues={mapMultipartyPrefillValues(record.prefill_values)}
        variant={composerProps.variant}
        boxCountOverride={composerProps.boxCountOverride}
        editablePartyIndices={composerProps.editablePartyIndices}
        onSendMessage={handleSendMessage}
      />
    );
  }

  // NDA Review
  if (data?.clause_reviews !== undefined) {
    return <ReviewView data={data} />;
  }

  // NDA Validation
  if (data?.deviations !== undefined && data?.is_compliant !== undefined) {
    return <ValidationView data={data} messageId={messageId} />;
  }

  // Template selection without HITL question — suppress bare card (F-05: interactive card is in ConversationView)
  if (data?.recommended_template !== undefined && data?.question === undefined) {
    return (
      <Box className={styles.container}>
        <Typography className={styles.noData}>Processing template selection...</Typography>
      </Box>
    );
  }

  // Counterparty Verification
  if (data?.verification_status !== undefined) {
    return <CounterpartyView data={data} />;
  }

  // Conflict Check
  if (data?.has_conflict !== undefined) {
    return <ConflictView data={data} />;
  }

  // Intake Summary
  if (isInputReviewResult(data)) {
    return (
      <InputReviewView
        data={data}
        onSendMessage={handleSendMessage}
        isLatestMessage={isLatestMessage}
      />
    );
  }

  if (data?.is_complete !== undefined) {
    return <IntakeView data={data} />;
  }

  // Fallback: show response text (only if it's a string)
  const fallbackText =
    typeof data?.response === "string"
      ? data.response
      : typeof data?.finalAnswer?.response === "string"
        ? data.finalAnswer.response
      : typeof data?.message === "string"
        ? data.message
        : typeof data?.summary === "string"
          ? data.summary
          : "";
  if (fallbackText) {
    return (
      <Box className={styles.container}>
        <MarkdownRenderer content={fallbackText} onLinkClick={handleLinkClick} />
      </Box>
    );
  }

  return (
    <Box className={styles.container}>
      <Typography className={styles.noData}>
        Processing NDA request...
      </Typography>
    </Box>
  );
}
